/**
 * AI Service - RAG-ready architecture with provider adapters.
 * Supports multiple LLM providers through a unified interface.
 * Includes audit logging for all AI interactions.
 */

import type { AiMessage, AiToolCall, AuditLogEntry } from '@/types/ppm'
import type { AiContentBlock } from '@/services/ai/documentIntake'
import { EMPTY_USAGE, effortConfigFor, readUsage, thinkingConfigFor } from '@/services/ai/conversation'
import type { AiBlock, AiTurnRequest, AiTurnResponse } from '@/services/ai/conversation'
import type { IngestedContractDraft, IngestPhase } from '@/modules/clm/types/ai.types'
import { ContractType, ClauseType } from '@/modules/clm/types/clm.types'
import { generateId } from '@/lib/utils'
// Entra bearer for the AI proxy on the enterprise (Azure) build — resolves to
// {} on demo builds, so these headers are a no-op there.
import { entraAuthHeaders } from '@/lib/entraAuth'
import { buildDefaultAiProxyUrl, resolveAiProxyUrl } from '@/services/ai/aiConnection'
import { noteAiResponseMeta, readAiResponseMeta } from '@/services/ai/aiCapabilities'
import { AI_MODEL_LABEL, customerWordedAiError, reportedModelLabel, reportedModelOrNull } from '@/lib/aiMessages'
import { AI_FEATURES_DISABLED, assertAiAllowed } from '@/config/aiSurface'
// Pure, deterministic agile reasoning helpers shared by the Section 10 agile
// AI tools (below) and the mock provider preview path. See P6 AI phase.
import * as agilePlan from './agileAiPlanning'
// Module gate: the CRM tool set is advertised only when the CRM module is built
// into this deployment. Type-only dependency chain, so no runtime cycle.
import { isModuleVisible } from '@/config/moduleEntitlements'
// IT Ticketing's own tool set lives in its module (not inlined here like CRM's),
// per that module's port plan — its handlers still dynamically import the
// actual ticketing service code, so this costs only the tool metadata.
import { itTicketingAiTools } from '@/modules/it-ticketing/services/aiTools'
// Type-only: the CRM capture shapes the mock provider answers in. The runtime
// import is dynamic inside the method, so loading aiService never pulls the CRM
// module in.
import type { CaptureDirectory, ProposedAction } from '@/lib/crm/captureExtraction'

// Provider adapter interface - swap implementations without changing consumers
export interface AiProvider {
  /**
   * An INTERNAL name, not a vendor: 'mock', or 'claude' for the provider that
   * posts to the server AI endpoint. Which model vendor answers behind that
   * endpoint is the server's configuration (web/api/ai/_provider.js).
   */
  name: string
  sendMessage(messages: AiMessage[], tools?: AiTool[], opts?: AiSendOptions): Promise<AiMessage>
  /** The model name the server reported on the most recent reply, when it named one. */
  lastReportedModel?: string | null
}

/** Per-call overrides, e.g. a module-focused persona (IT Ticketing mode in AiPanel). */
export interface AiSendOptions {
  /** Replaces the default portfolio-management system prompt for this call. */
  systemPrompt?: string
}

/**
 * What a confirmation-required tool will do, shown on its Confirm card before
 * the user decides. `blocking` means the call can't run as proposed (it names
 * something unknown, or matches nothing) and Confirm stays disabled.
 */
export interface AiToolPreview {
  summary: string
  items?: string[]
  total?: number
  blocking?: string
}

export interface AiTool {
  name: string
  description: string
  parameters: Record<string, unknown>
  requiresConfirmation: boolean
  handler: (input: Record<string, unknown>) => Promise<Record<string, unknown>>
  /** Optional, read-only: describes the effect of a write for its Confirm card. */
  preview?: (input: Record<string, unknown>) => Promise<AiToolPreview>
}

// Audit log (in-memory for now, ready for backend persistence)
const auditLog: AuditLogEntry[] = []

export function getAuditLog(): AuditLogEntry[] {
  return [...auditLog]
}

export function addAuditEntry(entry: Omit<AuditLogEntry, 'id' | 'timestamp'>): void {
  auditLog.push({
    ...entry,
    id: generateId(),
    timestamp: new Date().toISOString(),
  })
}

// Built-in tools for the PPM assistant
export const ppmTools: AiTool[] = [
  {
    name: 'get_portfolio_summary',
    description: 'Get a summary of portfolio KPIs including project count, budget, health status',
    parameters: { portfolioId: { type: 'string', description: 'Portfolio ID' } },
    requiresConfirmation: false,
    handler: async (input) => {
      const { getPortfolioById } = await import('./dataService')
      const portfolio = await getPortfolioById(input.portfolioId as string)
      if (!portfolio) return { error: 'Portfolio not found' }
      return {
        name: portfolio.name,
        kpis: portfolio.kpis,
        healthStatus: portfolio.healthStatus,
        budget: portfolio.budget,
      }
    },
  },
  {
    name: 'get_project_details',
    description: 'Get detailed information about a specific project',
    parameters: { projectId: { type: 'string', description: 'Project ID' } },
    requiresConfirmation: false,
    handler: async (input) => {
      const { getProjectById } = await import('./dataService')
      const project = await getProjectById(input.projectId as string)
      if (!project) return { error: 'Project not found' }
      return {
        name: project.name,
        status: project.status,
        healthStatus: project.healthStatus,
        budget: project.budget,
        schedule: project.schedule,
        riskScore: project.riskScore,
      }
    },
  },
  {
    name: 'get_at_risk_projects',
    description: 'List all projects that are at risk or have health status Yellow/Red',
    parameters: { portfolioId: { type: 'string', description: 'Optional portfolio filter' } },
    requiresConfirmation: false,
    handler: async (input) => {
      const { getProjects } = await import('./dataService')
      const projects = await getProjects(input.portfolioId as string | undefined)
      const atRisk = projects.filter(
        p => p.status === 'At Risk' || p.healthStatus === 'Yellow' || p.healthStatus === 'Red'
      )
      return {
        count: atRisk.length,
        projects: atRisk.map(p => ({
          id: p.id,
          name: p.name,
          status: p.status,
          healthStatus: p.healthStatus,
          spi: p.schedule.spi,
          cpi: p.schedule.cpi,
          riskScore: p.riskScore,
        })),
      }
    },
  },
  {
    name: 'draft_status_report',
    description: 'Draft a comprehensive, visually rich status report for a project. Gathers data from schedule, cost baseline, forecast, earned value, resources, risks/issues, and pending changes. Returns structured data for visual rendering plus AI-generated narrative with recommendations.',
    parameters: {
      entityType: { type: 'string', description: '"project" or "portfolio"' },
      entityId: { type: 'string', description: 'Entity ID' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const entityType = input.entityType as string
      if (entityType === 'project') {
        const result = await gatherProjectStatusReportData(input.entityId as string)
        return result as unknown as Record<string, unknown>
      }
      return { error: 'Unsupported entity type' }
    },
  },
  {
    name: 'guide_project_request',
    description: 'Provide AI guidance for creating a new project request. Analyzes the current form state and suggests improvements, missing fields, and strategic alignment opportunities.',
    parameters: {
      portfolioId: { type: 'string', description: 'Portfolio ID for context' },
      formState: { type: 'object', description: 'Current form data including name, type, budget, priority, justification' },
      currentStep: { type: 'string', description: 'Current wizard step: basics, budget, priority, schedule, alignment, funding, review' },
    },
    requiresConfirmation: false,
    handler: async (input) => {
      const { getStrategicPriorities, getProjectRequests } = await import('./dataService')
      const portfolioId = input.portfolioId as string
      const formState = input.formState as Record<string, unknown>
      const currentStep = input.currentStep as string

      const priorities = portfolioId ? await getStrategicPriorities(portfolioId) : []
      const existingRequests = portfolioId ? await getProjectRequests(portfolioId) : []

      const activePriorities = (priorities as Array<Record<string, unknown>>).filter(
        p => p.status === 'Active'
      )

      const suggestions: string[] = []
      const warnings: string[] = []

      // Step-specific guidance
      if (currentStep === 'basics') {
        const name = formState.name as string || ''
        if (name && existingRequests.some(r => (r as Record<string, unknown>).name === name)) {
          warnings.push('A request with this name already exists in the portfolio.')
        }
        if (name && name.length < 10) {
          suggestions.push('Consider a more descriptive project name to help reviewers quickly understand the scope.')
        }
      }

      if (currentStep === 'budget') {
        const budget = formState.estimatedBudget as number || 0
        const avgBudget = existingRequests.length > 0
          ? existingRequests.reduce((s, r) => s + ((r as Record<string, unknown>).estimatedBudget as number || 0), 0) / existingRequests.length
          : 0
        if (budget > 0 && avgBudget > 0) {
          const ratio = budget / avgBudget
          if (ratio > 2) suggestions.push(`This budget is ${ratio.toFixed(1)}x the portfolio average. Consider whether a phased approach would reduce risk.`)
          if (ratio < 0.3) suggestions.push('This is a relatively small request. Verify whether portfolio governance is needed or if a simpler process applies.')
        }
      }

      if (currentStep === 'alignment') {
        if (activePriorities.length > 0) {
          suggestions.push(`This portfolio has ${activePriorities.length} active strategic priorities. Aligning to at least one will improve your prioritization score.`)
          const topPriority = activePriorities.sort((a, b) => (b.weight as number) - (a.weight as number))[0]
          if (topPriority) {
            suggestions.push(`The highest-weighted priority is "${topPriority.name}" (weight: ${((topPriority.weight as number) * 100).toFixed(0)}%). Consider alignment if relevant.`)
          }
        }
      }

      return {
        suggestions,
        warnings,
        activePriorities: activePriorities.map(p => ({ id: p.id, name: p.name, weight: p.weight })),
        existingRequestCount: existingRequests.length,
      }
    },
  },
  {
    name: 'create_portfolio',
    description: 'Create a new portfolio in the system. Owner must be a portfolio-owner-eligible user (managed in Personnel Management). Optionally place under a parent portfolio by name. Requires user confirmation before the insert runs.',
    parameters: {
      name: { type: 'string', description: 'Portfolio name (required)' },
      code: { type: 'string', description: 'Portfolio code/abbreviation (auto-generated from name if omitted)' },
      description: { type: 'string', description: 'Optional portfolio description' },
      ownerName: { type: 'string', description: 'Owner full name. Must match a user flagged as portfolio-owner-eligible.' },
      parentName: { type: 'string', description: 'Optional parent portfolio name (for hierarchy)' },
      portfolioType: { type: 'string', description: 'Optional portfolio type (e.g. Program, Operations)' },
      startDate: { type: 'string', description: 'Optional ISO start date (YYYY-MM-DD); defaults to today' },
      endDate: { type: 'string', description: 'Optional ISO end date (YYYY-MM-DD)' },
      budgetTotal: { type: 'number', description: 'Optional total budget amount' },
      budgetCurrency: { type: 'string', description: 'Currency code (default USD)' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const ds = await import('./dataService')

      const name = ((input.name as string) ?? '').trim()
      if (!name) return { error: 'Portfolio name is required.' }

      const ownerNameInput = ((input.ownerName as string) ?? '').trim()
      if (!ownerNameInput) {
        return { error: 'Portfolio owner is required. Provide the full name of a portfolio-owner-eligible user.' }
      }

      const users = (await ds.getUsers()) as Array<{
        id: string; name: string; isPortfolioOwnerEligible?: boolean
      }>
      const eligible = users.filter(u => u.isPortfolioOwnerEligible)
      const lc = ownerNameInput.toLowerCase()
      const owner =
        eligible.find(u => u.name.toLowerCase() === lc) ||
        eligible.find(u => u.name.toLowerCase().includes(lc))
      if (!owner) {
        return {
          error: `No portfolio-owner-eligible user matches "${ownerNameInput}". Visit Personnel Management to flag the right user, or pick from: ${eligible.slice(0, 5).map(u => u.name).join(', ')}${eligible.length > 5 ? '…' : ''}`,
        }
      }

      const parentNameInput = ((input.parentName as string) ?? '').trim()
      let parentId: string | null = null
      let parentLevel = -1
      let parentPath = ''
      if (parentNameInput) {
        const portfolios = await ds.getPortfolios()
        const parent = portfolios.find(p => p.name.toLowerCase() === parentNameInput.toLowerCase())
        if (!parent) {
          return { error: `Parent portfolio "${parentNameInput}" not found.` }
        }
        parentId = parent.id
        parentLevel = parent.level
        parentPath = parent.path
      }

      const code =
        ((input.code as string) ?? '').trim() ||
        name.toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 24) || 'PRT'
      const startDate = ((input.startDate as string) ?? '').trim() || new Date().toISOString().slice(0, 10)
      const endDate = ((input.endDate as string) ?? '').trim()
      const budgetTotal = typeof input.budgetTotal === 'number' ? input.budgetTotal : 0
      const budgetCurrency = ((input.budgetCurrency as string) ?? 'USD').trim() || 'USD'
      const portfolioType = ((input.portfolioType as string) ?? '').trim() || null

      const portfolioId = `port_${generateId()}`
      const newPortfolio = {
        id: portfolioId,
        code,
        name,
        parentId,
        level: parentLevel + 1,
        path: parentPath ? `${parentPath} > ${name}` : name,
        childIds: [],
        description: ((input.description as string) ?? '').trim(),
        status: 'Active',
        portfolioType,
        owner: owner.id,
        ownerName: owner.name,
        startDate,
        endDate,
        budget: {
          total: budgetTotal,
          spent: 0,
          committed: 0,
          available: budgetTotal,
          currency: budgetCurrency,
        },
        healthStatus: 'Green' as const,
        projects: [],
        kpis: {
          activeProjects: 0,
          completedProjects: 0,
          atRiskProjects: 0,
          avgSpi: 0,
          avgCpi: 0,
          totalRisks: 0,
          openIssues: 0,
          childPortfolioCount: 0,
        },
      }

      try {
        // Imported type widening: createPortfolio expects the full Portfolio
        // shape; the literal above matches it.
        await ds.createPortfolio(newPortfolio as Parameters<typeof ds.createPortfolio>[0])
        return {
          ok: true,
          portfolioId,
          code,
          name,
          ownerName: owner.name,
          parentName: parentNameInput || null,
          path: newPortfolio.path,
          message: `Created portfolio "${name}" (${code}) owned by ${owner.name}.`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : 'Failed to create portfolio' }
      }
    },
  },
  {
    name: 'analyze_portfolio_projects',
    description: 'Analyze all projects in a portfolio and return a comprehensive summary highlighting issues, risks, budget concerns, schedule performance, and opportunities for the portfolio manager.',
    parameters: { portfolioId: { type: 'string', description: 'Portfolio ID to analyze' } },
    requiresConfirmation: false,
    handler: async (input) => {
      const { getProjects, getPortfolioById } = await import('./dataService')
      const portfolioId = input.portfolioId as string | undefined
      const portfolio = portfolioId ? await getPortfolioById(portfolioId) : null
      const projects = await getProjects(portfolioId)

      const totalBudget = projects.reduce((s, p) => s + p.budget.total, 0)
      const totalSpent = projects.reduce((s, p) => s + p.budget.spent, 0)
      const avgSpi = projects.length > 0 ? projects.reduce((s, p) => s + p.schedule.spi, 0) / projects.length : 0
      const avgCpi = projects.length > 0 ? projects.reduce((s, p) => s + p.schedule.cpi, 0) / projects.length : 0

      const atRisk = projects.filter(p => p.status === 'At Risk' || p.healthStatus === 'Red')
      const yellow = projects.filter(p => p.healthStatus === 'Yellow' && p.status !== 'At Risk')
      const green = projects.filter(p => p.healthStatus === 'Green')
      const overBudget = projects.filter(p => p.budget.spent / p.budget.total > 0.9)
      const behindSchedule = projects.filter(p => p.schedule.spi < 0.9)
      const onTrack = projects.filter(p => p.healthStatus === 'Green' && p.schedule.spi >= 0.95 && p.schedule.cpi >= 0.95)

      return {
        portfolioName: portfolio?.name ?? 'Selected Portfolio',
        projectCount: projects.length,
        totalBudget,
        totalSpent,
        budgetUtilization: totalBudget > 0 ? ((totalSpent / totalBudget) * 100).toFixed(1) : '0',
        avgSpi: avgSpi.toFixed(2),
        avgCpi: avgCpi.toFixed(2),
        healthBreakdown: {
          green: green.length,
          yellow: yellow.length,
          red: atRisk.length,
        },
        atRiskProjects: atRisk.map(p => ({
          name: p.name, code: p.code, status: p.status, healthStatus: p.healthStatus,
          spi: p.schedule.spi, cpi: p.schedule.cpi, riskScore: p.riskScore,
          budgetPct: ((p.budget.spent / p.budget.total) * 100).toFixed(1),
          scheduleVarianceDays: p.schedule.scheduleVarianceDays,
        })),
        yellowProjects: yellow.map(p => ({
          name: p.name, code: p.code, spi: p.schedule.spi, cpi: p.schedule.cpi,
          budgetPct: ((p.budget.spent / p.budget.total) * 100).toFixed(1),
        })),
        overBudgetProjects: overBudget.map(p => ({
          name: p.name, code: p.code,
          budgetPct: ((p.budget.spent / p.budget.total) * 100).toFixed(1),
          overage: p.budget.spent - p.budget.total,
        })),
        behindScheduleProjects: behindSchedule.map(p => ({
          name: p.name, code: p.code, spi: p.schedule.spi,
          varianceDays: p.schedule.scheduleVarianceDays,
        })),
        topPerformers: onTrack.map(p => ({
          name: p.name, code: p.code, spi: p.schedule.spi, cpi: p.schedule.cpi,
          progress: p.schedule.percentComplete,
        })),
        projects: projects.map(p => ({
          name: p.name, code: p.code, status: p.status, healthStatus: p.healthStatus,
          priority: p.priority, fundingType: p.fundingType,
          spi: p.schedule.spi, cpi: p.schedule.cpi,
          progress: p.schedule.percentComplete,
          budgetTotal: p.budget.total, budgetSpent: p.budget.spent,
        })),
      }
    },
  },
  {
    name: 'get_workflow_queue',
    description: 'Get pending workflow approvals and active workflow instances for the current user. Shows what items are waiting in their queue.',
    parameters: {},
    requiresConfirmation: false,
    handler: async () => {
      const { getWorkflowInstances } = await import('./dataService')
      const instances = await getWorkflowInstances()

      const running = instances.filter(i => (i as Record<string, unknown>).status === 'running' || (i as Record<string, unknown>).status === 'in_progress')
      const allPending: Array<Record<string, unknown>> = []

      for (const inst of running) {
        const rec = inst as Record<string, unknown>
        const approvals = (rec.pendingApprovals ?? []) as Array<Record<string, unknown>>
        for (const appr of approvals) {
          if (!appr.decision) {
            allPending.push({
              instanceId: rec.id,
              workflowName: rec.workflowName ?? rec.definitionId,
              currentState: rec.currentState,
              projectName: (rec.context as Record<string, unknown>)?.projectName ?? 'Unknown',
              approvalId: appr.approvalId,
              role: appr.role,
              userName: appr.userName,
              level: appr.level,
            })
          }
        }
      }

      return {
        totalRunning: running.length,
        pendingApprovals: allPending.length,
        queue: allPending,
        navigationPath: '/workflows',
        message: allPending.length > 0
          ? `You have ${allPending.length} pending approval(s). Navigate to Workflows to review them.`
          : 'No pending approvals in your queue.',
      }
    },
  },

  // ---------------------------------------------------------------------------
  // EVM AI Tools (Phase 3)
  // ---------------------------------------------------------------------------
  {
    name: 'analyze_evm_variances',
    description: 'Analyze earned value variances across a project or portfolio, identifying the top drivers of cost and schedule variance.',
    parameters: {
      projectId: { type: 'string', description: 'Project ID to analyze (optional — omit for portfolio-wide)' },
    },
    requiresConfirmation: false,
    handler: async (input: Record<string, unknown>) => {
      const { getEvmTimeSeries, getProjects } = await import('./dataService')
      const { buildProjectEvmRows, aggregatePortfolioEvm, buildPortfolioEvmScope } = await import('./evmPortfolioService')
      const { calculateEvmMetrics, getLatestActualPeriod } = await import('./evmCalculationService')

      const projectId = input.projectId as string | undefined
      const allTs = await getEvmTimeSeries(projectId)
      const projects = await getProjects()

      if (projectId) {
        const ts = allTs[0]
        if (!ts) return { error: 'No EVM data found for this project.' }
        const latest = getLatestActualPeriod(ts.periods)
        if (!latest || latest.ev == null || latest.ac == null) return { error: 'No actual data recorded yet.' }
        const m = calculateEvmMetrics(ts.bac, latest.pv, latest.ev, latest.ac)
        const p = projects.find((pp: any) => pp.id === projectId)

        return {
          project: p?.name ?? projectId,
          bac: ts.bac,
          spi: m.spi,
          cpi: m.cpi,
          sv: m.sv,
          cv: m.cv,
          eacTypical: m.eacTypical,
          vac: m.vac,
          tcpi: m.tcpi,
          spiZone: m.spiZone,
          cpiZone: m.cpiZone,
          percentComplete: m.percentComplete,
          insight: m.cpi < 0.9
            ? `CPI of ${m.spi.toFixed(2)} indicates significant cost overrun. At ${m.percentComplete.toFixed(0)}% complete, recovery is ${m.percentComplete > 20 ? 'unlikely' : 'still possible'} without corrective action.`
            : m.spi < 0.9
              ? `SPI of ${m.spi.toFixed(2)} indicates the project is behind schedule. Cost is ${m.cpi >= 0.95 ? 'under control' : 'also at risk'}.`
              : 'Project performance is within acceptable thresholds.',
        }
      } else {
        const rows = buildProjectEvmRows(projects, allTs)
        // Pass the scope so the answer states what it is NOT measuring. The
        // rollup covers only projects that have reported a period; without
        // this the assistant reported a partial BAC as the portfolio total.
        const agg = aggregatePortfolioEvm(rows, buildPortfolioEvmScope(projects, allTs))
        const worst = [...rows].sort((a, b) => a.cpi - b.cpi).slice(0, 3)
        const best = [...rows].sort((a, b) => b.cpi - a.cpi).slice(0, 3)

        return {
          portfolioSpi: agg.portfolioSpi,
          portfolioCpi: agg.portfolioCpi,
          portfolioSv: agg.portfolioSv,
          portfolioCv: agg.portfolioCv,
          totalBac: agg.totalBac,
          projectCount: agg.projectCount,
          evmEnabledCount: agg.evmEnabledCount,
          enabledBac: agg.enabledBac,
          unreportedCount: agg.unreportedCount,
          unreportedBac: agg.unreportedBac,
          coverageNote: agg.unreportedCount > 0
            ? `These figures cover ${agg.projectCount} of ${agg.evmEnabledCount} EVM-enabled projects. ${agg.unreportedCount} have not reported a period yet and are excluded from every total and index above.`
            : `All ${agg.evmEnabledCount} EVM-enabled projects have reported.`,
          greenCount: agg.greenCount,
          yellowCount: agg.yellowCount,
          redCount: agg.redCount,
          worstPerformers: worst.map((r) => ({ name: r.projectName, cpi: r.cpi, spi: r.spi, cv: r.cv })),
          bestPerformers: best.map((r) => ({ name: r.projectName, cpi: r.cpi, spi: r.spi })),
        }
      }
    },
  },
  {
    name: 'forecast_evm_completion',
    description: 'Run multiple EAC (Estimate at Completion) scenarios for a project and provide a narrative forecast.',
    parameters: {
      projectId: { type: 'string', description: 'Project ID' },
    },
    requiresConfirmation: false,
    handler: async (input: Record<string, unknown>) => {
      const { getEvmTimeSeries } = await import('./dataService')
      const { calculateEvmMetrics, getLatestActualPeriod } = await import('./evmCalculationService')

      const ts = (await getEvmTimeSeries(input.projectId as string))[0]
      if (!ts) return { error: 'No EVM data found.' }
      const latest = getLatestActualPeriod(ts.periods)
      if (!latest || latest.ev == null || latest.ac == null) return { error: 'No actuals yet.' }

      const m = calculateEvmMetrics(ts.bac, latest.pv, latest.ev, latest.ac)

      return {
        bac: ts.bac,
        eacAtypical: m.eacAtypical,
        eacTypical: m.eacTypical,
        eacComposite: m.eacComposite,
        eacDoD: m.eacDoD,
        tcpi: m.tcpi,
        recommendation: m.tcpi > 1.1
          ? 'TCPI exceeds 1.10 — the efficiency required to finish on budget is unrealistic. Recommend rebaselining or applying Management Reserve.'
          : m.cpi < 0.95
            ? `The most likely outcome is EAC of $${(m.eacTypical / 1e6).toFixed(1)}M (${((m.eacTypical - ts.bac) / ts.bac * 100).toFixed(1)}% overrun). Consider corrective actions or EAC revision.`
            : `Project is projected to finish near budget. EAC range: $${(m.eacAtypical / 1e6).toFixed(1)}M to $${(m.eacComposite / 1e6).toFixed(1)}M.`,
      }
    },
  },
  {
    name: 'generate_variance_report',
    description: 'Draft a variance analysis report (VAR) for a project based on current EVM trends and context.',
    parameters: {
      projectId: { type: 'string', description: 'Project ID' },
      wbsName: { type: 'string', description: 'WBS element name experiencing the variance' },
    },
    requiresConfirmation: true,
    handler: async (input: Record<string, unknown>) => {
      const { getEvmTimeSeries } = await import('./dataService')
      const { calculateEvmMetrics, getLatestActualPeriod } = await import('./evmCalculationService')

      const ts = (await getEvmTimeSeries(input.projectId as string))[0]
      if (!ts) return { error: 'No EVM data found.' }
      const latest = getLatestActualPeriod(ts.periods)
      if (!latest || latest.ev == null || latest.ac == null) return { error: 'No actuals yet.' }

      const m = calculateEvmMetrics(ts.bac, latest.pv, latest.ev, latest.ac)
      const wbs = input.wbsName as string

      return {
        draftReport: {
          wbsElement: wbs,
          period: latest.label,
          triggerType: m.cpi < m.spi ? 'CPI' : 'SPI',
          triggerValue: Math.min(m.cpi, m.spi),
          suggestedRootCause: `[AI-suggested: Analyze resource utilization and scope changes for ${wbs}. Common drivers include labor rate variance, scope growth, and productivity differences from estimate.]`,
          suggestedImpact: `Projected cost impact: ${m.cv < 0 ? 'overrun of ' + (-m.cv).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : 'within budget'}. Schedule impact: ${m.sv < 0 ? 'behind by equivalent of ' + (-m.sv).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) + ' in planned work' : 'on or ahead of schedule'}.`,
          suggestedCorrectiveAction: '[AI-suggested: 1) Review and update remaining estimates. 2) Identify and resolve resource bottlenecks. 3) Evaluate scope for de-scope opportunities. 4) Consider Management Reserve application if warranted.]',
        },
        citations: [
          { field: 'spi', value: String(m.spi.toFixed(3)), source: 'evm_calculation' },
          { field: 'cpi', value: String(m.cpi.toFixed(3)), source: 'evm_calculation' },
          { field: 'cv', value: String(m.cv), source: 'evm_calculation' },
        ],
      }
    },
  },
  {
    name: 'assess_evm_recoverability',
    description: 'Evaluate whether a project\'s CPI/SPI trends suggest recovery is feasible, including the "point of no return" assessment.',
    parameters: {
      projectId: { type: 'string', description: 'Project ID' },
    },
    requiresConfirmation: false,
    handler: async (input: Record<string, unknown>) => {
      const { getEvmTimeSeries } = await import('./dataService')
      const { calculateEvmMetrics, getLatestActualPeriod, calculatePerformanceTrend, isConsecutiveDecline } = await import('./evmCalculationService')

      const ts = (await getEvmTimeSeries(input.projectId as string))[0]
      if (!ts) return { error: 'No EVM data found.' }
      const latest = getLatestActualPeriod(ts.periods)
      if (!latest || latest.ev == null || latest.ac == null) return { error: 'No actuals yet.' }

      const m = calculateEvmMetrics(ts.bac, latest.pv, latest.ev, latest.ac)
      const trend = calculatePerformanceTrend(ts)
      const spiValues = trend.map((t) => t.spi)
      const cpiValues = trend.map((t) => t.cpi)
      const spiDeclining = isConsecutiveDecline(spiValues, 3)
      const cpiDeclining = isConsecutiveDecline(cpiValues, 3)
      const percentComplete = m.percentComplete

      // Point of no return check
      const pointOfNoReturn = m.cpi < 0.9 && percentComplete > 20

      let assessment: string
      if (pointOfNoReturn) {
        assessment = `CRITICAL: Project has passed the "point of no return" — CPI of ${m.cpi.toFixed(2)} at ${percentComplete.toFixed(0)}% completion. Historical data shows CPI rarely recovers after this point. Recommend immediate rebaseline discussion.`
      } else if (m.tcpi > 1.1) {
        assessment = `UNLIKELY RECOVERY: TCPI of ${m.tcpi.toFixed(2)} means the project would need ${((m.tcpi - 1) * 100).toFixed(0)}% improvement in cost efficiency to finish on budget. This is rarely achievable without significant scope reduction.`
      } else if (spiDeclining && cpiDeclining) {
        assessment = `DETERIORATING: Both SPI and CPI have declined for 3+ consecutive periods. While not yet critical, the trend must be arrested immediately to avoid the point of no return.`
      } else if (spiDeclining || cpiDeclining) {
        assessment = `WATCH: ${spiDeclining ? 'SPI' : 'CPI'} has declined for 3+ periods. Current performance is ${m.spi >= 0.95 && m.cpi >= 0.95 ? 'still acceptable' : 'approaching thresholds'}. Monitor closely.`
      } else {
        assessment = `RECOVERABLE: Current performance indices are ${m.spi >= 0.95 && m.cpi >= 0.95 ? 'within green thresholds' : 'outside green but stable'}. No structural deterioration detected.`
      }

      return {
        spi: m.spi,
        cpi: m.cpi,
        tcpi: m.tcpi,
        percentComplete,
        spiDeclining,
        cpiDeclining,
        pointOfNoReturn,
        assessment,
      }
    },
  },
  {
    name: 'plan_workflow',
    description: 'Draft an executable plan for a workflow run. Reads the workflow goal + mode, the run context, and the portfolio policy set, then proposes ordered steps with rationale. For governed workflows, emits the fixed gate sequence. For assistive workflows, plans freely against allowed_actions. For hybrid, fills in steps between fixed gates. Result is a draft plan that requires human approval before execution.',
    parameters: {
      runId: { type: 'string', description: 'Workflow run ID to plan' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const {
        getWorkflowRun, getWorkflowV3, getPolicySetForPortfolio, createWorkflowPlan,
      } = await import('./dataService')
      const runId = input.runId as string
      const run = await getWorkflowRun(runId)
      if (!run) return { error: 'Run not found' }
      const wf = await getWorkflowV3(run.workflowId)
      if (!wf) return { error: 'Workflow not found' }
      const policy = await getPolicySetForPortfolio(run.portfolioId)

      type Step = {
        id: string; name: string; actionType: string; assignee: string | null
        inputs: Record<string, unknown>; expectedOutput: string | null
        gateRef?: string; status: 'pending'
      }
      const steps: Step[] = []
      let stepIdx = 1
      const nextId = () => `s${stepIdx++}`

      const costDelta = (run.context.costDelta as number) ?? 0
      const riskScore = (run.context.riskScore as number) ?? 0

      // Helper: should a gate be skipped given policy thresholds?
      const skipGate = (gateId: string): boolean => {
        if (!policy) return false
        // Finance / Exec gates are dollar-thresholded
        if (gateId === 'finance_approval' || gateId === 'finance_review') {
          const finRule = policy.data.thresholdLadder.find(t => t.requiresRole === 'FinanceManager')
          if (finRule && costDelta < finRule.atUsd) return true
        }
        if (gateId === 'exec_approval') {
          const execRule = policy.data.thresholdLadder.find(t => t.requiresRole === 'ExecutiveSponsor')
          if (execRule && costDelta < execRule.atUsd) return true
        }
        // L1 escalation skipped if risk score triggers direct L2 per escalation rules
        if (gateId === 'l1_escalation') {
          const directL2 = policy.data.escalationRules.some(r => r.escalateTo === 'VP' && riskScore >= 0.8)
          if (directL2) return true
        }
        return false
      }

      // Build steps
      if (wf.mode === 'governed' || wf.mode === 'hybrid') {
        // Pre-gate AI work for hybrid only
        if (wf.mode === 'hybrid' && wf.data.allowedActions?.length) {
          for (const action of wf.data.allowedActions) {
            if (action === 'request_artifact' && policy) {
              const required = policy.data.requiredArtifactsByType[wf.type] ?? []
              if (required.length > 0) {
                steps.push({
                  id: nextId(), name: 'Verify required artifacts', actionType: 'request_artifact',
                  assignee: run.createdBy ?? null, inputs: { artifacts: required },
                  expectedOutput: 'All required artifacts present', status: 'pending',
                })
              }
            } else if (action === 'summarize_risk' || action === 'summarize_business_case' || action === 'summarize_blocker') {
              steps.push({
                id: nextId(), name: action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                actionType: action, assignee: 'ai', inputs: {}, expectedOutput: 'Concise summary for reviewers', status: 'pending',
              })
            } else if (action === 'score_alignment' || action === 'score_severity') {
              steps.push({
                id: nextId(), name: action === 'score_alignment' ? 'Score strategic alignment' : 'Score severity',
                actionType: action, assignee: 'ai', inputs: {}, expectedOutput: 'Numeric score with rationale', status: 'pending',
              })
            } else if (action === 'propose_mitigation' || action === 'propose_workaround') {
              steps.push({
                id: nextId(), name: action === 'propose_mitigation' ? 'Propose mitigation options' : 'Propose workaround',
                actionType: action, assignee: 'ai', inputs: {}, expectedOutput: '2-3 ranked options', status: 'pending',
              })
            }
          }
        }
        // Gates from definition, filtered by policy
        for (const gateId of wf.data.gateOrder) {
          if (skipGate(gateId)) continue
          const gate = wf.data.gates[gateId]
          if (!gate) continue
          steps.push({
            id: nextId(), name: gate.label, actionType: 'gate', gateRef: gateId,
            assignee: null, inputs: {}, expectedOutput: 'Approval or rejection with rationale',
            status: 'pending',
          })
        }
      } else {
        // assistive: plan freely against allowed_actions
        for (const action of wf.data.allowedActions ?? []) {
          steps.push({
            id: nextId(), name: action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            actionType: action, assignee: action === 'notify' ? 'system' : 'ai',
            inputs: {}, expectedOutput: null, status: 'pending',
          })
        }
      }

      // Build rationale
      const rationaleParts: string[] = []
      rationaleParts.push(`Workflow mode: ${wf.mode}.`)
      if (policy) {
        rationaleParts.push(`Applied policy ${policy.name} v${policy.version}.`)
        if (costDelta > 0) {
          const skippedExec = wf.data.gateOrder.includes('exec_approval') && skipGate('exec_approval')
          const skippedFin = (wf.data.gateOrder.includes('finance_approval') || wf.data.gateOrder.includes('finance_review')) && (skipGate('finance_approval') || skipGate('finance_review'))
          if (skippedExec) rationaleParts.push(`$${costDelta.toLocaleString()} is below the executive threshold; exec gate skipped.`)
          else if (costDelta > 0) rationaleParts.push(`$${costDelta.toLocaleString()} requires the full approval ladder.`)
          if (skippedFin) rationaleParts.push(`Below finance threshold; finance gate skipped.`)
        }
        if (riskScore >= 0.8 && wf.data.gateOrder.includes('l1_escalation')) {
          rationaleParts.push(`Risk score ${riskScore} triggers direct L2 escalation per policy; L1 skipped.`)
        }
      } else {
        rationaleParts.push('No portfolio policy found; using workflow defaults.')
      }
      const rationale = rationaleParts.join(' ')

      // Persist (Supabase mode) or return draft (mock)
      const plan = await createWorkflowPlan({
        // Built by the policy rules above, with no model call to name. A
        // neutral label rather than a model id nothing here ever ran.
        runId, source: 'ai_planner', plannerModel: AI_MODEL_LABEL,
        rationale, steps,
      })

      addAuditEntry({
        action: 'plan_workflow',
        userId: 'ai',
        entityType: 'workflow_run',
        entityId: runId,
        details: { planId: plan.id, stepCount: steps.length, mode: wf.mode },
        aiGenerated: true,
        confirmed: false,
      })

      return {
        planId: plan.id,
        runId,
        workflowName: wf.name,
        mode: wf.mode,
        rationale,
        stepCount: steps.length,
        steps: steps.map(s => ({ name: s.name, actionType: s.actionType, gateRef: s.gateRef })),
        requiresApproval: true,
      }
    },
  },

  // ---------------------------------------------------------------------------
  // Supply Chain Planning AI Tools (Phase 7)
  //
  // Every handler below RUNS A PURE ENGINE and returns its output. The LLM never
  // computes a quantity, a date, a dollar amount, a score, or a compliance verdict
  // — it reasons over what the engine returned. All are read-only (no confirmation)
  // and touch neither SAP nor the Supabase overlay.
  // ---------------------------------------------------------------------------
  {
    name: 'scp_explain_allocation',
    description:
      'Run the cross-program weighted allocation solver for a short common part and return the award set with each demand\'s composite score and its seven-factor breakdown, plus the pure DPAS-first baseline it extends. Use this to DEFEND an allocation; never invent awards.',
    parameters: {
      materialRef: { type: 'string', description: 'The contended material' },
      availableSupply: { type: 'number', description: 'Scarce supply available to allocate' },
      candidates: {
        type: 'array',
        description: 'Competing demands: { demandId, program?, clin?, wbsRef?, dpasRating: "DX"|"DO"|null, requested, needByDate?, contractPriority?, latePenaltyExposure?, awardFeeExposure?, cashImpact?, programMargin? }',
      },
    },
    requiresConfirmation: false,
    handler: async (input: Record<string, unknown>) => {
      const { allocateWeighted, allocateDpasBaseline } = await import('./supply-chain/engines/gpdAlignment')
      const materialRef = String(input.materialRef ?? '')
      const availableSupply = Number(input.availableSupply ?? 0)
      const candidates = (Array.isArray(input.candidates) ? input.candidates : []) as Parameters<typeof allocateWeighted>[2]
      if (candidates.length === 0) return { error: 'No competing demands supplied; the solver has nothing to allocate.' }
      const weighted = allocateWeighted(materialRef, availableSupply, candidates)
      const baseline = allocateDpasBaseline(availableSupply, candidates)
      return {
        materialRef,
        availableSupply: weighted.availableSupply,
        totalRequested: weighted.totalRequested,
        totalAwarded: weighted.totalAwarded,
        coverageGap: weighted.coverageGap,
        ruleInputs: weighted.ruleInputs,
        awards: weighted.awards,
        dpasBaseline: baseline.awards,
        note: 'Awards, scores, and the baseline are computed by gpdAlignment. Narrate and defend them; do not recompute.',
      }
    },
  },
  {
    name: 'scp_compile_clause_flowdown',
    description:
      'Resolve which awarded compliance clauses flow down to a specific plan line (material + CLIN) and return them with their severities. Use this before describing subcontract flow-down obligations; the engine decides applicability, not you.',
    parameters: {
      materialRef: { type: 'string', description: 'Material on the plan line' },
      clin: { type: 'string', description: 'CLIN the plan line is funded from (optional)' },
      wbsRef: { type: 'string', description: 'WBS billing element (optional)' },
      rules: {
        type: 'array',
        description: 'Awarded clause set: { ruleType, clin?, materialRefs?, severity: "block"|"warn"|"info", description?, active? }',
      },
    },
    requiresConfirmation: false,
    handler: async (input: Record<string, unknown>) => {
      const { mapClauseFlowdown } = await import('./supply-chain/engines/complianceEngine')
      const rules = (Array.isArray(input.rules) ? input.rules : []) as Parameters<typeof mapClauseFlowdown>[0]
      const applicable = mapClauseFlowdown(rules, {
        materialRef: String(input.materialRef ?? ''),
        clin: (input.clin as string | undefined) ?? null,
        wbsRef: (input.wbsRef as string | undefined) ?? null,
      })
      return {
        materialRef: input.materialRef,
        clin: input.clin ?? null,
        applicableClauses: applicable,
        blockingClauses: applicable.filter((r) => r.severity === 'block').map((r) => r.ruleType),
        note: 'Berry blocks with no price differential; Buy American is a price preference; TAA is designated-country eligibility. A GIDEP alert or broken provenance is a hard stop.',
      }
    },
  },
  {
    name: 'scp_predict_dmsms_buy',
    description:
      'Size a DMSMS life-of-type / last-time buy for an obsolescent part and return the quantity and cost the engine computed. Use this before recommending a buy; never estimate the quantity yourself.',
    parameters: {
      materialRef: { type: 'string', description: 'The diminishing-source part' },
      annualDemand: { type: 'number', description: 'Annual demand for the part' },
      yearsOfSupportRemaining: { type: 'number', description: 'Years of fielded support still to cover' },
      onHandQty: { type: 'number', description: 'On-hand quantity to net against (optional)' },
      safetyPct: { type: 'number', description: 'Safety fraction over the point estimate, 0..1 (optional)' },
      unitCost: { type: 'number', description: 'Unit cost for the cost exposure (optional)' },
    },
    requiresConfirmation: false,
    handler: async (input: Record<string, unknown>) => {
      const { dmsmsLastTimeBuy } = await import('./supply-chain/engines/sustainmentEngine')
      const result = dmsmsLastTimeBuy({
        materialRef: String(input.materialRef ?? ''),
        annualDemand: Number(input.annualDemand ?? 0),
        yearsOfSupportRemaining: Number(input.yearsOfSupportRemaining ?? 0),
        onHandQty: input.onHandQty == null ? undefined : Number(input.onHandQty),
        safetyPct: input.safetyPct == null ? undefined : Number(input.safetyPct),
        unitCost: input.unitCost == null ? undefined : Number(input.unitCost),
      })
      return { ...result, note: 'Quantities and cost are computed by sustainmentEngine.dmsmsLastTimeBuy. Justify the buy; do not resize it.' }
    },
  },
  {
    name: 'scp_triage_exceptions',
    description:
      'Re-rank MRP exception messages by PROGRAM IMPACT — the threatened CLIN/WBS deliverable, DPAS rating, need-date slack, and cash at risk — rather than by quantity or value. Use this before recommending what a buyer works first.',
    parameters: {
      exceptions: {
        type: 'array',
        description: 'Exceptions: { id, materialRef, messageType, threatenedClin?, threatenedWbsRef?, dpasRating?, needByDate?, supplyDate?, quantity?, unitValue?, progressPaymentRate? }',
      },
      asOf: { type: 'string', description: 'As-of date (yyyy-mm-dd); defaults to today' },
    },
    requiresConfirmation: false,
    handler: async (input: Record<string, unknown>) => {
      const { triageExceptions } = await import('./supply-chain/engines/controlTower')
      const exceptions = (Array.isArray(input.exceptions) ? input.exceptions : []) as Parameters<typeof triageExceptions>[0]
      if (exceptions.length === 0) return { error: 'No exceptions supplied; nothing to triage.' }
      const asOf = typeof input.asOf === 'string' ? input.asOf : undefined
      const triaged = asOf ? triageExceptions(exceptions, asOf) : triageExceptions(exceptions)
      return {
        triaged,
        programThreats: triaged.filter((e) => e.isProgramThreat).length,
        note: 'The rank never depends on quantity. Explain the ranking the engine produced; do not reorder it.',
      }
    },
  },
  {
    name: 'scp_reconcile_supply_plan_pnl',
    description:
      'Roll a make/buy supply plan into each program\'s projected profit and loss by contract and fiscal period, attributing margin erosion to expedite premiums, forward-rate revisions, and shortage-driven substitutions. Use this before discussing S&OP margin; never compute the margin yourself.',
    parameters: {
      lines: {
        type: 'array',
        description: 'Supply-plan lines: { program, clin?, fiscalPeriod, materialRef, makeBuy: "make"|"buy", quantity, baselineUnitCost, expeditePremium?, forwardRateFactor?, substitutionUnitDelta?, contractRevenue? }',
      },
    },
    requiresConfirmation: false,
    handler: async (input: Record<string, unknown>) => {
      const { reconcileSupplyPlanToPnl } = await import('./supply-chain/engines/sopEngine')
      const lines = (Array.isArray(input.lines) ? input.lines : []) as Parameters<typeof reconcileSupplyPlanToPnl>[0]
      if (lines.length === 0) return { error: 'No supply-plan lines supplied; nothing to reconcile.' }
      const result = reconcileSupplyPlanToPnl(lines)
      return {
        currency: result.currency,
        totalRevenue: result.totalRevenue,
        totalPlannedCost: result.totalPlannedCost,
        totalMargin: result.totalMargin,
        totalMarginPct: result.totalMarginPct,
        totalErosion: result.totalErosion,
        programs: result.programs.map((p) => ({
          program: p.program,
          revenue: p.revenue,
          plannedCost: p.plannedCost,
          margin: p.margin,
          marginPct: p.marginPct,
          marginErosion: p.marginErosion,
          erosionDrivers: p.erosionDrivers,
          narrative: p.narrative,
        })),
        note: 'Every figure is computed by sopEngine.reconcileSupplyPlanToPnl. A GovCon S&OP number is inert unless it also ties to funded CLINs, the EVMS baseline, and progress-payment cash.',
      }
    },
  },
]

// ---------------------------------------------------------------------------
// Comprehensive project status report data gatherer
// ---------------------------------------------------------------------------
import type { StatusReportData } from '@/components/ai/StatusReportView'
import type { ForecastWbsSummary } from '@/types/forecasting'

export interface ForecastCostPosition {
  eacCost: number
  etcCost: number
  actualCost: number
  baselineBac: number
  varianceToBac: number
  eacRevenue: number
  eacMarginPercent: number
  /** How many summaries were rolled up. */
  summaryCount: number
  /** How many of those were missing at least one of the five totals. */
  summariesMissingTotals: number
}

/**
 * Roll forecast_wbs_summaries up into the project's cost position.
 *
 * forecast_wbs_summaries is an `id + data jsonb default '{}'` supporting
 * table, so a row missing one of these totals is routine. Summing without a
 * guard turned eacCost, varianceToBac and eacMarginPercent into NaN, which the
 * assistant then read out as the project's EAC. Sparse rows are counted rather
 * than hidden so the answer can say the data is incomplete.
 */
export function summarizeForecastWbsTotals(
  wbsSummaries: ForecastWbsSummary[],
): ForecastCostPosition {
  const num = (v: number | null | undefined): number => (Number.isFinite(v as number) ? (v as number) : 0)
  const totalKeys: Array<keyof ForecastWbsSummary> = [
    'totalEacCost', 'totalEtcCost', 'totalActualCost', 'baselineBac', 'totalEacRevenue',
  ]

  const totals = wbsSummaries.reduce((acc, w) => ({
    eacCost: acc.eacCost + num(w.totalEacCost),
    etcCost: acc.etcCost + num(w.totalEtcCost),
    actualCost: acc.actualCost + num(w.totalActualCost),
    baselineBac: acc.baselineBac + num(w.baselineBac),
    eacRevenue: acc.eacRevenue + num(w.totalEacRevenue),
  }), { eacCost: 0, etcCost: 0, actualCost: 0, baselineBac: 0, eacRevenue: 0 })

  const summariesMissingTotals = wbsSummaries.filter(
    (w) => totalKeys.some((k) => !Number.isFinite(w[k] as number)),
  ).length

  return {
    ...totals,
    varianceToBac: totals.baselineBac - totals.eacCost,
    eacMarginPercent: totals.eacRevenue > 0 ? ((totals.eacRevenue - totals.eacCost) / totals.eacRevenue) * 100 : 0,
    summaryCount: wbsSummaries.length,
    summariesMissingTotals,
  }
}

async function gatherProjectStatusReportData(projectId: string): Promise<StatusReportData | { error: string }> {
  const {
    getProjectById, getRisks, getResources, getResourceAssignments,
    getEvmTimeSeries, getTasksByProject, getBaselineModels,
    getForecastVersions, getForecastWbsSummaries,
    getBaselineChangeRequests, getWbsElements,
  } = await import('./dataService')
  const { calculateEvmMetrics, getLatestActualPeriod, getActualPeriods } = await import('./evmCalculationService')

  const project = await getProjectById(projectId)
  if (!project) return { error: 'Project not found' }

  // Fetch all data sources in parallel
  const [risks, tasks, evmTsArr, , forecastVersions, , resourceAssignments, allResources, changeRequests] = await Promise.all([
    getRisks(projectId),
    getTasksByProject(projectId),
    getEvmTimeSeries(projectId),
    getBaselineModels(projectId),
    getForecastVersions(projectId),
    getWbsElements(projectId),
    getResourceAssignments(projectId),
    getResources(),
    getBaselineChangeRequests(projectId),
  ])

  // ── Schedule data ──
  const completeTasks = tasks.filter(t => t.status === 'Completed' || t.percentComplete === 100)
  const inProgressTasks = tasks.filter(t => t.status === 'In Progress' && t.percentComplete < 100)
  const overdueTasks = tasks.filter(t => {
    const endDate = new Date(t.endDate)
    return endDate < new Date() && t.percentComplete < 100
  })

  // Key milestones (tasks that look like milestones — 0 duration or name includes 'milestone')
  const milestones = tasks
    .filter(t => t.duration === 0 || t.name.toLowerCase().includes('milestone') || t.name.toLowerCase().includes('review') || t.name.toLowerCase().includes('delivery'))
    .slice(0, 5)
    .map(t => ({
      name: t.name,
      date: t.endDate,
      status: t.percentComplete === 100 ? 'complete' as const
        : new Date(t.endDate) < new Date() ? 'late' as const
        : t.percentComplete > 0 ? 'on-track' as const
        : 'at-risk' as const,
    }))

  // ── EVM data ──
  const evmTs = evmTsArr[0]
  let evmMetrics = { spi: project.schedule.spi, cpi: project.schedule.cpi, sv: 0, cv: 0, eacTypical: 0, eacAtypical: 0, eacComposite: 0, tcpi: 1.0, vac: 0, percentComplete: project.schedule.percentComplete }
  let sCurveData: Array<{ period: string; pv: number; ev: number; ac: number }> = []
  let spiTrendData: Array<{ period: string; spi: number; cpi: number }> = []

  if (evmTs) {
    const latest = getLatestActualPeriod(evmTs.periods)
    if (latest && latest.ev != null && latest.ac != null) {
      const m = calculateEvmMetrics(evmTs.bac, latest.pv, latest.ev, latest.ac)
      evmMetrics = { ...m, eacComposite: m.eacComposite ?? m.eacTypical }
    }

    // S-Curve data
    const actuals = getActualPeriods(evmTs.periods)
    sCurveData = evmTs.periods.map(p => ({
      period: p.label,
      pv: p.pv,
      ev: p.ev ?? 0,
      ac: p.ac ?? 0,
    }))

    // SPI/CPI trend from actuals
    spiTrendData = actuals.filter(p => p.ev != null && p.ac != null && p.pv > 0).map(p => ({
      period: p.label,
      spi: p.pv > 0 ? (p.ev ?? 0) / p.pv : 1,
      cpi: (p.ac ?? 0) > 0 ? (p.ev ?? 0) / (p.ac ?? 1) : 1,
    }))
  }

  // ── Risk data ──
  const sortedRisks = [...risks].sort((a, b) => b.score - a.score)
  const highRisks = risks.filter(r => r.score > 0.6)
  const medRisks = risks.filter(r => r.score > 0.3 && r.score <= 0.6)
  const lowRisks = risks.filter(r => r.score <= 0.3)
  const totalExposure = risks.reduce((s, r) => s + (r.costExposure || 0), 0)

  // ── Resource data ──
  const assignedResourceIds = new Set((resourceAssignments as Array<Record<string, unknown>>).map(a => a.resourceId as string))
  const assignedResources = allResources.filter(r => assignedResourceIds.has(r.id))
  const roleGroups = new Map<string, { count: number; totalUtil: number }>()
  let overallocated = 0
  for (const r of assignedResources) {
    const role = r.role || 'Unassigned'
    const group = roleGroups.get(role) || { count: 0, totalUtil: 0 }
    group.count++
    group.totalUtil += r.utilization ?? 0
    roleGroups.set(role, group)
    if ((r.utilization ?? 0) > 100) overallocated++
  }
  const avgUtil = assignedResources.length > 0
    ? Math.round(assignedResources.reduce((s, r) => s + (r.utilization ?? 0), 0) / assignedResources.length)
    : 0

  // ── Forecast data ──
  const approvedForecast = forecastVersions.find(v => v.status === 'Approved' || v.status === 'Committed')
    ?? forecastVersions.find(v => v.status === 'Draft' || v.status === 'InProgress')
    ?? forecastVersions[0]

  let forecastSummary = summarizeForecastWbsTotals([])
  if (approvedForecast) {
    forecastSummary = summarizeForecastWbsTotals(await getForecastWbsSummaries(approvedForecast.id))
  }

  // ── Pending changes ──
  const pendingChanges = (changeRequests || [])
    .filter(cr => cr.status !== 'Approved' && cr.status !== 'Rejected')
    .map(cr => ({
      type: cr.changeType || 'Baseline Change',
      description: cr.title || cr.description || 'Change request',
      status: cr.status || 'Pending',
      impact: cr.costImpact ? `$${(cr.costImpact / 1000).toFixed(0)}K cost impact` : 'TBD',
    }))

  // ── Compute burn rate ──
  const monthsElapsed = Math.max(1, Math.ceil(
    (new Date().getTime() - new Date(project.startDate).getTime()) / (1000 * 60 * 60 * 24 * 30)
  ))
  const burnRate = project.budget.spent / monthsElapsed

  // ── Determine overall health ──
  const overallHealth: 'Green' | 'Yellow' | 'Red' =
    project.healthStatus === 'Red' || evmMetrics.cpi < 0.85 || evmMetrics.spi < 0.85 ? 'Red'
    : project.healthStatus === 'Yellow' || evmMetrics.cpi < 0.95 || evmMetrics.spi < 0.95 || highRisks.length > 2 ? 'Yellow'
    : 'Green'

  // ── Build AI narrative (data-driven) ──
  const aiNarrative = buildStatusReportNarrative(project, evmMetrics, risks, forecastSummary, assignedResources, overallocated, pendingChanges, overallHealth)

  const reportData: StatusReportData = {
    generatedAt: new Date().toISOString(),
    project: {
      id: project.id,
      name: project.name,
      code: project.code,
      status: project.status,
      healthStatus: project.healthStatus,
      priority: project.priority,
      projectManager: project.projectManagerName || project.projectManager,
      startDate: project.startDate,
      plannedEndDate: project.plannedEndDate,
      percentComplete: project.schedule.percentComplete,
      schedulingMethod: project.schedulingMethod,
    },
    overallHealth,
    executiveSummary: aiNarrative.summary,
    schedule: {
      spi: evmMetrics.spi,
      scheduleVarianceDays: project.schedule.scheduleVarianceDays,
      percentComplete: project.schedule.percentComplete,
      criticalMilestones: milestones,
      tasksTotal: tasks.length,
      tasksComplete: completeTasks.length,
      tasksInProgress: inProgressTasks.length,
      tasksOverdue: overdueTasks.length,
    },
    cost: {
      cpi: evmMetrics.cpi,
      budgetTotal: project.budget.total,
      budgetSpent: project.budget.spent,
      budgetCommitted: project.budget.committed,
      budgetAvailable: project.budget.available,
      burnRate,
      baselineBac: evmTs?.bac ?? project.budget.total,
      eacTypical: evmMetrics.eacTypical || project.budget.total,
      eacOptimistic: evmMetrics.eacAtypical || project.budget.total * 0.95,
      eacPessimistic: evmMetrics.eacComposite || project.budget.total * 1.15,
      varianceAtCompletion: evmMetrics.vac,
      costVariance: evmMetrics.cv,
      tcpi: evmMetrics.tcpi,
    },
    earnedValue: {
      bcws: evmTs ? (getLatestActualPeriod(evmTs.periods)?.pv ?? 0) : 0,
      bcwp: evmTs ? (getLatestActualPeriod(evmTs.periods)?.ev ?? 0) : 0,
      acwp: evmTs ? (getLatestActualPeriod(evmTs.periods)?.ac ?? 0) : 0,
      sv: evmMetrics.sv,
      cv: evmMetrics.cv,
      spiTrend: spiTrendData,
      sCurve: sCurveData,
    },
    risks: {
      total: risks.length,
      high: highRisks.length,
      medium: medRisks.length,
      low: lowRisks.length,
      totalExposure,
      topRisks: sortedRisks.slice(0, 5).map(r => ({
        code: r.code,
        title: r.title,
        score: r.score,
        costExposure: r.costExposure || 0,
        status: r.status,
        category: r.category,
      })),
    },
    resources: {
      totalAssigned: assignedResources.length,
      avgUtilization: avgUtil,
      overallocated,
      byRole: Array.from(roleGroups.entries()).slice(0, 8).map(([role, g]) => ({
        role,
        count: g.count,
        utilization: Math.round(g.totalUtil / g.count),
      })),
    },
    forecast: forecastSummary,
    pendingChanges: {
      total: pendingChanges.length,
      items: pendingChanges.slice(0, 5),
    },
    aiNarrative,
  }

  return reportData
}

function buildStatusReportNarrative(
  project: { name: string; schedule: { spi: number; cpi: number; percentComplete: number; scheduleVarianceDays: number }; budget: { total: number; spent: number; available: number }; healthStatus: string },
  evm: { spi: number; cpi: number; tcpi: number; sv: number; cv: number; eacTypical: number; vac: number; percentComplete: number },
  risks: Array<{ title: string; score: number; costExposure: number; category: string }>,
  forecast: { eacCost: number; varianceToBac: number; eacMarginPercent: number; summaryCount?: number; summariesMissingTotals?: number },
  resources: Array<{ utilization?: number; role: string }>,
  overallocated: number,
  pendingChanges: Array<{ type: string; description: string }>,
  overallHealth: string,
) {
  const spiStatus = evm.spi >= 0.95 ? 'on schedule' : evm.spi >= 0.85 ? 'slightly behind schedule' : 'significantly behind schedule'
  const cpiStatus = evm.cpi >= 0.95 ? 'within budget' : evm.cpi >= 0.85 ? 'experiencing minor cost pressure' : 'facing significant cost overruns'
  const highRisks = risks.filter(r => r.score > 0.6)

  const summary = `${project.name} is ${evm.percentComplete.toFixed(0)}% complete and is currently ${spiStatus} (SPI: ${evm.spi.toFixed(2)}) and ${cpiStatus} (CPI: ${evm.cpi.toFixed(2)}). ` +
    (highRisks.length > 0 ? `There are ${highRisks.length} high-severity risk(s) requiring attention. ` : '') +
    (overallocated > 0 ? `${overallocated} resource(s) are overallocated. ` : '') +
    (pendingChanges.length > 0 ? `${pendingChanges.length} change request(s) are pending review. ` : '') +
    `Overall project health is ${overallHealth}.`

  const keyFindings: string[] = []

  // Schedule findings
  if (evm.spi < 0.9) {
    keyFindings.push(`Schedule performance is critical — SPI of ${evm.spi.toFixed(2)} indicates the project is delivering only ${(evm.spi * 100).toFixed(0)}% of planned work. Recovery plan needed.`)
  } else if (evm.spi < 0.95) {
    keyFindings.push(`Schedule is trending slightly behind with SPI of ${evm.spi.toFixed(2)}. Proactive monitoring recommended to prevent further slippage.`)
  } else {
    keyFindings.push(`Schedule performance is healthy with SPI of ${evm.spi.toFixed(2)} — the project is tracking to plan.`)
  }

  // Cost findings
  if (evm.cpi < 0.9) {
    keyFindings.push(`Cost performance is a significant concern — CPI of ${evm.cpi.toFixed(2)} puts the project on track for a ${((1 - evm.cpi) * 100).toFixed(0)}% cost overrun at completion.`)
  } else if (evm.cpi < 0.95) {
    keyFindings.push(`Minor cost pressure detected with CPI of ${evm.cpi.toFixed(2)}. Budget contingency should be monitored closely.`)
  }

  // Risk findings
  if (highRisks.length > 0) {
    const totalExposure = highRisks.reduce((s, r) => s + (r.costExposure || 0), 0)
    keyFindings.push(`${highRisks.length} high-severity risk(s) carry a combined exposure of $${(totalExposure / 1000).toFixed(0)}K. Top risk: "${highRisks[0]?.title ?? 'Unknown'}".`)
  }

  // Resource findings
  if (overallocated > 0) {
    keyFindings.push(`Resource constraint detected — ${overallocated} team member(s) are overallocated (>100% utilization), which threatens delivery quality and timeline.`)
  }

  // Forecast findings
  //
  // State the gap before quoting the number. forecast_wbs_summaries stores its
  // totals in jsonb, so a sparse row silently understates the roll-up; a reader
  // has to know the EAC below is built on partial data.
  if ((forecast.summariesMissingTotals ?? 0) > 0) {
    keyFindings.push(`Forecast roll-up is incomplete - ${forecast.summariesMissingTotals} of ${forecast.summaryCount ?? 0} WBS summaries are missing at least one cost or revenue total. The EAC and margin figures below understate the position by whatever those rows hold.`)
  }
  if (forecast.varianceToBac < 0) {
    keyFindings.push(`EAC forecast of $${(forecast.eacCost / 1e6).toFixed(1)}M exceeds baseline by $${(Math.abs(forecast.varianceToBac) / 1000).toFixed(0)}K — formal rebaseline should be considered.`)
  }

  // TCPI
  if (evm.tcpi > 1.1) {
    keyFindings.push(`TCPI of ${evm.tcpi.toFixed(2)} indicates that recovering to budget would require ${((evm.tcpi - 1) * 100).toFixed(0)}% improvement in remaining cost efficiency — this is typically unachievable without scope reduction.`)
  }

  // Build immediate actions
  const immediateActions: Array<{ priority: 'critical' | 'high' | 'medium'; action: string; owner: string; dueDate: string }> = []

  if (evm.spi < 0.85) {
    immediateActions.push({ priority: 'critical', action: 'Develop and present schedule recovery plan to stakeholders', owner: 'Project Manager', dueDate: 'This week' })
  }
  if (evm.cpi < 0.85) {
    immediateActions.push({ priority: 'critical', action: 'Conduct cost variance root cause analysis and identify corrective actions', owner: 'Project Controller', dueDate: 'This week' })
  }
  if (highRisks.length > 0) {
    immediateActions.push({ priority: 'high', action: `Review and update mitigation plans for ${highRisks.length} high-severity risk(s)`, owner: 'Risk Manager', dueDate: 'Within 5 days' })
  }
  if (overallocated > 0) {
    immediateActions.push({ priority: 'high', action: `Resolve resource overallocation for ${overallocated} team member(s) — reassign or defer non-critical work`, owner: 'Resource Manager', dueDate: 'Within 1 week' })
  }
  if (pendingChanges.length > 0) {
    immediateActions.push({ priority: 'medium', action: `Process ${pendingChanges.length} pending change request(s) through governance review`, owner: 'Change Control Board', dueDate: 'Next CCB meeting' })
  }
  if (evm.spi < 0.95 && evm.spi >= 0.85) {
    immediateActions.push({ priority: 'medium', action: 'Review critical path activities and identify schedule compression opportunities', owner: 'Project Manager', dueDate: 'Within 2 weeks' })
  }
  if (forecast.eacMarginPercent < 5 && forecast.eacCost > 0) {
    immediateActions.push({ priority: 'high', action: 'Margin is below 5% — review ETC assumptions and negotiate cost reduction strategies', owner: 'Finance Lead', dueDate: 'Within 1 week' })
  }

  // If no critical actions, add positive reinforcement
  if (immediateActions.length === 0) {
    immediateActions.push({ priority: 'medium', action: 'Continue monitoring KPIs and prepare for next milestone review', owner: 'Project Manager', dueDate: 'Ongoing' })
  }

  // Build recommendations
  const recommendations: Array<{ area: string; recommendation: string; impact: string }> = []

  if (evm.spi < 0.95) {
    recommendations.push({
      area: 'Schedule',
      recommendation: 'Evaluate fast-tracking or crashing options on critical path activities. Consider parallel execution of independent work packages to recover schedule.',
      impact: `Could recover ${Math.abs(project.schedule.scheduleVarianceDays)} days of schedule variance`,
    })
  }
  if (evm.cpi < 0.95) {
    recommendations.push({
      area: 'Cost',
      recommendation: 'Conduct detailed variance analysis by WBS element to identify root cause of cost overrun. Review labor mix and consider shifting from named to generic resources where applicable.',
      impact: `Potential to improve CPI by ${((1 - evm.cpi) * 50).toFixed(0)}% through targeted interventions`,
    })
  }
  if (highRisks.length > 0) {
    recommendations.push({
      area: 'Risk',
      recommendation: `Accelerate mitigation for the top ${Math.min(3, highRisks.length)} risks. Consider pre-positioning contingency funds for the highest-exposure items.`,
      impact: `Could reduce total risk exposure by 30-50% through proactive mitigation`,
    })
  }
  if (overallocated > 0 || resources.filter(r => (r.utilization ?? 0) > 90).length > resources.length * 0.5) {
    recommendations.push({
      area: 'Resources',
      recommendation: 'Rebalance workload distribution across the team. Consider bringing in additional surge capacity for the next 2-3 sprints to relieve pressure.',
      impact: 'Improved delivery velocity and reduced burnout risk',
    })
  }
  recommendations.push({
    area: 'Governance',
    recommendation: 'Establish weekly earned value reviews with the project team to catch performance trends early. Ensure all variance narratives are documented for audit readiness.',
    impact: 'Earlier detection of issues enables less costly corrective action',
  })

  // Outlook
  const outlook = evm.spi >= 0.95 && evm.cpi >= 0.95
    ? `${project.name} is in strong position to deliver on time and within budget. Continued focus on risk mitigation and resource management will ensure successful completion. The team should maintain current execution discipline and prepare for upcoming milestone reviews.`
    : evm.spi >= 0.85 && evm.cpi >= 0.85
    ? `${project.name} faces manageable challenges that can be addressed through the recommended corrective actions. With focused attention on the ${evm.spi < evm.cpi ? 'schedule' : 'cost'} performance gap and proactive risk management, the project can still achieve its objectives. Close monitoring over the next 2-4 weeks will be critical to confirm the recovery trajectory.`
    : `${project.name} requires immediate management attention. Current performance trends suggest the project will ${evm.cpi < 0.9 ? 'exceed its budget' : ''} ${evm.spi < 0.9 && evm.cpi < 0.9 ? 'and ' : ''}${evm.spi < 0.9 ? 'miss its schedule targets' : ''} without significant intervention. A formal recovery review with senior leadership is recommended within the next 5 business days to evaluate options including rebaselining, scope reduction, or additional resource investment.`

  return { summary, keyFindings, immediateActions, recommendations, outlook }
}

// Mock AI provider for demo (no API key needed)
/**
 * Lightweight intent parser for "create portfolio …" prompts. Extracts the
 * fields needed to call the create_portfolio tool. Designed for the demo /
 * mock provider — a real LLM would return structured tool args directly.
 */
export interface ParsedPortfolioIntent {
  name?: string
  code?: string
  description?: string
  ownerName?: string
  parentName?: string
  portfolioType?: string
  startDate?: string
  endDate?: string
  budgetTotal?: number
}

export function parseCreatePortfolioIntent(raw: string): ParsedPortfolioIntent {
  const out: ParsedPortfolioIntent = {}
  // Strip leading verbs to make positional parsing easier.
  const text = raw.replace(/^\s*(please\s+)?(create|add|new|set\s+up)\s+(a\s+)?(new\s+)?portfolio[:\s]*/i, '').trim()

  // Match "called <name>" or "named <name>" or just <name> up to the next clause word.
  const calledMatch = text.match(/(?:called|named)\s+["']?([^"',]+?)["']?(?=\s+(?:owned|owner|under|with|type|description|starting|start|budget|parent|category)\b|$)/i)
  if (calledMatch && calledMatch[1]) {
    out.name = calledMatch[1].trim()
  } else {
    // Fall back to the first chunk of text up to a known clause keyword
    const fallback = text.match(/^["']?([^"',]+?)["']?(?=\s+(?:owned|owner|under|with|type|description|starting|start|budget|parent)\b|$)/i)
    if (fallback && fallback[1]) out.name = fallback[1].trim()
  }

  const owner = raw.match(/\bowner(?:ed)?(?:\s+by)?[:\s]+["']?([^"',]+?)["']?(?=\s+(?:under|with|type|description|starting|start|budget|parent|and)\b|[,.]|$)/i)
  if (owner && owner[1]) out.ownerName = owner[1].trim()

  const parent = raw.match(/\b(?:under|parent[:\s]+|inside|within)\s+["']?([^"',]+?)["']?(?=\s+(?:with|type|description|starting|start|budget|owned|owner|and)\b|[,.]|$)/i)
  if (parent && parent[1]) out.parentName = parent[1].trim()

  const type = raw.match(/\btype[:\s]+["']?([^"',]+?)["']?(?=\s+(?:with|description|starting|start|budget|owned|owner|under|parent|and)\b|[,.]|$)/i)
  if (type && type[1]) out.portfolioType = type[1].trim()

  const desc = raw.match(/\bdescription[:\s]+["']?([^"']+?)["']?(?=\s+(?:type|starting|start|budget|owned|owner|under|parent)\b|[,.]|$)/i)
  if (desc && desc[1]) out.description = desc[1].trim()

  const code = raw.match(/\bcode[:\s]+["']?([^"',\s]+)["']?/i)
  if (code && code[1]) out.code = code[1].trim()

  const start = raw.match(/\b(?:start(?:ing)?|begin(?:ning)?|from)[:\s]+(\d{4}-\d{2}-\d{2})/i)
  if (start && start[1]) out.startDate = start[1]

  const budget = raw.match(/\bbudget[:\s$]*([\d,]+(?:\.\d+)?)\s*([kKmMbB]?)/)
  if (budget && budget[1]) {
    const n = parseFloat(budget[1].replace(/,/g, ''))
    const suffix = (budget[2] ?? '').toLowerCase()
    const mult = suffix === 'k' ? 1_000 : suffix === 'm' ? 1_000_000 : suffix === 'b' ? 1_000_000_000 : 1
    if (!Number.isNaN(n)) out.budgetTotal = n * mult
  }

  return out
}

export class MockAiProvider implements AiProvider {
  name = 'mock'

  async sendMessage(messages: AiMessage[], tools?: AiTool[]): Promise<AiMessage> {
    const lastMsg = messages[messages.length - 1]
    if (!lastMsg) {
      return this.createResponse('How can I help you with your portfolio today?')
    }

    const query = lastMsg.content.toLowerCase()

    // Handle tool-based queries
    if (tools && tools.length > 0) {
      // Agile Delivery tools (only present when the agile.enabled capability
      // is on). Matched first so agile phrases like "summarize sprint" win over
      // the generic analyze / risk branches below.
      const agileResponse = await this.tryAgileTools(query, lastMsg.content, tools)
      if (agileResponse) return agileResponse

      // IT Ticketing tools (always present when the module is visible; the
      // only tools at all in the panel's IT Ticketing mode).
      const ticketingResponse = await this.tryTicketingTools(query, tools)
      if (ticketingResponse) return ticketingResponse

      if (query.includes('at risk') || query.includes('risk')) {
        const tool = tools.find(t => t.name === 'get_at_risk_projects')
        if (tool) {
          const result = await tool.handler({})
          const data = result as { count: number; projects: Array<{ name: string; spi: number; healthStatus: string }> }
          const projectList = data.projects
            .map(p => `- **${p.name}**: Health=${p.healthStatus}, SPI=${p.spi}`)
            .join('\n')
          return this.createResponse(
            `I found **${data.count} projects at risk**:\n\n${projectList}\n\nWould you like me to draft a status report for any of these?`,
            [{ id: generateId(), name: 'get_at_risk_projects', input: {}, output: result, status: 'executed' }]
          )
        }
      }

      if (query.includes('new project') || query.includes('create request') || query.includes('project request') || query.includes('new request')) {
        const tool = tools.find(t => t.name === 'guide_project_request')
        if (tool) {
          const result = await tool.handler({ currentStep: 'basics', formState: {} })
          return this.createResponse(
            'I can help you create a new project request! Here\'s what I recommend:\n\n' +
            '1. **Start with the AI-guided wizard** - Navigate to Project Requests and click "New Request"\n' +
            '2. **The wizard walks you through 7 steps**: Project basics, budget, priority, schedule, strategic alignment, funding, and review\n' +
            '3. **AI guidance appears on each step** with contextual tips, warnings, and auto-suggestions\n\n' +
            '**Key tips for approval:**\n' +
            '- Link to at least one strategic priority for a higher prioritization score\n' +
            '- Include a clear business justification with measurable outcomes\n' +
            '- Name an executive sponsor to increase credibility\n\n' +
            'Would you like me to navigate you to the new request form?',
            [{ id: generateId(), name: 'guide_project_request', input: { currentStep: 'basics' }, output: result, status: 'executed' }]
          )
        }
      }

      if (query.includes('create portfolio') || query.includes('create a portfolio') || query.includes('new portfolio') || query.includes('add portfolio') || query.includes('add a portfolio')) {
        // Parse the user's message into a portfolio-create payload.
        // Supports patterns like:
        //   "create a portfolio called Sustainment owned by Jennifer Muller under Revelation Technologies Consulting"
        //   "new portfolio: Optics R&D, owner: Marcus Reyes, type: Program"
        const raw = lastMsg.content
        const parsed = parseCreatePortfolioIntent(raw)
        const tool = tools.find(t => t.name === 'create_portfolio')

        // If we couldn't parse a name, ask for it instead of emitting a half-baked tool call.
        if (!tool || !parsed.name) {
          return this.createResponse(
            'I can create a portfolio for you. Tell me at minimum the **name** and **owner**, e.g.:\n\n' +
            '> create a portfolio called *Sustainment* owned by *Jennifer Muller* under *Revelation Technologies Consulting*\n\n' +
            'Optional: parent portfolio, description, type (Program, Operations…), start date, budget.'
          )
        }
        if (!parsed.ownerName) {
          return this.createResponse(
            `I can create the portfolio **${parsed.name}**, but I need an owner. Try:\n\n` +
            `> create portfolio ${parsed.name} owned by *<full name>*\n\n` +
            'Owners must be flagged as portfolio-owner-eligible in Personnel Management.'
          )
        }

        // Emit a pending tool call. The AiPanel renders Confirm/Reject buttons,
        // and on confirm runs the handler against Supabase.
        const summaryLines = [
          `**Name:** ${parsed.name}`,
          parsed.code ? `**Code:** ${parsed.code}` : null,
          `**Owner:** ${parsed.ownerName}`,
          parsed.parentName ? `**Parent:** ${parsed.parentName}` : '**Parent:** _(top-level)_',
          parsed.portfolioType ? `**Type:** ${parsed.portfolioType}` : null,
          parsed.startDate ? `**Start:** ${parsed.startDate}` : null,
          parsed.budgetTotal ? `**Budget:** $${parsed.budgetTotal.toLocaleString()}` : null,
          parsed.description ? `**Description:** ${parsed.description}` : null,
        ].filter(Boolean).join('\n')

        return this.createResponse(
          `Ready to create this portfolio:\n\n${summaryLines}\n\nClick **Confirm** below to write it to Supabase, or **Reject** to cancel.`,
          [{
            id: generateId(),
            name: 'create_portfolio',
            input: parsed as unknown as Record<string, unknown>,
            status: 'pending',
          }]
        )
      }

      if (query.includes('workflow') || query.includes('queue') || query.includes('approval') || query.includes('pending')) {
        const tool = tools.find(t => t.name === 'get_workflow_queue')
        if (tool) {
          const result = await tool.handler({})
          const data = result as { totalRunning: number; pendingApprovals: number; queue: Array<Record<string, unknown>>; navigationPath: string; message: string }
          let response = `**Workflow Queue Summary**\n\n- **${data.totalRunning}** running workflow(s)\n- **${data.pendingApprovals}** pending approval(s)\n\n`
          if (data.queue.length > 0) {
            response += 'Items awaiting your review:\n\n'
            for (const item of data.queue) {
              response += `- **${item.projectName}** — ${item.currentState} (${item.role})\n`
            }
            response += '\n**[Navigate to Workflows →](/workflows)** to review and action these items.'
          } else {
            response += 'Your queue is clear — no pending approvals at this time.'
          }
          return this.createResponse(
            response,
            [{ id: generateId(), name: 'get_workflow_queue', input: {}, output: result, status: 'executed' }]
          )
        }
      }

      if (query.includes('analyze') || query.includes('summary') || query.includes('summarize') || query.includes('portfolio overview') || query.includes('how are my projects')) {
        const tool = tools.find(t => t.name === 'analyze_portfolio_projects')
        if (tool) {
          const result = await tool.handler({})
          const data = result as {
            portfolioName: string; projectCount: number; totalBudget: number; totalSpent: number;
            budgetUtilization: string; avgSpi: string; avgCpi: string;
            healthBreakdown: { green: number; yellow: number; red: number };
            atRiskProjects: Array<{ name: string; code: string; status: string; healthStatus: string; spi: number; cpi: number; riskScore: number; budgetPct: string; scheduleVarianceDays: number }>;
            yellowProjects: Array<{ name: string; code: string; spi: number; cpi: number; budgetPct: string }>;
            overBudgetProjects: Array<{ name: string; code: string; budgetPct: string }>;
            behindScheduleProjects: Array<{ name: string; code: string; spi: number; varianceDays: number }>;
            topPerformers: Array<{ name: string; code: string; spi: number; cpi: number; progress: number }>;
          }

          let response = `## Portfolio Analysis: ${data.portfolioName}\n\n`
          response += `**${data.projectCount} projects** | Budget: $${(data.totalBudget / 1000000).toFixed(1)}M | Utilization: ${data.budgetUtilization}%\n`
          response += `**Avg SPI:** ${data.avgSpi} | **Avg CPI:** ${data.avgCpi}\n`
          response += `**Health:** ${data.healthBreakdown.green} Green | ${data.healthBreakdown.yellow} Yellow | ${data.healthBreakdown.red} Red\n\n`

          if (data.atRiskProjects.length > 0) {
            response += `### Issues Requiring Attention\n\n`
            for (const p of data.atRiskProjects) {
              response += `- **${p.name}** (${p.code}): Health=${p.healthStatus}, SPI=${p.spi}, CPI=${p.cpi}, Budget ${p.budgetPct}% spent`
              if (p.scheduleVarianceDays > 0) response += `, ${p.scheduleVarianceDays} days behind`
              response += `\n`
            }
            response += `\n`
          }

          if (data.yellowProjects.length > 0) {
            response += `### Watch List\n\n`
            for (const p of data.yellowProjects) {
              response += `- **${p.name}** (${p.code}): SPI=${p.spi}, CPI=${p.cpi}, Budget ${p.budgetPct}% spent\n`
            }
            response += `\n`
          }

          if (data.overBudgetProjects.length > 0) {
            response += `### Budget Concerns\n\n`
            for (const p of data.overBudgetProjects) {
              response += `- **${p.name}** (${p.code}): ${p.budgetPct}% of budget consumed\n`
            }
            response += `\n`
          }

          if (data.behindScheduleProjects.length > 0) {
            response += `### Schedule Risks\n\n`
            for (const p of data.behindScheduleProjects) {
              response += `- **${p.name}** (${p.code}): SPI=${p.spi}, ${p.varianceDays} days behind schedule\n`
            }
            response += `\n`
          }

          response += `### Opportunities\n\n`
          if (data.topPerformers.length > 0) {
            response += `- **${data.topPerformers.length} top performers** (Green, SPI/CPI >= 0.95) — consider these as models for best practices or candidates for accelerated delivery\n`
            for (const p of data.topPerformers) {
              response += `  - ${p.name} (${p.code}): SPI=${p.spi}, CPI=${p.cpi}, ${p.progress}% complete\n`
            }
          }
          if (parseFloat(data.budgetUtilization) < 70) {
            response += `- **Budget headroom available** — ${data.budgetUtilization}% utilized, consider accelerating high-value initiatives or reallocating to underfunded projects\n`
          }
          if (data.yellowProjects.length > 0) {
            response += `- **Early intervention opportunity** — ${data.yellowProjects.length} project(s) in Yellow status can be recovered with targeted action before they escalate to Red\n`
          }

          response += `\nWould you like me to drill into any specific project or draft a status report?`

          return this.createResponse(
            response,
            [{ id: generateId(), name: 'analyze_portfolio_projects', input: {}, output: result, status: 'executed' }]
          )
        }
      }

      if (query.includes('status report') || query.includes('draft report')) {
        const tool = tools.find(t => t.name === 'draft_status_report')
        if (tool) {
          // Auto-execute using the first available project to generate the visual report
          try {
            const { getProjects } = await import('./dataService')
            const projects = await getProjects()
            const targetProject = projects[0]
            if (targetProject) {
              const result = await tool.handler({ entityType: 'project', entityId: targetProject.id })
              if ('error' in result) {
                return this.createResponse(`Unable to generate status report: ${(result as { error: string }).error}`)
              }
              return this.createResponse(
                '__STATUS_REPORT__',
                [{
                  id: generateId(),
                  name: 'draft_status_report',
                  input: { entityType: 'project', entityId: targetProject.id },
                  output: result,
                  status: 'executed',
                }],
              )
            }
          } catch {
            // Fall through to generic response
          }
          return this.createResponse(
            'I can draft a comprehensive status report. Which project would you like the report for?',
            [{ id: generateId(), name: 'draft_status_report', input: { entityType: 'project' }, status: 'pending' }]
          )
        }
      }
    }

    // General responses
    if (query.includes('explain') || query.includes('why')) {
      return this.createResponse(
        'Based on the data shown, here are the key insights:\n\n' +
        '1. **CPI trending down** - Cost performance has declined 3% this quarter, driven by unplanned overtime on the Cybersecurity program\n' +
        '2. **SPI stable** - Schedule performance holds at 0.93 across the portfolio\n' +
        '3. **Budget utilization at 42%** - On track for year-end target\n\n' +
        'The main area of concern is the Cybersecurity Enhancement Program (SPI: 0.87). Would you like me to drill into that project?'
      )
    }

    if (query.includes('hello') || query.includes('hi') || query.includes('help')) {
      return this.createResponse(
        'Hello! I\'m your Tesseract AI assistant. I can help you with:\n\n' +
        '- **"Show at-risk projects"** - Find projects needing attention\n' +
        '- **"Create a new project request"** - AI-guided project intake wizard\n' +
        '- **"Explain this dashboard"** - Get AI-powered insights on current data\n' +
        '- **"Draft status report for [project]"** - Generate executive summaries\n' +
        '- **"Why is CPI down?"** - Analyze trends and variances\n\n' +
        'What would you like to know?'
      )
    }

    return this.createResponse(
      'I can help analyze your portfolio data. Try asking me about at-risk projects, budget variances, or I can draft a status report for you.'
    )
  }

  /**
   * Route an agile prompt to one of the Section 10 agile tools (mock provider).
   * Read tools run immediately and are formatted; write tools emit a pending
   * tool call whose input carries the pre-computed plan, so confirming it in
   * the panel writes exactly what was previewed. Returns null when the prompt
   * is not an agile request (the caller then falls through to the other tools).
   */
  private async tryAgileTools(query: string, rawText: string, tools: AiTool[]): Promise<AiMessage | null> {
    const has = (name: string) => tools.find((t) => t.name === name)
    const done = (content: string, call: AiToolCall) => this.createResponse(content, [call])

    // draft_backlog_from_scope (write -> pending)
    if (has('draft_backlog_from_scope') && /\bbacklog\b/.test(query) && /\b(draft|generate|create|build|propose)\b/.test(query)) {
      const epics = agilePlan.scaffoldBacklogTree(this.extractScope(rawText))
      const counts = agilePlan.countDraftTree(epics)
      const preview = epics
        .map(
          (e) =>
            `- Epic: ${e.title}\n` +
            (e.features ?? [])
              .map(
                (f) =>
                  `  - Feature: ${f.title}\n` +
                  (f.stories ?? []).map((s) => `    - Story: ${s.title}${s.storyPoints ? ` (${s.storyPoints} pts)` : ''}`).join('\n'),
              )
              .join('\n'),
        )
        .join('\n')
      return done(
        `Here is a draft backlog (${counts.epics} epic, ${counts.features} features, ${counts.stories} stories). Confirm to create it.\n\n${preview}`,
        { id: generateId(), name: 'draft_backlog_from_scope', input: { epics }, status: 'pending' },
      )
    }

    // refine_backlog (read)
    if (has('refine_backlog') && /\bbacklog\b/.test(query) && /\b(refine|refinement|groom|hygiene|health|stale|unestimated|duplicate|ready)\b/.test(query)) {
      const out = await has('refine_backlog')!.handler({})
      return done(this.formatRefine(out), { id: generateId(), name: 'refine_backlog', input: {}, output: out, status: 'executed' })
    }

    // detect_blockers (read)
    if (has('detect_blockers') && /\bblock(ed|er|ers|ing)?\b/.test(query)) {
      const out = await has('detect_blockers')!.handler({})
      return done(this.formatBlockers(out), { id: generateId(), name: 'detect_blockers', input: {}, output: out, status: 'executed' })
    }

    // plan_sprint (write -> pending)
    if (has('plan_sprint') && /\bsprint\b/.test(query) && /\b(plan|fill|pull)\b/.test(query)) {
      const ctx = await resolveAgileProject({})
      if ('error' in ctx) return this.createResponse(ctx.error)
      const { getWorkItems, getSprints } = await import('@/services/dataService')
      const [items, sprints] = await Promise.all([getWorkItems(ctx.projectId, ctx.projectCode), getSprints(ctx.projectId)])
      const sprint = agilePlan.pickTargetSprint(sprints)
      if (!sprint) return this.createResponse('No sprint to plan. Create a sprint on the Sprints page first.')
      const { rollingVelocity } = await import('@/engine/agileMetrics')
      const capacity = rollingVelocity(sprints) ?? 20
      const plan = agilePlan.selectSprintPlan(items, capacity)
      if (plan.selected.length === 0) return this.createResponse('No estimated, unassigned backlog items are available to plan into the sprint. Estimate some stories first.')
      const preview = plan.selected.map((s) => `- ${s.key} ${s.title} (${s.storyPoints} pts, ${s.priority})`).join('\n')
      return done(
        `Proposed plan for **${sprint.name}**: ${plan.totalPoints} of about ${Math.round(capacity)} points across ${plan.selected.length} items. Confirm to assign them.\n\n${preview}`,
        { id: generateId(), name: 'plan_sprint', input: { sprintId: sprint.id, itemIds: plan.selected.map((s) => s.id) }, status: 'pending' },
      )
    }

    // summarize_sprint (write -> pending)
    if (has('summarize_sprint') && /\bsprint\b/.test(query) && /\b(summari[sz]e|summary|review|retro|retrospective)\b/.test(query)) {
      const ctx = await resolveAgileProject({})
      if ('error' in ctx) return this.createResponse(ctx.error)
      const { getWorkItems, getSprints } = await import('@/services/dataService')
      const [items, sprints] = await Promise.all([getWorkItems(ctx.projectId, ctx.projectCode), getSprints(ctx.projectId)])
      const sprint = agilePlan.pickTargetSprint(sprints)
      if (!sprint) return this.createResponse('No sprint found to summarize.')
      const { rollingVelocity } = await import('@/engine/agileMetrics')
      const s = agilePlan.draftSprintSummary(sprint, items.filter((i) => i.sprintId === sprint.id), rollingVelocity(sprints))
      return done(
        `Draft summary for **${sprint.name}** (${s.completedPoints}/${s.committedPoints} pts, ${s.completionPct}%). Confirm to save it to the sprint.\n\n**Review**\n${s.reviewNotes}\n\n**Retrospective starter**\n${s.retroNotes}`,
        { id: generateId(), name: 'summarize_sprint', input: { sprintId: sprint.id, reviewNotes: s.reviewNotes, retroNotes: s.retroNotes }, status: 'pending' },
      )
    }

    // split_epic (write -> pending)
    if (has('split_epic') && /\bsplit\b/.test(query) && /\b(epic|feature)\b/.test(query)) {
      const ctx = await resolveAgileProject({})
      if ('error' in ctx) return this.createResponse(ctx.error)
      const { getWorkItems } = await import('@/services/dataService')
      const items = await getWorkItems(ctx.projectId, ctx.projectCode)
      const ref = this.extractEpicRef(rawText)
      const parent = (ref ? agilePlan.findWorkItem(items, ref) : undefined) ?? items.find((i) => i.itemType === 'epic') ?? items.find((i) => i.itemType === 'feature')
      if (!parent) return this.createResponse('No epic or feature found to split. Add one on the Backlog first, or name it in your request.')
      const stories = agilePlan.scaffoldEpicStories(parent)
      const preview = stories.map((s) => `- ${s.title}${s.storyPoints ? ` (${s.storyPoints} pts)` : ''}`).join('\n')
      return done(
        `Proposed split of **${parent.key} ${parent.title}** into ${stories.length} stories. Confirm to create them.\n\n${preview}`,
        { id: generateId(), name: 'split_epic', input: { epicId: parent.id, stories }, status: 'pending' },
      )
    }

    // estimate_story_points (read)
    if (has('estimate_story_points') && /\b(estimate|story points|sizing|size)\b/.test(query)) {
      const out = await has('estimate_story_points')!.handler({})
      return done(this.formatEstimate(out), { id: generateId(), name: 'estimate_story_points', input: {}, output: out, status: 'executed' })
    }

    // suggest_acceptance_criteria (advisory when no target item is named)
    if (has('suggest_acceptance_criteria') && /\bacceptance criteria\b/.test(query)) {
      const out = await has('suggest_acceptance_criteria')!.handler({ storyTitle: rawText })
      const list = ((out as { criteria?: string[] }).criteria) ?? []
      return done(
        `Suggested acceptance criteria:\n\n${list.map((c) => `- ${c}`).join('\n')}`,
        { id: generateId(), name: 'suggest_acceptance_criteria', input: { storyTitle: rawText }, output: out, status: 'executed' },
      )
    }

    // agile_board_query (read)
    if (
      has('agile_board_query') &&
      /\b(show|find|list|which|what)\b/.test(query) &&
      /\b(stor(y|ies)|epic|epics|feature|features|task|tasks|bug|bugs|spike|spikes|board|backlog)\b/.test(query)
    ) {
      const out = await has('agile_board_query')!.handler({ query: rawText })
      return done(this.formatBoardQuery(out), { id: generateId(), name: 'agile_board_query', input: { query: rawText }, output: out, status: 'executed' })
    }

    return null
  }

  private extractScope(text: string): string {
    const m = text.match(/scope[:\s]+([\s\S]+)$/i)
    return (m && m[1] ? m[1] : text).trim()
  }

  private extractEpicRef(text: string): string | null {
    const m = text.match(/split\s+(?:the\s+)?(?:epic|feature)\s+["']?([^"'.,;]+?)["']?(?=\s*$|[.,;])/i)
    return m && m[1] ? m[1].trim() : null
  }

  private formatRefine(out: unknown): string {
    const o = out as {
      error?: string; total?: number; healthy?: number; counts?: Record<string, number>
      [k: string]: unknown
    }
    if (o.error) return `Could not refine the backlog: ${o.error}`
    const lines = [`**Backlog refinement:** ${o.healthy ?? 0} of ${o.total ?? 0} items look healthy.`]
    const groups: [string, string][] = [
      ['oversized', 'Oversized (split)'], ['unestimated', 'Unestimated'],
      ['unready', 'Not ready'], ['stale', 'Stale'], ['duplicates', 'Possible duplicates'],
    ]
    for (const [k, label] of groups) {
      const arr = (o[k] as { key: string; title: string; detail: string }[] | undefined) ?? []
      const count = (o.counts ?? {})[k] ?? arr.length
      if (count > 0) {
        lines.push(`\n**${label}** (${count}):`)
        for (const f of arr.slice(0, 5)) lines.push(`- ${f.key} ${f.title}: ${f.detail}`)
      }
    }
    if (lines.length === 1) lines.push('\nNothing flagged. The backlog is in good shape.')
    return lines.join('\n')
  }

  private formatBlockers(out: unknown): string {
    const o = out as {
      error?: string; blockedCount?: number; dependencyRiskCount?: number
      blocked?: { key: string; title: string; reason: string }[]
      dependencyRisk?: { key: string; title: string; reason: string }[]
    }
    if (o.error) return `Could not scan for blockers: ${o.error}`
    const lines = [`**Blockers:** ${o.blockedCount ?? 0} flagged blocked, ${o.dependencyRiskCount ?? 0} at dependency risk.`]
    if ((o.blocked ?? []).length) {
      lines.push('\n**Blocked:**')
      for (const b of o.blocked!.slice(0, 8)) lines.push(`- ${b.key} ${b.title}: ${b.reason}`)
    }
    if ((o.dependencyRisk ?? []).length) {
      lines.push('\n**Dependency risk:**')
      for (const b of o.dependencyRisk!.slice(0, 8)) lines.push(`- ${b.key} ${b.title}: ${b.reason}`)
    }
    if ((o.blockedCount ?? 0) === 0 && (o.dependencyRiskCount ?? 0) === 0) {
      lines.push('\nNothing is blocked and there is no open dependency risk. The board is clear.')
    }
    return lines.join('\n')
  }

  private formatEstimate(out: unknown): string {
    const o = out as {
      error?: string; story?: string | null; suggested?: number; rationale?: string
      batch?: boolean; unestimatedCount?: number; suggestions?: { key: string; title: string; suggested: number }[]
    }
    if (o.error) return `Could not estimate: ${o.error}`
    if (o.batch) {
      const lines = [`**Estimation:** ${o.unestimatedCount ?? 0} unestimated item(s). Suggested sizes:`]
      for (const s of o.suggestions ?? []) lines.push(`- ${s.key} ${s.title}: ${s.suggested} pts`)
      if ((o.suggestions ?? []).length === 0) lines.push('- Nothing unestimated. Every story is sized.')
      return lines.join('\n')
    }
    return `**Suggested size:** ${o.suggested} points${o.story ? ` for ${o.story}` : ''}.\n\n${o.rationale ?? ''}`
  }

  private formatBoardQuery(out: unknown): string {
    const o = out as {
      error?: string; count?: number
      matches?: { key: string; type: string; title: string; statusCategory: string; priority: string; blocked: boolean }[]
    }
    if (o.error) return `Could not run the query: ${o.error}`
    const lines = [`**${o.count ?? 0} matching item(s):**`]
    for (const m of (o.matches ?? []).slice(0, 20)) {
      lines.push(`- ${m.key} [${m.type}] ${m.title} (${m.statusCategory}, ${m.priority}${m.blocked ? ', blocked' : ''})`)
    }
    if ((o.count ?? 0) === 0) lines.push('No items matched that query.')
    return lines.join('\n')
  }

  /**
   * Demo-mode keyword routing for the IT Ticketing read tools. Writes are left
   * to the real provider; this only needs to make the queue questions in the
   * panel's IT Ticketing suggestions answer sensibly without a model.
   */
  private async tryTicketingTools(query: string, tools: AiTool[]): Promise<AiMessage | null> {
    if (!tools.some(t => t.name === 'summarize_tickets')) return null
    // Outside the panel's IT Ticketing mode (PPM tools also present), only
    // claim queries that name tickets, so "what's in my workflow queue?" etc.
    // still reach the portfolio branches below.
    const focused = !tools.some(t => t.name === 'get_at_risk_projects')
    if (!focused && !/\bticket/.test(query)) return null
    if (!/\bticket|queue|unassigned|stale|sitting|longest/.test(query)) return null
    const run = async (name: string, input: Record<string, unknown>) => {
      const tool = tools.find(t => t.name === name)
      if (!tool) return null
      const output = await tool.handler(input)
      return { output, call: { id: generateId(), name, input, output, status: 'executed' as const } }
    }
    type Row = { ticketNumber: string; title: string; status: string; priority: string; assignee: string; timeInCurrentStatus?: string }
    const lines = (rows: Row[]) => rows.map(r =>
      `- **${r.ticketNumber}** ${r.title} (${r.status}, ${r.priority}, ${r.assignee}${r.timeInCurrentStatus ? `, ${r.timeInCurrentStatus} in status` : ''})`).join('\n')

    if (/stale|sitting|longest|oldest|aging/.test(query)) {
      const r = await run('find_stale_tickets', { limit: 10 })
      if (!r) return null
      const o = r.output as { total: number; tickets: Row[] }
      return this.createResponse(o.total === 0 ? 'No open tickets right now.' : `The open tickets that have sat longest in their current status:\n\n${lines(o.tickets)}`, [r.call])
    }
    if (/unassigned/.test(query)) {
      const r = await run('find_stale_tickets', { assigneeName: 'unassigned', limit: 20 })
      if (!r) return null
      const o = r.output as { total: number; tickets: Row[] }
      return this.createResponse(o.total === 0 ? 'Every open ticket has an assignee.' : `**${o.total} open tickets are unassigned:**\n\n${lines(o.tickets)}`, [r.call])
    }
    if (/critical|high/.test(query)) {
      const [c, h] = await Promise.all([run('list_tickets', { priority: 'Critical' }), run('list_tickets', { priority: 'High' })])
      if (!c || !h) return null
      const open = [...(c.output as { tickets: Row[] }).tickets, ...(h.output as { tickets: Row[] }).tickets]
        .filter(t => t.status !== 'Resolved' && t.status !== 'Closed')
      return this.createResponse(open.length === 0 ? 'No open Critical or High priority tickets.' : `**Open Critical / High tickets:**\n\n${lines(open)}`, [c.call, h.call])
    }
    if (/summar|queue|overview|how many/.test(query) && !/this ticket/.test(query)) {
      const r = await run('summarize_tickets', {})
      if (!r) return null
      const o = r.output as { total: number; byStatus: Record<string, number>; byAssignee: Record<string, number> }
      const fmt = (m: Record<string, number>) => Object.entries(m).sort((a, b) => b[1] - a[1]).map(([k, v]) => `- ${k}: ${v}`).join('\n')
      return this.createResponse(`**${o.total} tickets in the queue.**\n\n**By status**\n${fmt(o.byStatus)}\n\n**By assignee**\n${fmt(o.byAssignee)}`, [r.call])
    }
    return null
  }

  private createResponse(content: string, toolCalls?: AiToolCall[]): AiMessage {
    return {
      id: generateId(),
      role: 'assistant',
      content,
      timestamp: new Date().toISOString(),
      toolCalls,
    }
  }

  /**
   * CRM Quick Capture extraction, without a model.
   *
   * The capture page asks the PROVIDER to turn a transcript into proposed
   * actions rather than branching on a provider name, and this is the mock's
   * answer: the same deterministic keyword parse the live path falls back to,
   * running against the same directory, producing the same `ProposedAction`
   * shape. So the whole capture flow (cards, per-card edit, accept, execute
   * through the confirm-gated tools, audit entry) demos end to end with no API
   * key, and what is being demoed is the real flow rather than a mock of it.
   *
   * Deliberately keyword-driven and deliberately unclever: it resolves names
   * only against the directory it is handed and returns nothing with a stated
   * reason when it recognises nothing. See `lib/crm/captureExtraction.ts`.
   */
  async extractCaptureActions(input: {
    transcript: string
    directory: CaptureDirectory
  }): Promise<{ actions: ProposedAction[]; emptyReason: string | null }> {
    const { deterministicExtract } = await import('@/lib/crm/captureExtraction')
    return deterministicExtract({ transcript: input.transcript, directory: input.directory })
  }
}

// Debug log for prompt inspection
export interface DebugLogEntry {
  id: string
  timestamp: string
  direction: 'request' | 'response'
  data: unknown
}

const debugLog: DebugLogEntry[] = []

export function getDebugLog(): DebugLogEntry[] {
  return [...debugLog]
}

export function clearDebugLog(): void {
  debugLog.length = 0
}

function addDebugEntry(direction: 'request' | 'response', data: unknown): void {
  debugLog.push({
    id: generateId(),
    timestamp: new Date().toISOString(),
    direction,
    data,
  })
}

// Anthropic API types
interface AnthropicMessage {
  role: 'user' | 'assistant'
  content: string | AnthropicContentBlock[]
}

interface AnthropicContentBlock {
  type: 'text' | 'tool_use' | 'tool_result'
  text?: string
  id?: string
  name?: string
  input?: Record<string, unknown>
  tool_use_id?: string
  content?: string
  is_error?: boolean
}

interface AnthropicTool {
  name: string
  description: string
  input_schema: {
    type: 'object'
    properties: Record<string, unknown>
    required?: string[]
  }
}

interface AnthropicResponse {
  id: string
  content: AnthropicContentBlock[]
  stop_reason: 'end_turn' | 'tool_use' | 'max_tokens'
  usage: { input_tokens: number; output_tokens: number }
}

const PPM_SYSTEM_PROMPT = `You are the Tesseract AI assistant, embedded in Tesseract XPM (Total Program Management). You help project managers, portfolio directors, and executives analyze portfolio data, identify risks, and make informed decisions.

Your capabilities:
- Analyze portfolio KPIs (SPI, CPI, budget utilization, risk scores)
- Identify at-risk projects and explain why they need attention
- Draft comprehensive, visually rich executive status reports grounded in real data
- Guide users through project request creation
- Explain trends, variances, and anomalies in portfolio data
- Analyze earned value performance and forecast completion scenarios

Guidelines:
- Always ground your responses in the data returned by tools. Do not fabricate numbers.
- When referencing metrics, cite the specific values (e.g., "SPI of 0.87" not just "low SPI").
- Use clear, professional language suitable for executive stakeholders.
- If a tool call fails, explain what happened and suggest alternatives.
- For write actions (like drafting reports), note that they require user confirmation before execution.
- Keep responses concise but comprehensive. Use markdown formatting for readability.
- When the draft_status_report tool returns data, summarize the key findings briefly — the visual report will be rendered automatically by the UI.`

// The server-endpoint provider: posts the app's AI wire format to the AI
// endpoint, where the server picks the model vendor and holds its key. The
// class and its `name` are historical; neither says which vendor answers.
export class ClaudeAiProvider implements AiProvider {
  name = 'claude'
  private model: string
  lastReportedModel: string | null = null

  // The link is not held here: every request resolves the centrally managed
  // AI link (services/ai/aiConnection), falling back to the build default.
  constructor(model = 'claude-sonnet-4-6') {
    this.model = model
  }

  async sendMessage(messages: AiMessage[], tools?: AiTool[], opts?: AiSendOptions): Promise<AiMessage> {
    const anthropicMessages = this.convertMessages(messages)
    const anthropicTools = tools ? this.convertTools(tools) : undefined
    const systemPrompt = opts?.systemPrompt ?? PPM_SYSTEM_PROMPT

    let response = await this.callApi(anthropicMessages, anthropicTools, systemPrompt)
    const allToolCalls: AiToolCall[] = []

    // Agentic tool-use loop
    while (response.stop_reason === 'tool_use') {
      const toolUseBlocks = response.content.filter(b => b.type === 'tool_use')
      const toolResults: AnthropicContentBlock[] = []

      for (const block of toolUseBlocks) {
        const tool = tools?.find(t => t.name === block.name)
        const toolCall: AiToolCall = {
          id: block.id!,
          name: block.name!,
          input: block.input ?? {},
          status: 'executed',
        }

        if (tool) {
          try {
            const result = await tool.handler(block.input ?? {})
            toolCall.output = result
            toolResults.push({
              type: 'tool_result',
              tool_use_id: block.id,
              content: JSON.stringify(result),
            })
          } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Tool execution failed'
            toolCall.status = 'rejected'
            toolResults.push({
              type: 'tool_result',
              tool_use_id: block.id,
              content: errorMsg,
              is_error: true,
            })
          }
        } else {
          toolResults.push({
            type: 'tool_result',
            tool_use_id: block.id,
            content: `Unknown tool: ${block.name}`,
            is_error: true,
          })
        }

        allToolCalls.push(toolCall)
      }

      anthropicMessages.push({
        role: 'assistant',
        content: response.content,
      })
      anthropicMessages.push({
        role: 'user',
        content: toolResults,
      })

      response = await this.callApi(anthropicMessages, anthropicTools, systemPrompt)
    }

    const textContent = response.content
      .filter(b => b.type === 'text')
      .map(b => b.text)
      .join('\n')

    return {
      id: response.id,
      role: 'assistant',
      content: textContent,
      timestamp: new Date().toISOString(),
      toolCalls: allToolCalls.length > 0 ? allToolCalls : undefined,
    }
  }

  private async callApi(
    messages: AnthropicMessage[],
    tools?: AnthropicTool[],
    systemPrompt: string = PPM_SYSTEM_PROMPT,
  ): Promise<AnthropicResponse> {
    const body: Record<string, unknown> = {
      model: this.model,
      max_tokens: 4096,
      system: systemPrompt,
      messages,
    }
    if (tools && tools.length > 0) {
      body.tools = tools
    }

    addDebugEntry('request', body)

    const res = await fetch(await resolveAiProxyUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await entraAuthHeaders()) },
      body: JSON.stringify(body),
    })

    if (!res.ok) {
      const errorText = await res.text()
      addDebugEntry('response', { status: res.status, error: errorText })
      throw new AiProxyError(res.status, errorText)
    }

    noteAiResponseMeta(readAiResponseMeta(res.headers))
    const data = await res.json()
    addDebugEntry('response', data)
    this.lastReportedModel = reportedModelOrNull((data as { model?: unknown }).model) ?? this.lastReportedModel
    return data as AnthropicResponse
  }

  private convertMessages(messages: AiMessage[]): AnthropicMessage[] {
    return messages
      .filter(m => m.role !== 'system')
      .map(m => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
      }))
  }

  private convertTools(tools: AiTool[]): AnthropicTool[] {
    return tools.map(t => ({
      name: t.name,
      description: t.description,
      input_schema: {
        type: 'object' as const,
        properties: t.parameters,
      },
    }))
  }
}

/**
 * The provider on an AI-free build. Every call throws.
 *
 * This is what stops `MockAiProvider` becoming the de facto provider when AI is
 * switched off: the mock returns confident, fabricated prose with invented
 * figures, which in a timesheet or payroll screen reads exactly like analysis.
 * Refusing loudly is the only safe behaviour.
 */
export class DisabledAiProvider implements AiProvider {
  name = 'disabled'

  async sendMessage(): Promise<AiMessage> {
    assertAiAllowed('The AI assistant')
    throw new Error('unreachable')
  }
}

// Singleton provider instance — auto-initialize from env
function createProvider(): AiProvider {
  if (AI_FEATURES_DISABLED) {
    console.log('[AI] AI features are switched off for this build; no provider, no egress')
    return new DisabledAiProvider()
  }
  const providerType = import.meta.env.VITE_AI_PROVIDER

  if (providerType === 'anthropic') {
    const model = import.meta.env.VITE_AI_MODEL || 'claude-sonnet-4-6'
    // Same-origin proxy that injects the server-side key. The build default is
    // the live app's /api/ai/messages, or VITE_AI_PROXY_URL (the S/4-hosted build
    // sets /sap/bc/zm12ai so the call stays inside SAP). An admin can override it
    // centrally (Workflow and Forms Management > AI Connection); every request
    // resolves the link through services/ai/aiConnection.
    console.log(`[AI] Using the server AI endpoint (requested model ${model}), build default ${buildDefaultAiProxyUrl()}`)
    return new ClaudeAiProvider(model)
  }

  console.log('[AI] Using mock provider (the server AI endpoint is not enabled on this build)')
  return new MockAiProvider()
}

let provider: AiProvider = createProvider()

export function setAiProvider(p: AiProvider) {
  provider = p
}

export function getAiProvider(): AiProvider {
  return provider
}

/**
 * Mock response generator for demo mode.
 * Detects intent from the system/user prompts and returns contextual mock content.
 */
function getMockGenerateResponse(systemPrompt: string, userPrompt: string): string {
  const s = (systemPrompt + ' ' + userPrompt).toLowerCase()

  // Scenario planning — analyze
  if (s.includes('scenario') && (s.includes('analyze') || s.includes('analysis') || s.includes('assessment'))) {
    return `## Assessment
The portfolio has a healthy mix of scenarios covering conservative through aggressive strategies. The current baseline provides solid coverage but leaves approximately $2.1M in potential benefit value on the table by excluding several mid-priority initiatives.

## Key Findings
- **Budget utilization is conservative** — the baseline uses only 78% of its budget cap, leaving $1.8M unallocated that could fund 2-3 additional initiatives
- **High-ROI exclusions** — two excluded initiatives ("Cloud Migration Phase 2" and "Data Analytics Platform") have ROI above 180% but were cut due to headcount constraints, not budget
- **Headcount is the binding constraint** — all scenarios hit 90%+ of their headcount cap before reaching budget limits, suggesting the portfolio is talent-constrained rather than funding-constrained
- **Contingency may be over-allocated** — the 15% contingency reserve ($1.2M) exceeds the historical variance of 8-10%, potentially locking up usable funds

## Recommendations
- **Consider a hybrid scenario** that increases headcount cap by 5 FTE while keeping budget flat — this would unlock the two high-ROI initiatives above
- **Reduce contingency to 10%** on the baseline to free $400K for immediate allocation
- **Create a "talent-first" scenario** that prioritizes initiatives with lower FTE requirements to maximize output within the headcount constraint
- **Review the "Aggressive Growth" scenario** for Q3 — if hiring targets are met, it becomes viable mid-year
- **Flag dependency risk** between the 3 infrastructure initiatives — if one slips, the others will cascade`
  }

  // Scenario planning — suggest/build
  if (s.includes('scenario') && (s.includes('suggest') || s.includes('propose') || s.includes('gap'))) {
    return `## Proposed Scenario: "Smart Growth — Talent Optimized"
A headcount-efficient scenario that maximizes benefit value per FTE by prioritizing initiatives with high impact-to-staffing ratios.

## Strategy
- **Include** all initiatives with benefit-per-FTE above $150K (8 of 12 candidates)
- **Exclude** "Enterprise Platform Rebuild" (high FTE demand of 8.5, moderate ROI of 120%) — defer to Q3 when contractor capacity opens up
- **Exclude** "Legacy System Decommission" — low strategic value and can be batched into next fiscal year
- **Include** "AI/ML Pipeline" despite lower priority score — its 3.2 FTE requirement is modest and it has cross-initiative dependencies that de-risk 2 other included projects
- **Shift** "Customer Portal v3" start date from Q1 to Q2 to avoid headcount peak in months 1-3

## Parameters
- **Budget Cap:** $8.2M (5% above baseline — funded by reducing contingency from 15% to 11%)
- **Headcount Cap:** 42 FTE (same as baseline — this scenario works within existing constraints)
- **Inflation Rate:** 3.0% | **Labor Multiplier:** 1.05x | **Contingency:** 11%

## Expected Outcome
- **Total Benefit Value:** $14.8M (+$2.1M over baseline, +16.5%)
- **Portfolio ROI:** 180% (vs. 155% baseline)
- **Headcount Utilization:** 97% — near-optimal without exceeding cap
- **Risk:** Moderate — reduced contingency buffer means less room for cost overruns, but the excluded high-FTE initiatives reduce execution risk`
  }

  // Budget advisor
  if (s.includes('budget') && (s.includes('advisor') || s.includes('analysis') || s.includes('forecast'))) {
    return `## Budget Health Assessment
The portfolio budget is in good standing with 73% of allocated funds distributed to active projects. However, two projects are forecasting over their baseline by a combined $680K.

## Key Findings
- **Project Alpha** is trending 12% over baseline ($340K) due to scope additions approved in Q2 — a formal budget change request is recommended
- **Project Delta** has $340K in uncommitted contingency that could be redistributed to underfunded initiatives
- **Category imbalance** — Capital budget is 95% utilized while Expense is only 61%, suggesting rebalancing opportunity
- **Funding pipeline** — $1.2M in pending requests from two organizations remain unapproved past their target dates

## Recommendations
- Submit a budget change request for Project Alpha to formalize the $340K overrun
- Transfer $200K from Project Delta's contingency to the underfunded "Data Analytics" initiative
- Escalate the two stale funding requests — they are blocking Q3 project starts
- Consider moving $500K from Expense to Capital category to align with actual spend patterns`
  }

  // Resource analysis
  if (s.includes('resource') || s.includes('capacity') || s.includes('staffing') || s.includes('utilization')) {
    return `## Resource Utilization Assessment
Overall portfolio utilization is at 84%, which is within the healthy 75-90% range. However, utilization is unevenly distributed across roles and teams.

## Key Findings
- **Senior Engineers** are at 97% utilization — effectively maxed out with no capacity for new work or unplanned tasks
- **Project Managers** are at 68% utilization — there is capacity to absorb 1-2 additional projects without new hires
- **QA/Testing** is the bottleneck — 3 projects are sharing 2 QA resources, causing testing delays of 1-2 weeks per sprint
- **Contractor dependency** — 31% of total FTE is contractor-based, above the 25% recommended threshold for knowledge retention

## Recommendations
- **Immediately** hire or contract 1 additional QA resource to unblock the testing pipeline
- **Redistribute** PM capacity — assign the underutilized PMs to support the resource-constrained engineering teams with coordination and stakeholder management
- **Plan for Senior Engineer capacity** in Q3 — 2 initiatives are forecasting increased demand that cannot be met with current headcount
- **Reduce contractor ratio** by converting the 2 longest-tenured contractors (12+ months) to FTE to reduce knowledge risk`
  }

  // Risk analysis
  if (s.includes('risk') && (s.includes('analysis') || s.includes('assess') || s.includes('register'))) {
    return `## Risk Assessment Summary
The portfolio carries 14 active risks across 8 projects, with 3 rated as High severity requiring immediate mitigation attention.

## Critical Risks
- **R-007: Vendor dependency on CloudCorp** — Single-source risk for infrastructure. Impact: $2.1M exposure if vendor fails to deliver. Mitigation: Begin parallel evaluation of Azure alternative (due by Q2 end)
- **R-012: Key personnel risk** — Lead architect on 2 critical-path projects has indicated potential departure. Impact: 3-6 month project delays. Mitigation: Initiate knowledge transfer and begin backfill recruiting
- **R-015: Regulatory compliance deadline** — New data residency requirements effective July 1. Two projects need architecture changes. Impact: $500K rework + potential fines. Mitigation: Escalate to Priority 1 and dedicate sprint capacity

## Recommendations
- Escalate the 3 High-severity risks to the portfolio steering committee this week
- Allocate $150K from contingency reserves to fund the CloudCorp alternative evaluation
- Schedule a risk review workshop for the 5 Medium-severity risks before they escalate
- Update the risk register — 4 risks have not been reviewed in 30+ days`
  }

  // Priority JSON generation (AI Draft modal — expects raw JSON array)
  if (s.includes('valid json array') && s.includes('strategic')) {
    return `[
  {
    "name": "Operational Excellence & Delivery Performance",
    "description": "Improve project delivery predictability and reduce schedule variance across the portfolio through standardized execution practices and proactive risk management.",
    "weight": 0.35,
    "kpis": [
      { "name": "Schedule Performance Index (SPI)", "targetValue": 0.95, "unit": "ratio", "measurementFrequency": "Monthly" },
      { "name": "On-Time Delivery Rate", "targetValue": 90, "unit": "%", "measurementFrequency": "Quarterly" },
      { "name": "Average Schedule Variance", "targetValue": 5, "unit": "days", "measurementFrequency": "Monthly" }
    ]
  },
  {
    "name": "Cost Optimization & Budget Discipline",
    "description": "Maintain rigorous cost control and maximize return on portfolio investment by reducing budget overruns and improving forecasting accuracy.",
    "weight": 0.35,
    "kpis": [
      { "name": "Cost Performance Index (CPI)", "targetValue": 0.97, "unit": "ratio", "measurementFrequency": "Monthly" },
      { "name": "Budget Variance", "targetValue": 5, "unit": "%", "measurementFrequency": "Quarterly" },
      { "name": "Forecast Accuracy", "targetValue": 92, "unit": "%", "measurementFrequency": "Quarterly" },
      { "name": "Portfolio ROI", "targetValue": 150, "unit": "%", "measurementFrequency": "Annual" }
    ]
  },
  {
    "name": "Innovation & Digital Transformation",
    "description": "Accelerate adoption of emerging technologies and modernize legacy systems to drive competitive advantage and long-term organizational agility.",
    "weight": 0.30,
    "kpis": [
      { "name": "Digital Initiative Completion Rate", "targetValue": 85, "unit": "%", "measurementFrequency": "Quarterly" },
      { "name": "Legacy System Reduction", "targetValue": 20, "unit": "%", "measurementFrequency": "Annual" },
      { "name": "Technology Adoption Score", "targetValue": 80, "unit": "%", "measurementFrequency": "Quarterly" }
    ]
  }
]`
  }

  // Priority / strategic alignment
  if (s.includes('priorit') || s.includes('strategic') || s.includes('alignment')) {
    return `## Strategic Alignment Assessment
The portfolio shows 72% alignment with the organization's stated strategic objectives. However, 3 initiatives with low strategic alignment are consuming 28% of total budget.

## Key Findings
- **Top-aligned initiatives** (score 85+): 5 initiatives consuming 45% of budget with clear ties to digital transformation and customer experience goals
- **Misaligned spend** — "Legacy Reporting Overhaul" (priority: 42) and "Office Relocation IT" (priority: 38) are consuming $1.8M combined with minimal strategic value
- **Quick wins available** — 2 deferred initiatives ("API Modernization" and "Self-Service Analytics") have high priority scores (88, 91) but were excluded due to timing, not budget

## Recommendations
- Re-evaluate the 2 low-priority initiatives — consider descoping or deferring to free budget for higher-value work
- Accelerate "API Modernization" into Q2 — it has the highest priority score and enables 3 downstream initiatives
- Establish a quarterly priority recalibration process — 6 initiatives have not had their scores updated since initial planning
- Link KPI targets to initiative outcomes to improve traceability from strategy to execution`
  }

  // Scheduling / Gantt
  if (s.includes('schedul') || s.includes('gantt') || s.includes('timeline') || s.includes('critical path')) {
    return `## Schedule Analysis
The portfolio has 12 active work packages across the critical path, with an overall Schedule Performance Index (SPI) of 0.94 — slightly behind plan.

## Key Findings
- **Critical path at risk** — Task "Infrastructure Setup" is 8 days behind schedule, which directly impacts 3 downstream deliverables
- **Resource contention** — 2 tasks scheduled in parallel both require the same senior architect, creating a hidden dependency
- **Milestone clustering** — 5 of 8 major milestones fall within a 2-week window in Q2, creating delivery risk if any task slips
- **Buffer consumption** — 60% of schedule buffer has been consumed in the first 40% of the project timeline

## Recommendations
- Add 2 weeks of buffer before the Q2 milestone cluster to absorb upstream delays
- Resolve the resource conflict by staggering the parallel tasks or assigning a secondary architect
- Escalate the Infrastructure Setup delay — it needs a recovery plan this week to avoid cascading impacts
- Consider fast-tracking the "Testing Phase" by running integration and regression testing in parallel`
  }

  // Project requests
  if (s.includes('request') && s.includes('project')) {
    return `## Project Request Pipeline Assessment
There are 8 project requests in the pipeline: 3 pending review, 2 in evaluation, 2 approved awaiting funding, and 1 returned for revision.

## Key Findings
- **Average cycle time** is 23 days from submission to decision — above the 15-day target
- **Bottleneck at evaluation** — 2 requests have been in evaluation for 18+ days without progress, likely due to evaluator availability
- **High-value request pending** — "AI Customer Insights Platform" ($2.4M, priority score 92) has been waiting 12 days for initial review
- **Duplicate requests detected** — "Data Lake Enhancement" and "Analytics Infrastructure Upgrade" have 70% scope overlap and should be consolidated

## Recommendations
- Expedite review of the AI Customer Insights request — it has the highest strategic value in the queue
- Consolidate the two overlapping requests into a single initiative to avoid duplicate investment
- Set up automated escalation for requests exceeding 15 days without status change
- Consider a fast-track approval path for requests under $500K to reduce administrative overhead`
  }

  // Generic / fallback
  return `## Analysis Summary
Based on the current portfolio data, here are the key observations and recommendations.

## Key Findings
- The portfolio is performing at 87% of plan across the primary KPIs, with schedule performance slightly lagging cost performance
- Resource utilization is within healthy ranges overall, though specific roles show constraints that could impact upcoming milestones
- Three initiatives are trending above their approved baselines and may require formal change requests before quarter-end
- Strategic alignment remains strong for the top 60% of the portfolio by budget, but lower-priority items should be reviewed for continued relevance

## Recommendations
- Conduct a mid-cycle portfolio review to revalidate initiative priorities against current strategic objectives
- Address the resource bottlenecks in engineering and QA before they impact critical-path deliverables
- Formalize the budget overruns through change requests to maintain governance compliance
- Consider deferring 1-2 lower-priority initiatives to create capacity buffer for the high-performing initiatives`
}
void getMockGenerateResponse
// ── Contract PDF Ingestion ────────────────────────────────────────────────────

const MOCK_INGESTED_CONTRACT: IngestedContractDraft = {
  title: 'Integrated Logistics Support & Sustainment Services',
  piid: 'N00178-26-C-0314',
  contract_number: 'N00178-26-C-0314',
  contract_type: ContractType.CPFF,
  counterparty_name: 'Lockheed Martin Corporation',
  counterparty_contact: {
    name: 'Patricia Nguyen',
    title: 'Contracts Manager',
    email: 'p.nguyen@lm.com',
    phone: '(301) 897-6000',
  },
  estimated_value: 28500000,
  ceiling_value: 32000000,
  period_of_performance_start: '2026-05-01',
  period_of_performance_end: '2029-04-30',
  base_period_months: 36,
  option_periods: [
    { period_number: 1, duration_months: 12, estimated_value: 9500000, status: 'NOT_EXERCISED' },
  ],
  naics_code: '541330',
  set_aside_type: 'NONE',
  prime_or_sub: 'PRIME',
  contracting_officer: 'CDR James Watkins, USN',
  program_office: 'Naval Sea Systems Command (NAVSEA)',
  clauses: [
    { clause_title: 'Definitions', clause_type: ClauseType.GENERAL, content: 'As used throughout this contract, the following terms shall have the meanings set forth in FAR Part 2 and as supplemented herein.', far_reference: 'FAR 52.202-1', is_non_standard: false },
    { clause_title: 'Payments Under Cost-Plus-Fixed-Fee Contracts', clause_type: ClauseType.PAYMENT, content: 'The Government shall make payments to the Contractor when requested as work progresses, but not more frequently than bi-weekly, in amounts determined to be allowable by the Contracting Officer.', far_reference: 'FAR 52.216-8', is_non_standard: false },
    { clause_title: 'Inspection of Services — Cost Reimbursement', clause_type: ClauseType.INSPECTION, content: 'The Contractor shall provide and maintain an inspection system acceptable to the Government covering services under this contract.', far_reference: 'FAR 52.246-5', is_non_standard: false },
    { clause_title: 'Termination for Convenience of the Government', clause_type: ClauseType.TERMINATION, content: 'The Government may terminate performance of work under this contract in whole or, from time to time, in part if the Contracting Officer determines that a termination is in the Government\'s interest.', far_reference: 'FAR 52.249-6', is_non_standard: false },
    { clause_title: 'Disputes', clause_type: ClauseType.DISPUTES, content: 'This contract is subject to 41 U.S.C. chapter 71, Contract Disputes. Failure of the parties to this contract to reach agreement on any request for equitable adjustment shall constitute a dispute.', far_reference: 'FAR 52.233-1', is_non_standard: false },
    { clause_title: 'Rights in Technical Data — Noncommercial', clause_type: ClauseType.DATA_RIGHTS, content: 'Contractor grants the Government unlimited rights in all technical data first produced in the performance of this contract.', far_reference: 'DFARS 252.227-7013', is_non_standard: false },
    { clause_title: 'Cybersecurity Maturity Model Certification (CMMC)', clause_type: ClauseType.CYBERSECURITY, content: 'The Contractor shall have a current CMMC Level 2 certification prior to award and shall maintain certification throughout the period of performance.', far_reference: 'DFARS 252.204-7021', is_non_standard: false },
    { clause_title: 'Small Business Subcontracting Plan', clause_type: ClauseType.SMALL_BUSINESS, content: 'The Contractor agrees to carry out this plan submitted with the offer with goals for small business, veteran-owned small business, service-disabled veteran-owned small business, HUBZone, small disadvantaged business, and women-owned small business concerns.', far_reference: 'FAR 52.219-9', is_non_standard: false },
    { clause_title: 'Deliverables and Data Requirements List (CDRL)', clause_type: ClauseType.DELIVERABLES, content: 'Contractor shall deliver all Contract Data Requirements as specified on DD Form 1423 attached hereto as Attachment A. Deliverables shall be submitted electronically via the Electronic Document Access (EDA) portal.', far_reference: null, is_non_standard: true },
    { clause_title: 'CPFF Task Order Pricing Structure', clause_type: ClauseType.PRICING, content: 'Individual task orders under this contract shall be negotiated using the labor categories and rates established in Attachment B. Indirect rates shall be applied in accordance with the contractor\'s approved forward pricing rate agreement.', far_reference: null, is_non_standard: true },
    { clause_title: 'Warranty on Deliverables', clause_type: ClauseType.WARRANTY, content: 'Contractor warrants that all deliverables shall conform to the contract requirements and shall be free from defects in material and workmanship for a period of one year after Government acceptance.', far_reference: 'FAR 52.246-21', is_non_standard: false },
    { clause_title: 'Intellectual Property — Background and Foreground Rights', clause_type: ClauseType.INTELLECTUAL_PROPERTY, content: 'Contractor retains ownership of background IP. All foreground IP developed under this contract shall be jointly owned by Contractor and the Government, with Government receiving unlimited rights for Government purposes only.', far_reference: null, is_non_standard: true },
  ],
  clins: [
    {
      clin_number: '0001',
      description: 'Systems Engineering & Technical Services — Base Year',
      category: 'SERVICE',
      pricing_type: 'CPFF',
      quantity: null,
      unit_of_measure: 'LO',
      unit_price: null,
      total_price: 9500000,
      is_option: false,
      option_period: null,
      nsf_flag: false,
      period_of_performance_start: '2026-05-01',
      period_of_performance_end: '2027-04-30',
      slins: [
        { slin_number: '0001AA', slin_type: 'PRICED', description: 'Engineering Labor — Base Year', acrn_code: 'AA', total_price: 8200000 },
        { slin_number: '0001AB', slin_type: 'PRICED', description: 'Other Direct Costs (ODC) — Base Year', acrn_code: 'AB', total_price: 1300000 },
      ],
      elin_references: [],
    },
    {
      clin_number: '0002',
      description: 'Data & Deliverables — Base Year (See Exhibit A)',
      category: 'DATA',
      pricing_type: 'FFP',
      quantity: null,
      unit_of_measure: 'LO',
      unit_price: null,
      total_price: 0,
      is_option: false,
      option_period: null,
      nsf_flag: true,
      period_of_performance_start: '2026-05-01',
      period_of_performance_end: '2027-04-30',
      slins: [
        { slin_number: '000201', slin_type: 'INFORMATIONAL', description: 'Systems Engineering Management Plan (SEMP)', acrn_code: null, total_price: null },
        { slin_number: '000202', slin_type: 'INFORMATIONAL', description: 'Integrated Master Schedule (IMS)', acrn_code: null, total_price: null },
        { slin_number: '000203', slin_type: 'INFORMATIONAL', description: 'Monthly Status Report (MSR)', acrn_code: null, total_price: null },
      ],
      elin_references: ['A001', 'A002', 'A003'],
    },
    {
      clin_number: '0003',
      description: 'Contractor Logistics Support (CLS) — Base Year',
      category: 'SERVICE',
      pricing_type: 'CPFF',
      quantity: null,
      unit_of_measure: 'LO',
      unit_price: null,
      total_price: 7200000,
      is_option: false,
      option_period: null,
      nsf_flag: false,
      period_of_performance_start: '2026-05-01',
      period_of_performance_end: '2027-04-30',
      slins: [
        { slin_number: '0003AA', slin_type: 'PRICED', description: 'Field Service Representatives (FSR)', acrn_code: 'AA', total_price: 5800000 },
        { slin_number: '0003AB', slin_type: 'PRICED', description: 'Spare Parts & Consumables', acrn_code: 'AB', total_price: 1400000 },
      ],
      elin_references: [],
    },
    {
      clin_number: '0004',
      description: 'Travel — Base Year',
      category: 'SERVICE',
      pricing_type: 'CR',
      quantity: null,
      unit_of_measure: 'LO',
      unit_price: null,
      total_price: 350000,
      is_option: false,
      option_period: null,
      nsf_flag: false,
      period_of_performance_start: '2026-05-01',
      period_of_performance_end: '2027-04-30',
      slins: [],
      elin_references: [],
    },
    {
      clin_number: '1001',
      description: 'Systems Engineering & Technical Services — Option Period 1',
      category: 'SERVICE',
      pricing_type: 'CPFF',
      quantity: null,
      unit_of_measure: 'LO',
      unit_price: null,
      total_price: 9500000,
      is_option: true,
      option_period: 'Option Period 1',
      nsf_flag: false,
      period_of_performance_start: '2027-05-01',
      period_of_performance_end: '2028-04-30',
      slins: [
        { slin_number: '1001AA', slin_type: 'PRICED', description: 'Engineering Labor — Option Period 1', acrn_code: null, total_price: 8300000 },
        { slin_number: '1001AB', slin_type: 'PRICED', description: 'Other Direct Costs (ODC) — Option Period 1', acrn_code: null, total_price: 1200000 },
      ],
      elin_references: [],
    },
    {
      clin_number: '1002',
      description: 'Data & Deliverables — Option Period 1 (See Exhibit A)',
      category: 'DATA',
      pricing_type: 'FFP',
      quantity: null,
      unit_of_measure: 'LO',
      unit_price: null,
      total_price: 0,
      is_option: true,
      option_period: 'Option Period 1',
      nsf_flag: true,
      period_of_performance_start: '2027-05-01',
      period_of_performance_end: '2028-04-30',
      slins: [
        { slin_number: '100201', slin_type: 'INFORMATIONAL', description: 'Monthly Status Report (MSR) — Option Period 1', acrn_code: null, total_price: null },
      ],
      elin_references: ['A003'],
    },
  ],
  elins: [
    {
      elin_number: 'A001',
      title: 'Systems Engineering Management Plan (SEMP)',
      clin_reference: '0002',
      did_number: 'DI-MGMT-81024B',
      frequency: 'Once — 60 days after contract award',
    },
    {
      elin_number: 'A002',
      title: 'Integrated Master Schedule (IMS)',
      clin_reference: '0002',
      did_number: 'DI-MGMT-81650A',
      frequency: 'Once — 30 days after contract award; updated quarterly',
    },
    {
      elin_number: 'A003',
      title: 'Monthly Status Report (MSR)',
      clin_reference: '0002',
      did_number: 'DI-MGMT-80368B',
      frequency: 'Monthly — 10th calendar day of following month',
    },
  ],
  extraction_confidence: 0.91,
  extraction_notes: [
    'PIID extracted from cover page header — verify against the official award document.',
    '3 non-standard clauses identified (CDRL, Pricing Structure, IP Rights) — recommend legal review before execution.',
    'NAICS code 541330 inferred from Statement of Work description — confirm with contracting officer.',
    'Option period value estimated from ceiling allocation; verify against independently negotiated option amounts.',
  ],
  raw_text_preview: 'CONTRACT FOR INTEGRATED LOGISTICS SUPPORT & SUSTAINMENT SERVICES\n\nCONTRACT NUMBER: N00178-26-C-0314  |  DATE: April 15, 2026\n\nBETWEEN:\nNAVAL SEA SYSTEMS COMMAND (NAVSEA)\nWashington Navy Yard, DC 20376\n\nAND:\nLOCKHEED MARTIN CORPORATION\n6801 Rockledge Drive, Bethesda, MD 20817\nATTN: Patricia Nguyen, Contracts Manager\n\nThis Cost-Plus-Fixed-Fee (CPFF) contract is entered into pursuant to FAR Part 16.306...',
}

/**
 * Ingest a contract PDF through the AI pipeline.
 * Mock mode: simulates multi-phase OCR extraction with realistic delays.
 * Live mode: sends the PDF to /api/ai/ingest-contract, where the server has
 *            the model read the document itself.
 */
export async function ingestContractPdf(
  pdfBase64: string,
  _filename: string,
  onPhase?: (phase: IngestPhase) => void,
): Promise<IngestedContractDraft> {
  // Without this guard an AI-free build takes the branch below and returns
  // MOCK_INGESTED_CONTRACT, i.e. a fabricated contract presented as an extract.
  assertAiAllowed('Contract ingestion')
  if (provider.name !== 'claude') {
    // Simulate multi-phase OCR extraction
    onPhase?.('reading')
    await new Promise(r => setTimeout(r, 900))
    onPhase?.('ocr')
    await new Promise(r => setTimeout(r, 1400))
    onPhase?.('extracting')
    await new Promise(r => setTimeout(r, 1600))
    onPhase?.('synthesizing')
    await new Promise(r => setTimeout(r, 1000))
    onPhase?.('done')
    return MOCK_INGESTED_CONTRACT
  }

  // Live mode: forward the PDF to the server, which sends it to the model as a document
  onPhase?.('reading')
  const res = await fetch('/api/ai/ingest-contract', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await entraAuthHeaders()) },
    body: JSON.stringify({ pdf_base64: pdfBase64 }),
  })

  if (!res.ok) {
    const errText = await res.text()
    // A document this deployment's model cannot read comes back already worded
    // for the person who attached it; show that rather than a status line.
    const worded = customerWordedAiError(errText)
    if (worded) throw new Error(worded)
    throw new Error(`Contract ingestion failed (${res.status}): ${errText}`)
  }

  onPhase?.('done')
  return res.json() as Promise<IngestedContractDraft>
}

// ProjectTasks #103c Phase 2 — streaming variant of generateWithClaude.
//
// Behaves identically to generateWithClaude but reads the reply as a server-sent
// event stream from the same endpoint (the app's AI wire format, whichever
// provider is behind it). The endpoint writes the stream unbuffered
// when stream:true is set. The onProgress callback fires for every
// content_block_delta event so callers can render live token counters /
// partial text without waiting for the whole response.
//
/**
 * An HTTP failure from the AI proxy, carrying the status so callers can tell a
 * server-side duration cut-off apart from a rate limit apart from a dead proxy.
 *
 * This exists because all three used to surface as "Cannot connect to the AI
 * engine. Please check that the backend proxy is running" - which sent people to
 * inspect infrastructure that was fine, while the real cause (usually the
 * serverless function hitting its `maxDuration` on a long generation) went
 * unreported.
 */
/**
 * Summarize a user turn for the console, without printing a 4 MB payload.
 */
function describePrompt(userPrompt: string | readonly AiContentBlock[]): Record<string, unknown> {
  if (typeof userPrompt === 'string') return { promptLength: userPrompt.length }
  return {
    blocks: userPrompt.map(b => (b.type === 'text' ? `text:${b.text.length}` : `${b.type}:${b.source.media_type}`)),
    promptLength: userPrompt.reduce((n, b) => n + (b.type === 'text' ? b.text.length : b.source.data.length), 0),
  }
}

/**
 * Strip base64 payloads out of the in-memory debug log.
 *
 * `debugLog` is unbounded and lives for the browser session. A 3 MB PDF sent
 * three times is 12 MB of base64 retained forever behind a debug drawer nobody
 * opens, which is a memory leak wearing a diagnostic's clothing.
 */
function redactForDebug(body: Record<string, unknown>): Record<string, unknown> {
  const messages = body.messages
  if (!Array.isArray(messages)) return body
  return {
    ...body,
    messages: messages.map((m: { role?: string; content?: unknown }) => {
      if (!Array.isArray(m.content)) return m
      return {
        ...m,
        content: m.content.map((block: { type?: string; source?: { media_type?: string; data?: string } }) => (
          block?.source?.data
            ? { ...block, source: { ...block.source, data: `<${block.source.data.length} base64 chars omitted>` } }
            : block
        )),
      }
    }),
  }
}

export class AiProxyError extends Error {
  readonly status: number
  readonly body: string

  constructor(status: number, body: string) {
    // Some refusals are already worded by the server for the person looking at
    // the screen (a document this deployment's model cannot read). Show those
    // as they are; everything else gets the status-based diagnosis.
    super(customerWordedAiError(body) ?? `${describeProxyStatus(status)} (HTTP ${status})`)
    this.name = 'AiProxyError'
    this.status = status
    this.body = body
  }

  /** True when retrying the same request unchanged could plausibly succeed. */
  get retryable(): boolean {
    return this.status === 429 || this.status === 502 || this.status === 503 || this.status === 504
  }
}

function describeProxyStatus(status: number): string {
  switch (status) {
    case 504:
    case 502:
      return 'The AI request ran longer than the server allows and was cut off. Ask for a smaller slice of work (one phase, one WBS branch), or raise the serverless function timeout'
    case 429:
      return 'The AI service is rate limiting us. Wait a moment and try again'
    case 413:
      return 'The request was too large to send. Narrow the scope so less of the schedule has to travel with it'
    case 401:
    case 403:
      return 'The AI proxy rejected our credentials. The API key on the server needs attention'
    case 500:
      return 'The AI proxy failed while handling the request'
    default:
      return status >= 500
        ? 'The AI proxy returned a server error'
        : 'The AI proxy rejected the request'
  }
}

// Errors and truncation behave the same as the non-streaming variant — caller
// keeps the same try/catch ergonomics, and stop_reason='max_tokens' still
// throws when options.throwOnTruncation is set.
export interface StreamProgress {
  /** New text appended this tick (delta only). */
  deltaText: string
  /** Cumulative text received so far (delta-concat). */
  totalText: string
  /** Cumulative chunk count — useful for "X events" progress UIs. */
  chunkCount: number
}

/**
 * What one event of the AI text stream means to a caller that only wants the
 * text. The stream is the app's AI wire format, whichever provider is behind
 * the endpoint: `content_block_delta` carries text (or tool arguments, which a
 * text-only reader ignores), `message_delta` carries the stop reason, and
 * `error` reports a failure after the stream started. Every other event
 * (`message_start`, `content_block_start`, `content_block_stop`,
 * `message_stop`, `ping`) carries nothing a text reader needs, so a stream
 * with or without them reads the same.
 *
 * PURE and total: any value in, never throws.
 */
export function readTextStreamEvent(evt: unknown): { deltaText: string | null; stopReason: string | null; error: string | null } {
  const none = { deltaText: null, stopReason: null, error: null }
  if (!evt || typeof evt !== 'object') return none
  const e = evt as {
    type?: unknown
    delta?: { type?: unknown; text?: unknown; stop_reason?: unknown } | null
    error?: { message?: unknown } | null
  }
  if (e.type === 'content_block_delta') {
    return e.delta?.type === 'text_delta' && typeof e.delta.text === 'string'
      ? { ...none, deltaText: e.delta.text }
      : none
  }
  if (e.type === 'message_delta') {
    return typeof e.delta?.stop_reason === 'string' && e.delta.stop_reason
      ? { ...none, stopReason: e.delta.stop_reason }
      : none
  }
  if (e.type === 'error') {
    const message = typeof e.error?.message === 'string' ? e.error.message.trim() : ''
    return { ...none, error: message || 'The AI response was interrupted before it finished. Try again.' }
  }
  return none
}

export async function generateWithClaudeStream(
  systemPrompt: string,
  userPrompt: string | readonly AiContentBlock[],
  maxTokens = 1500,
  options?: {
    throwOnTruncation?: boolean
    model?: string
    onProgress?: (p: StreamProgress) => void
    signal?: AbortSignal
  },
): Promise<string | null> {
  assertAiAllowed('This AI request')
  const model = options?.model
    || (provider.name === 'claude'
      ? (provider as ClaudeAiProvider)['model']
      : (import.meta.env.VITE_AI_MODEL || 'claude-sonnet-4-6'))
  const proxyUrl = await resolveAiProxyUrl()

  const body = {
    model,
    max_tokens: maxTokens,
    system: systemPrompt,
    messages: [{ role: 'user', content: userPrompt }],
    stream: true,
  }

  console.log('[AI] generateWithClaudeStream: sending request', { model, proxyUrl, ...describePrompt(userPrompt) })
  addDebugEntry('request', redactForDebug(body))

  const controller = new AbortController()
  // Deliberately just ABOVE the serverless function's own 300s maxDuration
  // (web/api/ai/messages.js). Whichever side fires first writes the error the
  // user reads, and the server's 504 carries a real diagnosis where a client
  // abort only says "timed out". At 120s the client was cutting off requests
  // the server would have completed.
  const timeout = setTimeout(() => controller.abort(), 310_000)
  // Caller-supplied signal short-circuits the inner controller too.
  if (options?.signal) {
    options.signal.addEventListener('abort', () => controller.abort(), { once: true })
  }

  try {
    const res = await fetch(proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await entraAuthHeaders()) },
      body: JSON.stringify(body),
      signal: controller.signal,
    })

    if (!res.ok) {
      const errorText = await res.text()
      console.error('[AI] generateWithClaudeStream: API error', res.status, errorText)
      addDebugEntry('response', { status: res.status, error: errorText })
      throw new AiProxyError(res.status, errorText)
    }

    if (!res.body) {
      throw new Error('Streaming response had no body')
    }

    const decoder = new TextDecoder('utf-8')
    const reader = res.body.getReader()
    let buffer = ''
    let assembled = ''
    let chunkCount = 0
    let stopReason: string | null = null
    let streamError: string | null = null
    noteAiResponseMeta(readAiResponseMeta(res.headers))

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        // SSE events are separated by a blank line. Split on \n\n and keep
        // any trailing partial event in the buffer for the next iteration.
        let sep = buffer.indexOf('\n\n')
        while (sep !== -1) {
          const eventBlock = buffer.slice(0, sep)
          buffer = buffer.slice(sep + 2)

          // Each block is a series of `field: value` lines. We only care
          // about `data:` lines.
          const dataLines = eventBlock
            .split('\n')
            .filter(l => l.startsWith('data:'))
            .map(l => l.slice(5).trimStart())

          for (const dataLine of dataLines) {
            if (!dataLine || dataLine === '[DONE]') continue
            try {
              const step = readTextStreamEvent(JSON.parse(dataLine))
              if (step.deltaText !== null) {
                assembled += step.deltaText
                chunkCount += 1
                options?.onProgress?.({ deltaText: step.deltaText, totalText: assembled, chunkCount })
              }
              if (step.stopReason) stopReason = step.stopReason
              if (step.error) streamError = step.error
            } catch {
              // A keepalive, a ping, or a frame that is not JSON: a parse
              // failure on a single event is non-fatal.
            }
          }
          // The provider failed mid-stream. What arrived so far is a fragment,
          // and returning it would pass a cut-off answer off as a whole one.
          if (streamError) throw new Error(streamError)

          sep = buffer.indexOf('\n\n')
        }
      }
    } finally {
      try { reader.releaseLock() } catch { /* ignore */ }
      clearTimeout(timeout)
    }

    addDebugEntry('response', { streamed: true, chunkCount, length: assembled.length, stopReason })
    console.log('[AI] generateWithClaudeStream: complete', { chunkCount, length: assembled.length, stopReason })

    if (stopReason === 'max_tokens') {
      console.warn('[AI] generateWithClaudeStream: response truncated (max_tokens reached)')
      if (options?.throwOnTruncation) {
        throw new Error('AI response was truncated — the output exceeded the token limit. Try simplifying the request.')
      }
    }

    return assembled
  } catch (err) {
    clearTimeout(timeout)
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('AI request timed out. The WBS generation is complex — please try again or simplify the description.')
    }
    console.warn('[AI] generateWithClaudeStream: failed', err instanceof Error ? err.message : err)
    throw err instanceof Error ? err : new Error('Cannot connect to the AI engine. Please check that the backend proxy is running and try again.')
  }
}

export async function generateWithClaude(
  systemPrompt: string,
  /**
   * The user turn: plain text, or structured content blocks.
   *
   * The block form exists so a caller can put a PDF in front of the text and
   * have the model read the document itself - layout, tables and signature blocks
   * intact - which is where a contract keeps its dates, and where every local
   * text extraction loses them. Additive: the string form is unchanged, and the
   * proxy (`web/api/ai/messages.js`) forwards the body verbatim, so neither it
   * nor the other callers need touching.
   *
   * Limits depend on the deployment's provider. The tightest known are 32 MB
   * per request and 600 pages per PDF (100 against a 200k-context model),
   * base64 with no line breaks. A deployment whose model cannot read documents
   * answers 400 `unsupported_document` with a message worded for the user,
   * which `AiProxyError` shows as is.
   */
  userPrompt: string | readonly AiContentBlock[],
  maxTokens = 1500,
  options?: {
    throwOnTruncation?: boolean
    model?: string
    /**
     * The records the prompt was built from ("submission:<id>", "run:<id>",
     * "document:<id>"). The server refuses the call when any of them is
     * controlled (CUI), so their content never reaches the AI provider.
     */
    records?: readonly string[]
  },
): Promise<string | null> {
  assertAiAllowed('This AI request')
  // Always try the server AI endpoint first.
  // Falls back to mock responses if the backend is unreachable or not configured.
  // Per-call `options.model` overrides the configured provider model — used
  // by the As-Awarded import to pick Haiku (fast, cheap, plenty smart enough
  // for structured CSV → JSON extraction) instead of Sonnet.
  const model = options?.model
    || (provider.name === 'claude'
      ? (provider as ClaudeAiProvider)['model']
      : (import.meta.env.VITE_AI_MODEL || 'claude-sonnet-4-6'))
  const proxyUrl = await resolveAiProxyUrl()

  const body = {
    model,
    max_tokens: maxTokens,
    system: systemPrompt,
    messages: [{ role: 'user', content: userPrompt }],
  }

  console.log('[AI] generateWithClaude: sending request', { model, proxyUrl, ...describePrompt(userPrompt) })
  addDebugEntry('request', redactForDebug(body))

  const controller = new AbortController()
  // Deliberately just ABOVE the serverless function's own 300s maxDuration
  // (web/api/ai/messages.js). Whichever side fires first writes the error the
  // user reads, and the server's 504 carries a real diagnosis where a client
  // abort only says "timed out". At 120s the client was cutting off requests
  // the server would have completed.
  const timeout = setTimeout(() => controller.abort(), 310_000)

  try {
    const res = await fetch(proxyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await entraAuthHeaders()),
        ...(options?.records?.length ? { 'X-WFF-Records': options.records.join(',') } : {}),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    })

    clearTimeout(timeout)

    if (!res.ok) {
      const errorText = await res.text()
      console.error('[AI] generateWithClaude: API error', res.status, errorText)
      addDebugEntry('response', { status: res.status, error: errorText })
      throw new AiProxyError(res.status, errorText)
    }

    noteAiResponseMeta(readAiResponseMeta(res.headers))
    const data = await res.json()
    console.log('[AI] generateWithClaude: response received', { id: data.id, stopReason: data.stop_reason })
    addDebugEntry('response', data)

    if (data.stop_reason === 'max_tokens') {
      console.warn('[AI] generateWithClaude: response truncated (max_tokens reached)')
      if (options?.throwOnTruncation) {
        throw new Error('AI response was truncated — the output exceeded the token limit. Try simplifying the request.')
      }
    }

    const textBlocks = (data.content ?? []).filter((b: { type: string }) => b.type === 'text')
    return textBlocks.map((b: { text: string }) => b.text).join('\n')
  } catch (err) {
    clearTimeout(timeout)
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('AI request timed out. The WBS generation is complex — please try again or simplify the description.')
    }
    // An HTTP status IS a diagnosis - re-throw it. This used to be swallowed and
    // replaced with "check that the backend proxy is running", so a 504 from the
    // serverless function's duration limit, a 429, and a genuinely dead proxy all
    // read identically to the user and told them to go look at the wrong thing.
    if (err instanceof AiProxyError) throw err
    console.warn('[AI] generateWithClaude: backend unreachable', err instanceof Error ? err.message : err)
    throw new Error('Cannot connect to the AI engine. Please check that the backend proxy is running and try again.')
  }
}

/**
 * The model id this build REQUESTS, resolved the same way `generateWithClaude`
 * resolves it. On a deployment whose provider is not Anthropic the server reads
 * it only as a tier (fast, everyday, reasoning) and answers with its own
 * deployment, so this is never the record of what answered: use the `model` on
 * the response for that. Exported so the reasoning loop can pick the right
 * `thinking` shape for the id it is about to send.
 */
export function configuredAiModel(): string {
  return provider.name === 'claude'
    ? (provider as ClaudeAiProvider)['model']
    : (import.meta.env.VITE_AI_MODEL || 'claude-sonnet-4-6')
}

/**
 * The model the Master Scheduling Agent's reasoning loop runs on.
 *
 * Defaults to whatever the rest of the app uses. `VITE_AI_REASONING_MODEL`
 * exists so the scheduling agent can be moved to a more capable model WITHOUT
 * moving the twenty-odd other callers - a reasoning episode is several requests
 * where a one-shot is one, so its per-turn cost is the one that would change
 * materially, and that is a decision to make deliberately rather than inherit.
 */
export function reasoningAiModel(): string {
  return import.meta.env.VITE_AI_REASONING_MODEL || configuredAiModel()
}

/**
 * One turn of a multi-message, tool-using conversation.
 *
 * `generateWithClaude` above is the one-shot: it sends a single user message,
 * filters the reply to text, and returns a string. That is exactly what its
 * twenty-odd callers want and none of them changes here. What it cannot do is
 * hold a conversation with itself - send tools, notice `stop_reason:
 * "tool_use"`, hand back results and continue - because it discards the block
 * structure that carries the tool calls, the tool-use ids, and the
 * extended-thinking signatures.
 *
 * This returns the assistant turn WHOLE. The caller appends it to the message
 * list verbatim and appends its own `tool_result` blocks after it. Nothing is
 * reshaped on the way through, because the API validates parts of it (a
 * thinking block's signature, a tool_use id) that no intermediate type knows
 * about.
 *
 * On Anthropic the endpoint passes the body straight through, so `tools`,
 * `thinking` and `system`-as-blocks all arrive untouched. On Azure OpenAI
 * Service or OpenAI the server translates it: tools and system carry over,
 * while `thinking`, `cache_control` and `output_config` are left out (they
 * are optimizations, not content), so the reply simply has no thinking blocks
 * and no cache counts. Nothing below depends on either being present.
 */
export async function generateWithClaudeMessages(req: AiTurnRequest): Promise<AiTurnResponse> {
  assertAiAllowed('This AI request')
  const model = req.model || configuredAiModel()
  const proxyUrl = await resolveAiProxyUrl()

  // `undefined` means "work it out from the model"; `null` means "no thinking".
  // Leaving room for the answer: the budgeted form (older models only) must sit
  // under max_tokens or the request is rejected outright.
  const thinking = req.thinking === undefined
    ? thinkingConfigFor(model, { budgetTokens: Math.max(1024, Math.floor(req.maxTokens / 2)) })
    : req.thinking

  // The system prompt is sent as a block ONLY when it is being cached. Caching
  // is a prefix match over tools -> system -> messages, so one breakpoint at
  // the end of the system block covers the tool definitions in front of it,
  // which is the whole stable half of a reasoning episode's request.
  const system = req.cacheSystem
    ? [{ type: 'text', text: req.system, cache_control: { type: 'ephemeral' } }]
    : req.system

  // Sent only when the caller asked for one AND the model takes it. `effort`
  // lives inside `output_config`, never top-level.
  const effort = effortConfigFor(model, req.effort)

  const body: Record<string, unknown> = {
    model,
    max_tokens: req.maxTokens,
    system,
    messages: req.messages,
    ...(req.tools && req.tools.length > 0 ? { tools: req.tools } : {}),
    ...(thinking ? { thinking } : {}),
    ...(effort ? { output_config: { effort } } : {}),
  }

  console.log('[AI] generateWithClaudeMessages: sending request', {
    model,
    proxyUrl,
    messages: req.messages.length,
    tools: req.tools?.length ?? 0,
    thinking: thinking?.type ?? 'off',
  })
  addDebugEntry('request', redactForDebug(body))

  const controller = new AbortController()
  // Just above the serverless function's own 300s maxDuration, for the reason
  // the one-shot gives: the server's 504 carries a diagnosis and a client abort
  // only says "timed out".
  const timeout = setTimeout(() => controller.abort(), 310_000)
  if (req.signal) {
    if (req.signal.aborted) controller.abort()
    else req.signal.addEventListener('abort', () => controller.abort(), { once: true })
  }

  try {
    const res = await fetch(proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await entraAuthHeaders()) },
      body: JSON.stringify(body),
      signal: controller.signal,
    })

    clearTimeout(timeout)

    if (!res.ok) {
      const errorText = await res.text()
      console.error('[AI] generateWithClaudeMessages: API error', res.status, errorText)
      addDebugEntry('response', { status: res.status, error: errorText })
      throw new AiProxyError(res.status, errorText)
    }

    noteAiResponseMeta(readAiResponseMeta(res.headers))
    const data = await res.json()
    addDebugEntry('response', data)
    const content: AiBlock[] = Array.isArray(data.content) ? data.content as AiBlock[] : []
    console.log('[AI] generateWithClaudeMessages: response received', {
      id: data.id,
      stopReason: data.stop_reason,
      blocks: content.map(b => b.type),
    })

    return {
      content,
      stopReason: typeof data.stop_reason === 'string' ? data.stop_reason : null,
      // The record of what answered is the server's, never the id we asked for.
      model: reportedModelLabel(data.model),
      usage: data.usage ? readUsage(data.usage) : EMPTY_USAGE,
    }
  } catch (err) {
    clearTimeout(timeout)
    if (err instanceof DOMException && err.name === 'AbortError') {
      // A caller-supplied signal firing is a deliberate stop, not a failure.
      // The loop reads this and reports "you stopped it", where "it timed out"
      // would be a lie about something the user did on purpose.
      throw new Error(req.signal?.aborted ? 'The request was stopped.' : 'AI request timed out.')
    }
    if (err instanceof AiProxyError) throw err
    console.warn('[AI] generateWithClaudeMessages: backend unreachable', err instanceof Error ? err.message : err)
    throw new Error('Cannot connect to the AI engine. Please check that the backend proxy is running and try again.')
  }
}

// ---------------------------------------------------------------------------
// Agile Delivery AI tools (Hybrid Waterfall/Agile feature, Section 10).
//
// The agile tools (draft_backlog_from_scope, split_epic, plan_sprint, etc.)
// are registered here in the AI phase (P6). They are advertised to the AI
// panel only when the `agile.enabled` capability is on: `getPpmTools` (below)
// merges them in when the flag resolves true, and the panel resolves that flag
// once at init and passes it. So a disabled instance never surfaces them.
//
// Every write tool sets `requiresConfirmation: true` (the panel renders a
// Confirm / Reject gate and only then runs the handler) and every tool logs an
// `addAuditEntry` on invocation. The read/analysis tools run a pure engine in
// `agileAiPlanning.ts`; the LLM narrates what the engine returned. Writes go
// through `agileWriteService` (which also appends a work_item_activity row).
//
// Handlers resolve the target project from the input or, when omitted, the
// currently selected project in the store, so page deep-links can send clean
// natural-language prompts without embedding ids.
// ---------------------------------------------------------------------------

interface AgileToolContext {
  projectId: string
  projectCode?: string
  actor: { id?: string | null; name?: string | null }
}

/**
 * Resolve the project + actor an agile tool acts on. Falls back to the
 * selected project in the app store when the caller omits projectId, and to a
 * generic AI actor when there is no signed-in user in scope.
 */
async function resolveAgileProject(
  input: Record<string, unknown>,
): Promise<AgileToolContext | { error: string }> {
  let projectId = typeof input.projectId === 'string' ? input.projectId.trim() : ''
  let projectCode = typeof input.projectCode === 'string' ? input.projectCode.trim() : ''
  let actor: { id?: string | null; name?: string | null } = { id: 'ai', name: 'XPM Agent' }
  try {
    const { useAppStore } = await import('@/store/appStore')
    const state = useAppStore.getState()
    const proj = state.selectedProject
    if (!projectId && proj?.id) projectId = proj.id
    if (!projectCode && proj?.code) projectCode = proj.code
    const u = state.currentUser as { id?: string; name?: string } | null | undefined
    if (u?.id) actor = { id: u.id, name: u.name ?? u.id }
  } catch {
    /* store unavailable (non-UI context) - rely on explicit input + ai actor */
  }
  if (!projectId) {
    return { error: 'No project is selected. Open a project first, then ask again (or pass projectId).' }
  }
  return { projectId, projectCode: projectCode || undefined, actor }
}

/** Map plain criterion strings into the stored acceptance-criterion shape. */
function toAcceptanceCriteria(list?: string[]): { id: string; text: string; met: boolean }[] {
  return (list ?? []).map((text) => ({ id: generateId(), text: String(text), met: false }))
}

/** One-line audit helper for every agile tool invocation. */
function auditAgileTool(
  action: string,
  entityType: string,
  entityId: string,
  details: Record<string, unknown>,
  confirmed: boolean,
  actorId?: string | null,
): void {
  addAuditEntry({
    userId: actorId ?? 'ai',
    action,
    entityType,
    entityId,
    details,
    aiGenerated: true,
    confirmed,
  })
}

export const agileAiTools: AiTool[] = [
  {
    name: 'draft_backlog_from_scope',
    description:
      'Draft an Epic to Feature to Story backlog tree with Given/When/Then acceptance criteria from a scope description, statement of work, or WBS element, then create the items for the current project. Pass a structured `epics` tree to create it verbatim, or free-text `scope` to have a starter tree scaffolded. Writes the items on confirmation.',
    parameters: {
      projectId: { type: 'string', description: 'Target project id (defaults to the selected project)' },
      scope: { type: 'string', description: 'Free-text scope / statement of work to decompose' },
      wbsElementId: { type: 'string', description: 'Optional WBS element id to link every created item to (feeds the hybrid EVM rollup)' },
      epics: {
        type: 'array',
        description:
          'Optional pre-structured tree: [{ title, description?, acceptanceCriteria?: string[], features: [{ title, description?, stories: [{ title, description?, acceptanceCriteria?: string[], storyPoints? }] }] }]',
      },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const ctx = await resolveAgileProject(input)
      if ('error' in ctx) return ctx
      const { createWorkItem } = await import('./agileWriteService')
      const epics: agilePlan.DraftEpic[] =
        Array.isArray(input.epics) && input.epics.length > 0
          ? (input.epics as agilePlan.DraftEpic[])
          : agilePlan.scaffoldBacklogTree(String(input.scope ?? ''))
      const wbsElementId =
        typeof input.wbsElementId === 'string' && input.wbsElementId.trim() ? input.wbsElementId.trim() : null
      const created: { key: string; itemType: string; title: string }[] = []
      try {
        let afterEpic: string | null = null
        for (const epic of epics) {
          const e = await createWorkItem(
            {
              projectId: ctx.projectId, projectCode: ctx.projectCode, itemType: 'epic',
              title: epic.title, description: epic.description,
              acceptanceCriteria: toAcceptanceCriteria(epic.acceptanceCriteria),
              wbsElementId, afterRank: afterEpic, dataClass: 'demo',
            },
            ctx.actor,
          )
          afterEpic = e.backlogRank ?? afterEpic
          created.push({ key: e.key, itemType: 'epic', title: e.title })
          let afterFeature: string | null = null
          for (const feature of epic.features ?? []) {
            const f = await createWorkItem(
              {
                projectId: ctx.projectId, projectCode: ctx.projectCode, itemType: 'feature',
                title: feature.title, description: feature.description, parentId: e.id,
                wbsElementId, afterRank: afterFeature, dataClass: 'demo',
              },
              ctx.actor,
            )
            afterFeature = f.backlogRank ?? afterFeature
            created.push({ key: f.key, itemType: 'feature', title: f.title })
            let afterStory: string | null = null
            for (const story of feature.stories ?? []) {
              const s = await createWorkItem(
                {
                  projectId: ctx.projectId, projectCode: ctx.projectCode, itemType: 'story',
                  title: story.title, description: story.description, parentId: f.id,
                  storyPoints: story.storyPoints ?? null,
                  acceptanceCriteria: toAcceptanceCriteria(story.acceptanceCriteria),
                  wbsElementId, afterRank: afterStory, dataClass: 'demo',
                },
                ctx.actor,
              )
              afterStory = s.backlogRank ?? afterStory
              created.push({ key: s.key, itemType: 'story', title: s.title })
            }
          }
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : 'Failed to create backlog items' }
      }
      const epicN = created.filter((c) => c.itemType === 'epic').length
      const featureN = created.filter((c) => c.itemType === 'feature').length
      const storyN = created.filter((c) => c.itemType === 'story').length
      auditAgileTool('draft_backlog_from_scope', 'work_item', ctx.projectId, { created: created.length }, true, ctx.actor.id)
      return {
        ok: true, createdCount: created.length, created,
        message: `Created ${created.length} backlog items (${epicN} epic, ${featureN} feature, ${storyN} story).`,
      }
    },
  },
  {
    name: 'split_epic',
    description:
      'Decompose an epic or feature into child stories, each with suggested acceptance criteria and rough story points, then create them under the parent. Identify the parent by id, key, or title. Pass a `stories` array to create those verbatim, otherwise a starter set is scaffolded. Writes on confirmation.',
    parameters: {
      projectId: { type: 'string', description: 'Target project id (defaults to the selected project)' },
      epicId: { type: 'string', description: 'Parent epic/feature id, key, or exact title to match' },
      stories: {
        type: 'array',
        description: 'Optional child stories: [{ title, description?, acceptanceCriteria?: string[], storyPoints? }]',
      },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const ctx = await resolveAgileProject(input)
      if ('error' in ctx) return ctx
      const { getWorkItems } = await import('./dataService')
      const items = await getWorkItems(ctx.projectId, ctx.projectCode)
      const ref = (input.epicId as string) ?? (input.epicKey as string) ?? (input.epicTitle as string)
      const parent = agilePlan.findWorkItem(items, ref)
      if (!parent) return { error: 'Could not find the epic/feature to split. Pass its id, key, or exact title.' }
      if (parent.itemType !== 'epic' && parent.itemType !== 'feature') {
        return { error: `${parent.key} is a ${parent.itemType}; only an epic or feature can be split into stories.` }
      }
      const stories: agilePlan.DraftStory[] =
        Array.isArray(input.stories) && input.stories.length > 0
          ? (input.stories as agilePlan.DraftStory[])
          : agilePlan.scaffoldEpicStories(parent)
      const { createWorkItem } = await import('./agileWriteService')
      const created: { key: string; title: string; storyPoints?: number }[] = []
      try {
        let after: string | null = parent.backlogRank ?? null
        for (const story of stories) {
          const s = await createWorkItem(
            {
              projectId: ctx.projectId, projectCode: ctx.projectCode, itemType: 'story',
              title: story.title, description: story.description, parentId: parent.id,
              storyPoints: story.storyPoints ?? null,
              acceptanceCriteria: toAcceptanceCriteria(story.acceptanceCriteria),
              wbsElementId: parent.wbsElementId ?? null, afterRank: after, dataClass: 'demo',
            },
            ctx.actor,
          )
          after = s.backlogRank ?? after
          created.push({ key: s.key, title: s.title, storyPoints: s.storyPoints })
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : 'Failed to create stories' }
      }
      auditAgileTool('split_epic', 'work_item', parent.id, { parent: parent.key, created: created.length }, true, ctx.actor.id)
      return { ok: true, parent: parent.key, createdCount: created.length, created, message: `Split ${parent.key} into ${created.length} stories.` }
    },
  },
  {
    name: 'suggest_acceptance_criteria',
    description:
      'Produce Given/When/Then acceptance criteria for a story. When a target story is identified (by id, key, or title) the criteria are appended to it on confirmation; otherwise the suggested criteria are returned for review only.',
    parameters: {
      projectId: { type: 'string', description: 'Target project id (defaults to the selected project)' },
      storyId: { type: 'string', description: 'Story id, key, or exact title to attach the criteria to' },
      storyTitle: { type: 'string', description: 'A story title to base suggestions on when no item is targeted' },
      criteria: { type: 'array', description: 'Optional explicit criterion strings to apply verbatim' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const ctx = await resolveAgileProject(input)
      if ('error' in ctx) return ctx
      const { getWorkItems } = await import('./dataService')
      const items = await getWorkItems(ctx.projectId, ctx.projectCode)
      const ref = (input.storyId as string) ?? (input.storyKey as string) ?? (input.storyTitle as string)
      const story = agilePlan.findWorkItem(items, ref)
      const criteriaText: string[] =
        Array.isArray(input.criteria) && input.criteria.length > 0
          ? (input.criteria as unknown[]).map(String)
          : agilePlan.suggestAcceptanceCriteria(story?.title ?? String(input.storyTitle ?? 'the story'))
      if (!story) {
        auditAgileTool('suggest_acceptance_criteria', 'work_item', ctx.projectId, { applied: false }, false, ctx.actor.id)
        return { applied: false, criteria: criteriaText, note: 'No matching story found; returning suggested criteria only. Pass a storyId to apply them.' }
      }
      const { updateWorkItem } = await import('./agileWriteService')
      try {
        const merged = [...story.acceptanceCriteria, ...toAcceptanceCriteria(criteriaText)]
        await updateWorkItem(story.id, { acceptanceCriteria: merged }, { actor: ctx.actor, projectCode: ctx.projectCode })
      } catch (e) {
        return { error: e instanceof Error ? e.message : 'Failed to apply acceptance criteria' }
      }
      auditAgileTool('suggest_acceptance_criteria', 'work_item', story.id, { applied: true, count: criteriaText.length }, true, ctx.actor.id)
      return { ok: true, applied: true, story: story.key, criteria: criteriaText, message: `Added ${criteriaText.length} acceptance criteria to ${story.key}.` }
    },
  },
  {
    name: 'estimate_story_points',
    description:
      'Suggest a relative story-point size for a story (or for every unestimated backlog item), with rationale, by comparing it to recent estimated items of the same type. Read-only: it recommends a size, it does not change any estimate.',
    parameters: {
      projectId: { type: 'string', description: 'Target project id (defaults to the selected project)' },
      storyId: { type: 'string', description: 'Story id, key, or title to size (omit to size the whole unestimated backlog)' },
      storyTitle: { type: 'string', description: 'A story title to size when it is not yet a saved item' },
    },
    requiresConfirmation: false,
    handler: async (input) => {
      const ctx = await resolveAgileProject(input)
      if ('error' in ctx) return ctx
      const { getWorkItems } = await import('./dataService')
      const items = await getWorkItems(ctx.projectId, ctx.projectCode)
      const ref = (input.storyId as string) ?? (input.storyTitle as string)
      const story = agilePlan.findWorkItem(items, ref)
      auditAgileTool('estimate_story_points', 'work_item', ctx.projectId, { targeted: Boolean(story) }, false, ctx.actor.id)
      if (story) {
        const r = agilePlan.suggestPoints(story, items)
        return { story: story.key, currentPoints: story.storyPoints ?? null, ...r }
      }
      if (ref) {
        const r = agilePlan.suggestPoints({ title: String(ref), itemType: 'story' }, items)
        return { story: null, storyTitle: String(ref), ...r }
      }
      const unestimated = items.filter(
        (i) =>
          (i.itemType === 'story' || i.itemType === 'bug') &&
          i.statusCategory !== 'done' &&
          !(typeof i.storyPoints === 'number' && i.storyPoints > 0),
      )
      return {
        batch: true,
        unestimatedCount: unestimated.length,
        suggestions: unestimated.slice(0, 10).map((i) => ({ key: i.key, title: i.title, ...agilePlan.suggestPoints(i, items) })),
      }
    },
  },
  {
    name: 'plan_sprint',
    description:
      'Fill the active or next planned sprint to capacity by priority and dependency order, respecting the team recent velocity. Returns the proposed selection; assigns the items to the sprint on confirmation. Pass `itemIds` to assign a specific set, or `capacityPoints` to override the target capacity.',
    parameters: {
      projectId: { type: 'string', description: 'Target project id (defaults to the selected project)' },
      sprintId: { type: 'string', description: 'Sprint id or name to plan (defaults to the active, else the next planned, sprint)' },
      capacityPoints: { type: 'number', description: 'Optional point capacity to fill to (defaults to rolling velocity)' },
      itemIds: { type: 'array', description: 'Optional explicit work item ids to pull into the sprint' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const ctx = await resolveAgileProject(input)
      if ('error' in ctx) return ctx
      const { getWorkItems, getSprints } = await import('./dataService')
      const [items, sprints] = await Promise.all([
        getWorkItems(ctx.projectId, ctx.projectCode),
        getSprints(ctx.projectId),
      ])
      const sprint = agilePlan.pickTargetSprint(sprints, input.sprintId as string | undefined)
      if (!sprint) return { error: 'No sprint to plan. Create a sprint on the Sprints page first.' }
      const { rollingVelocity } = await import('@/engine/agileMetrics')
      const capacity =
        typeof input.capacityPoints === 'number' && input.capacityPoints > 0
          ? input.capacityPoints
          : rollingVelocity(sprints) ?? 20
      let plan: agilePlan.SprintPlan | null = null
      let selectedIds: string[]
      if (Array.isArray(input.itemIds) && input.itemIds.length > 0) {
        selectedIds = (input.itemIds as unknown[]).map(String)
      } else {
        plan = agilePlan.selectSprintPlan(items, capacity)
        selectedIds = plan.selected.map((s) => s.id)
      }
      if (selectedIds.length === 0) {
        return { error: 'No estimated, unassigned backlog items are available to plan into the sprint. Estimate some stories first.' }
      }
      const { setWorkItemSprint } = await import('./agileWriteService')
      try {
        for (const id of selectedIds) {
          await setWorkItemSprint(id, sprint.id, { actor: ctx.actor, projectCode: ctx.projectCode })
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : 'Failed to assign items to the sprint' }
      }
      auditAgileTool('plan_sprint', 'agile_sprint', sprint.id, { assigned: selectedIds.length, capacity }, true, ctx.actor.id)
      return {
        ok: true, sprint: sprint.name, capacityPoints: capacity, assignedCount: selectedIds.length,
        totalPoints: plan?.totalPoints, selected: plan?.selected, skippedUnestimated: plan?.skippedUnestimated,
        message: `Pulled ${selectedIds.length} items into ${sprint.name}${plan ? ` (${plan.totalPoints} of about ${Math.round(capacity)} points)` : ''}.`,
      }
    },
  },
  {
    name: 'refine_backlog',
    description:
      'Analyse the current project backlog and flag stale, oversized, unestimated, duplicate, and not-ready (Definition of Ready) items so the team knows what to groom. Read-only.',
    parameters: {
      projectId: { type: 'string', description: 'Target project id (defaults to the selected project)' },
    },
    requiresConfirmation: false,
    handler: async (input) => {
      const ctx = await resolveAgileProject(input)
      if ('error' in ctx) return ctx
      const { getWorkItems } = await import('./dataService')
      const items = await getWorkItems(ctx.projectId, ctx.projectCode)
      const r = agilePlan.analyzeBacklogRefinement(items)
      auditAgileTool('refine_backlog', 'work_item', ctx.projectId, { total: r.total, healthy: r.healthy }, false, ctx.actor.id)
      return {
        total: r.total, healthy: r.healthy,
        counts: {
          oversized: r.oversized.length, unestimated: r.unestimated.length,
          unready: r.unready.length, stale: r.stale.length, duplicates: r.duplicates.length,
        },
        oversized: r.oversized.slice(0, 10), unestimated: r.unestimated.slice(0, 10),
        unready: r.unready.slice(0, 10), stale: r.stale.slice(0, 10), duplicates: r.duplicates.slice(0, 10),
      }
    },
  },
  {
    name: 'detect_blockers',
    description:
      'Scan the board for blocked and at-risk work items and cross-item dependency risk (any item that is blocked_by or depends_on an item that is not yet Done). Read-only.',
    parameters: {
      projectId: { type: 'string', description: 'Target project id (defaults to the selected project)' },
    },
    requiresConfirmation: false,
    handler: async (input) => {
      const ctx = await resolveAgileProject(input)
      if ('error' in ctx) return ctx
      const { getWorkItems, getWorkItemLinks } = await import('./dataService')
      const [items, links] = await Promise.all([
        getWorkItems(ctx.projectId, ctx.projectCode),
        getWorkItemLinks(ctx.projectId),
      ])
      const r = agilePlan.scanBlockers(items, links)
      auditAgileTool('detect_blockers', 'work_item', ctx.projectId, { blocked: r.blocked.length, dependencyRisk: r.dependencyRisk.length }, false, ctx.actor.id)
      return {
        total: r.total, blockedCount: r.blocked.length, dependencyRiskCount: r.dependencyRisk.length,
        blocked: r.blocked.slice(0, 15), dependencyRisk: r.dependencyRisk.slice(0, 15),
      }
    },
  },
  {
    name: 'summarize_sprint',
    description:
      'Draft sprint review notes and a retrospective starter for a sprint from its items and metrics (committed vs completed points, carryover, and rolling velocity). Writes the notes onto the sprint on confirmation. Identify the sprint by id or name, or the active / most-recent sprint is used.',
    parameters: {
      projectId: { type: 'string', description: 'Target project id (defaults to the selected project)' },
      sprintId: { type: 'string', description: 'Sprint id or name to summarize (defaults to the active, else most-recent, sprint)' },
      reviewNotes: { type: 'string', description: 'Optional review notes to save verbatim instead of the drafted text' },
      retroNotes: { type: 'string', description: 'Optional retrospective notes to save verbatim instead of the drafted starter' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const ctx = await resolveAgileProject(input)
      if ('error' in ctx) return ctx
      const { getWorkItems, getSprints } = await import('./dataService')
      const [items, sprints] = await Promise.all([
        getWorkItems(ctx.projectId, ctx.projectCode),
        getSprints(ctx.projectId),
      ])
      const sprint = agilePlan.pickTargetSprint(sprints, input.sprintId as string | undefined)
      if (!sprint) return { error: 'No sprint found to summarize.' }
      const { rollingVelocity } = await import('@/engine/agileMetrics')
      const sprintItems = items.filter((i) => i.sprintId === sprint.id)
      const summary = agilePlan.draftSprintSummary(sprint, sprintItems, rollingVelocity(sprints))
      const reviewNotes = typeof input.reviewNotes === 'string' && input.reviewNotes.trim() ? String(input.reviewNotes) : summary.reviewNotes
      const retroNotes = typeof input.retroNotes === 'string' && input.retroNotes.trim() ? String(input.retroNotes) : summary.retroNotes
      const { updateSprint } = await import('./agileWriteService')
      try {
        await updateSprint(sprint.id, { reviewNotes, retroNotes })
      } catch (e) {
        return { error: e instanceof Error ? e.message : 'Failed to save sprint notes' }
      }
      auditAgileTool('summarize_sprint', 'agile_sprint', sprint.id, { completionPct: summary.completionPct }, true, ctx.actor.id)
      return {
        ok: true, sprint: sprint.name,
        committedPoints: summary.committedPoints, completedPoints: summary.completedPoints, completionPct: summary.completionPct,
        reviewNotes, retroNotes,
        message: `Saved review and retrospective notes to ${sprint.name}.`,
      }
    },
  },
  {
    name: 'agile_board_query',
    description:
      'Answer a natural-language question over the backlog / board (for example "show blocked stories in Epic Onboarding" or "high priority bugs in progress") and return the parsed filter plus the matching items. Read-only.',
    parameters: {
      projectId: { type: 'string', description: 'Target project id (defaults to the selected project)' },
      query: { type: 'string', description: 'The natural-language query over the backlog / board' },
    },
    requiresConfirmation: false,
    handler: async (input) => {
      const ctx = await resolveAgileProject(input)
      if ('error' in ctx) return ctx
      const { getWorkItems } = await import('./dataService')
      const items = await getWorkItems(ctx.projectId, ctx.projectCode)
      const filter = agilePlan.buildBoardQueryFilter(String(input.query ?? ''))
      const matches = agilePlan.applyBoardQuery(items, filter)
      auditAgileTool('agile_board_query', 'work_item', ctx.projectId, { query: String(input.query ?? ''), matches: matches.length }, false, ctx.actor.id)
      return {
        filter, count: matches.length,
        matches: matches.slice(0, 30).map((i) => ({
          key: i.key, type: i.itemType, title: i.title, status: i.status, statusCategory: i.statusCategory,
          priority: i.priority, storyPoints: i.storyPoints ?? null, blocked: i.blocked, assignee: i.assigneeName ?? null,
        })),
      }
    },
  },
]

// ---------------------------------------------------------------------------
// Section 11 - CRM Studio tools (P1: accounts, contacts, interactions, tasks)
// ---------------------------------------------------------------------------
// Every one of these WRITES, so every one sets `requiresConfirmation: true`:
// the panel renders a Confirm / Reject gate and only then runs the handler.
// Handlers resolve the target account from an id or a name (fuzzy, via the same
// dedupe engine the forms use) so a prompt can say "log a call with Moog"
// without the model inventing an id. Writes go through crmService, and every
// invocation appends an audit entry.
//
// crmService is imported dynamically inside each handler, the way the agile
// tools import agileWriteService, so loading aiService never pulls in the CRM
// module or constructs a Supabase client.
// ---------------------------------------------------------------------------

/** One-line audit helper for every CRM tool invocation. */
function auditCrmTool(
  action: string,
  entityType: string,
  entityId: string,
  details: Record<string, unknown>,
): void {
  addAuditEntry({
    userId: 'ai',
    action,
    entityType,
    entityId,
    details,
    aiGenerated: true,
    confirmed: true,
  })
}

/**
 * Resolve an account from an explicit id or a name. Falls back to the dedupe
 * engine's name matcher so "Moog" finds "Moog Inc" without an exact string.
 * Returns an error object the tool can hand straight back to the model.
 */
async function resolveCrmAccount(
  input: Record<string, unknown>,
): Promise<{ id: string; name: string } | { error: string }> {
  const { listAccounts } = await import('@/modules/crm/services/crmService')
  const accounts = await listAccounts()
  if (accounts.length === 0) {
    return { error: 'There are no CRM accounts yet. Create one first, or import a customer from SAP.' }
  }

  const id = typeof input.accountId === 'string' ? input.accountId.trim() : ''
  if (id) {
    const hit = accounts.find((a) => a.id === id)
    if (hit) return { id: hit.id, name: hit.name }
    return { error: `No CRM account with id ${id}.` }
  }

  const name = typeof input.accountName === 'string' ? input.accountName.trim() : ''
  if (!name) return { error: 'Name the account to act on (accountName), or pass its accountId.' }

  const { findDuplicates } = await import('@/lib/crm/dedupe')
  const matches = findDuplicates(
    { name },
    accounts.map((a) => ({ id: a.id, name: a.name, email: a.email, website: a.website, sapKunnr: a.sapKunnr })),
    0.5,
  )
  if (matches.length === 0) return { error: `No CRM account matches "${name}".` }
  const best = matches[0]!
  return { id: best.candidate.id, name: best.candidate.name }
}

export const crmAiTools: AiTool[] = [
  {
    name: 'create_account',
    description:
      'Create a CRM account (a customer, prospect, teammate, prime, competitor, or an individual B2C buyer). Warns nothing and blocks nothing on duplicates; check first with the account list if that matters. Writes on confirmation.',
    parameters: {
      name: { type: 'string', description: 'Account name. For an individual, the person\'s full name.' },
      kind: { type: 'string', description: 'customer | prospect | teammate | prime | competitor | individual (default prospect)' },
      segment: { type: 'string', description: 'government | commercial | mixed' },
      sapKunnr: { type: 'string', description: 'SAP customer number (KUNNR) when the account already exists in SAP' },
      samUei: { type: 'string', description: 'SAM Unique Entity ID' },
      cage: { type: 'string', description: 'CAGE code' },
      city: { type: 'string', description: 'City' },
      country: { type: 'string', description: 'Country key' },
      ownerUserId: { type: 'string', description: 'User id of the account owner' },
      notes: { type: 'string', description: 'Free-text notes' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const name = typeof input.name === 'string' ? input.name.trim() : ''
      if (!name) return { error: 'An account needs a name.' }
      const { createAccount } = await import('@/modules/crm/services/crmService')
      try {
        const account = await createAccount({
          name,
          kind: (input.kind as never) ?? 'prospect',
          segment: (input.segment as never) ?? null,
          sapKunnr: (input.sapKunnr as string) ?? null,
          samUei: (input.samUei as string) ?? null,
          cage: (input.cage as string) ?? null,
          city: (input.city as string) ?? null,
          country: (input.country as string) ?? null,
          ownerUserId: (input.ownerUserId as string) ?? null,
          notes: (input.notes as string) ?? null,
          createdBy: 'ai',
        })
        auditCrmTool('create_account', 'crm_account', account.id, { name: account.name, kind: account.kind })
        return { ok: true, accountId: account.id, name: account.name, message: `Created the account ${account.name}.` }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'update_account',
    description:
      'Update fields on an existing CRM account. Identify it by accountId, or by accountName which is matched fuzzily against the account list. Only the fields passed are changed. Writes on confirmation.',
    parameters: {
      accountId: { type: 'string', description: 'Target account id' },
      accountName: { type: 'string', description: 'Target account name (used when accountId is omitted)' },
      name: { type: 'string', description: 'New account name' },
      kind: { type: 'string', description: 'customer | prospect | teammate | prime | competitor | individual' },
      segment: { type: 'string', description: 'government | commercial | mixed' },
      tier: { type: 'string', description: 'strategic | key | standard | long_tail' },
      health: { type: 'string', description: 'green | yellow | red' },
      ownerUserId: { type: 'string', description: 'User id of the account owner' },
      sapKunnr: { type: 'string', description: 'SAP customer number (KUNNR)' },
      notes: { type: 'string', description: 'Free-text notes' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const ctx = await resolveCrmAccount(input)
      if ('error' in ctx) return ctx
      const patch: Record<string, unknown> = {}
      for (const key of ['name', 'kind', 'segment', 'tier', 'health', 'ownerUserId', 'sapKunnr', 'notes']) {
        if (typeof input[key] === 'string' && (input[key] as string).length > 0) patch[key] = input[key]
      }
      if (Object.keys(patch).length === 0) return { error: 'Name at least one field to change.' }
      const { updateAccount } = await import('@/modules/crm/services/crmService')
      try {
        const account = await updateAccount(ctx.id, patch)
        auditCrmTool('update_account', 'crm_account', account.id, patch)
        return { ok: true, accountId: account.id, name: account.name, changed: Object.keys(patch), message: `Updated ${account.name}.` }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'create_contact',
    description:
      'Add a contact to a CRM account, with the buying roles that person holds (decision_maker, economic_buyer, technical_buyer, champion, influencer, blocker, gatekeeper, contracting_officer, program_manager, quality, finance, end_user). Writes on confirmation.',
    parameters: {
      accountId: { type: 'string', description: 'Target account id' },
      accountName: { type: 'string', description: 'Target account name (used when accountId is omitted)' },
      firstName: { type: 'string', description: 'First name' },
      lastName: { type: 'string', description: 'Last name' },
      title: { type: 'string', description: 'Job title' },
      email: { type: 'string', description: 'Email address' },
      phone: { type: 'string', description: 'Phone number' },
      roleTags: { type: 'array', description: 'Buying roles this person holds' },
      isPrimary: { type: 'boolean', description: 'Mark as the primary contact for the account' },
      notes: { type: 'string', description: 'Free-text notes' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const ctx = await resolveCrmAccount(input)
      if ('error' in ctx) return ctx
      const first = typeof input.firstName === 'string' ? input.firstName.trim() : ''
      const last = typeof input.lastName === 'string' ? input.lastName.trim() : ''
      const email = typeof input.email === 'string' ? input.email.trim() : ''
      if (!first && !last && !email) return { error: 'A contact needs a name or an email address.' }
      const { createContact } = await import('@/modules/crm/services/crmService')
      try {
        const contact = await createContact({
          accountId: ctx.id,
          firstName: first || null,
          lastName: last || null,
          title: (input.title as string) ?? null,
          email: email || null,
          phone: (input.phone as string) ?? null,
          roleTags: Array.isArray(input.roleTags) ? (input.roleTags as never[]) : [],
          isPrimary: input.isPrimary === true,
          notes: (input.notes as string) ?? null,
          createdBy: 'ai',
        })
        auditCrmTool('create_contact', 'crm_contact', contact.id, { accountId: ctx.id, name: contact.fullName })
        return {
          ok: true, contactId: contact.id, name: contact.fullName, account: ctx.name,
          message: `Added ${contact.fullName || 'the contact'} to ${ctx.name}.`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'log_interaction',
    description:
      'Log an interaction against a CRM account: a call, email, meeting, site_visit, feedback, escalation, marketplace_inquiry, or note. Feedback and escalations carry a status, and escalations also carry a severity. Writes on confirmation.',
    parameters: {
      accountId: { type: 'string', description: 'Target account id' },
      accountName: { type: 'string', description: 'Target account name (used when accountId is omitted)' },
      type: { type: 'string', description: 'call | email | meeting | site_visit | feedback | escalation | marketplace_inquiry | note' },
      subject: { type: 'string', description: 'One-line subject' },
      body: { type: 'string', description: 'What was said or agreed' },
      direction: { type: 'string', description: 'inbound | outbound | internal' },
      sentiment: { type: 'string', description: 'positive | neutral | negative' },
      status: { type: 'string', description: 'open | actioned | closed (feedback and escalations only)' },
      severity: { type: 'string', description: 'low | medium | high | critical (escalations only)' },
      contactName: { type: 'string', description: 'Name of the contact involved, matched against the account\'s contacts' },
      occurredAt: { type: 'string', description: 'ISO timestamp of when it happened (defaults to now)' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const ctx = await resolveCrmAccount(input)
      if ('error' in ctx) return ctx
      const type = typeof input.type === 'string' ? input.type : 'note'
      const statusBearing = type === 'feedback' || type === 'escalation'
      const { listContacts, createInteraction } = await import('@/modules/crm/services/crmService')

      let contactId: string | null = null
      const contactName = typeof input.contactName === 'string' ? input.contactName.trim().toLowerCase() : ''
      if (contactName) {
        const contacts = await listContacts(ctx.id)
        contactId = contacts.find((c) => (c.fullName ?? '').toLowerCase().includes(contactName))?.id ?? null
      }

      try {
        const interaction = await createInteraction({
          accountId: ctx.id,
          contactId,
          type: type as never,
          subject: (input.subject as string) ?? null,
          body: (input.body as string) ?? null,
          direction: (input.direction as never) ?? null,
          sentiment: (input.sentiment as never) ?? null,
          status: statusBearing ? ((input.status as never) ?? 'open') : null,
          severity: type === 'escalation' ? ((input.severity as never) ?? 'medium') : null,
          occurredAt: typeof input.occurredAt === 'string' ? input.occurredAt : undefined,
          createdBy: 'ai',
        })
        auditCrmTool('log_interaction', 'crm_interaction', interaction.id, { accountId: ctx.id, type })
        return {
          ok: true, interactionId: interaction.id, account: ctx.name, type,
          message: `Logged a ${type} against ${ctx.name}.`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'create_task',
    description:
      'Create a CRM follow-up task on an account, with an optional due date. A task with no due date is valid and never reads as overdue. Writes on confirmation.',
    parameters: {
      accountId: { type: 'string', description: 'Target account id' },
      accountName: { type: 'string', description: 'Target account name (used when accountId is omitted)' },
      title: { type: 'string', description: 'What needs doing' },
      kind: { type: 'string', description: 'task | follow_up | reminder | call_back | next_best_action' },
      priority: { type: 'string', description: 'low | normal | high' },
      dueAt: { type: 'string', description: 'ISO date or timestamp the task is due' },
      ownerUserId: { type: 'string', description: 'User id the task is assigned to' },
      notes: { type: 'string', description: 'Free-text notes' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const ctx = await resolveCrmAccount(input)
      if ('error' in ctx) return ctx
      const title = typeof input.title === 'string' ? input.title.trim() : ''
      if (!title) return { error: 'A task needs a title.' }
      let dueAt: string | null = null
      if (typeof input.dueAt === 'string' && input.dueAt.trim()) {
        const d = new Date(input.dueAt)
        if (!Number.isNaN(d.getTime())) dueAt = d.toISOString()
      }
      const { createActivity } = await import('@/modules/crm/services/crmService')
      try {
        const activity = await createActivity({
          accountId: ctx.id,
          title,
          kind: (input.kind as never) ?? 'task',
          priority: (input.priority as never) ?? 'normal',
          dueAt,
          ownerUserId: (input.ownerUserId as string) ?? null,
          notes: (input.notes as string) ?? null,
          createdBy: 'ai',
        })
        auditCrmTool('create_task', 'crm_activity', activity.id, { accountId: ctx.id, title, dueAt })
        return {
          ok: true, taskId: activity.id, account: ctx.name, title, dueAt,
          message: `Added the task "${title}" on ${ctx.name}${dueAt ? `, due ${new Date(dueAt).toLocaleDateString()}` : ''}.`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },

  // -------------------------------------------------------------------------
  // Leads, opportunities, scoring, gates (P3)
  // -------------------------------------------------------------------------

  {
    name: 'create_lead',
    description:
      'Create a CRM lead: unqualified demand from an enquiry, a sources-sought notice, a marketplace request, a referral, or a show. Does not check for duplicates; the Leads page does that on the form. Writes on confirmation.',
    parameters: {
      company: { type: 'string', description: 'The organization the lead is at. Duplicate checking keys on this.' },
      firstName: { type: 'string', description: 'Contact first name' },
      lastName: { type: 'string', description: 'Contact last name' },
      title: { type: 'string', description: 'Their job title' },
      email: { type: 'string', description: 'Contact email' },
      phone: { type: 'string', description: 'Contact phone' },
      website: { type: 'string', description: 'Company website' },
      source: { type: 'string', description: 'samgov | marketplace | referral | web | trade_show | inbound | outbound | partner | existing_customer | other (default inbound)' },
      sourceDetail: { type: 'string', description: 'The notice id, the show name, or who referred them' },
      channel: { type: 'string', description: 'gov_prime | gov_sub | commercial_oem | aftermarket | ecommerce | fms_dcs' },
      estimatedValue: { type: 'number', description: 'Rough value, if known' },
      ownerUserId: { type: 'string', description: 'User id of the owner' },
      notes: { type: 'string', description: 'Free-text notes' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const company = typeof input.company === 'string' ? input.company.trim() : ''
      const first = typeof input.firstName === 'string' ? input.firstName.trim() : ''
      const last = typeof input.lastName === 'string' ? input.lastName.trim() : ''
      if (!company && !first && !last) {
        return { error: 'A lead needs a company or a person\'s name.' }
      }
      const { createLead } = await import('@/modules/crm/services/crmService')
      try {
        const lead = await createLead({
          company: company || null,
          firstName: first || null,
          lastName: last || null,
          title: (input.title as string) ?? null,
          email: (input.email as string) ?? null,
          phone: (input.phone as string) ?? null,
          website: (input.website as string) ?? null,
          source: (input.source as never) ?? 'inbound',
          sourceDetail: (input.sourceDetail as string) ?? null,
          channel: (input.channel as string) ?? null,
          estimatedValue: typeof input.estimatedValue === 'number' ? input.estimatedValue : null,
          ownerUserId: (input.ownerUserId as string) ?? null,
          notes: (input.notes as string) ?? null,
          createdBy: 'ai',
        })
        auditCrmTool('create_lead', 'crm_lead', lead.id, { company, source: lead.source })
        return {
          ok: true, leadId: lead.id, company: lead.company, name: lead.fullName,
          message: `Created the lead ${lead.company || lead.fullName}.`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },

  {
    name: 'qualify_lead',
    description:
      'Record answers on a lead\'s guided qualification worksheet. Each answer is yes, no, unknown, or na, with a note holding the evidence. Optionally marks the lead qualified, which is a judgement rather than a score threshold. Writes on confirmation.',
    parameters: {
      leadId: { type: 'string', description: 'Lead id' },
      leadCompany: { type: 'string', description: 'Lead company name, used when leadId is omitted' },
      answers: {
        type: 'array',
        description: 'Answers as [{ key, state, note }]. key is a qualification item key such as budget, authority, need, timeline, set_aside. state is yes | no | unknown | na.',
      },
      summary: { type: 'string', description: 'What was learned overall' },
      markQualified: { type: 'boolean', description: 'Set the lead status to qualified (default false)' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const { listLeads, saveLeadQualification } = await import('@/modules/crm/services/crmService')
      const { QUALIFICATION_ITEMS } = await import('@/modules/crm/lib/crmOptions')

      const leads = await listLeads()
      const id = typeof input.leadId === 'string' ? input.leadId.trim() : ''
      const company = typeof input.leadCompany === 'string' ? input.leadCompany.trim().toLowerCase() : ''
      const lead = id
        ? leads.find((l) => l.id === id)
        : leads.find((l) => (l.company ?? '').toLowerCase() === company)
          ?? leads.find((l) => (l.company ?? '').toLowerCase().includes(company) && company.length > 2)
      if (!lead) return { error: id ? `No lead with id ${id}.` : `No lead matches "${input.leadCompany}".` }

      const raw = Array.isArray(input.answers) ? input.answers : []
      const now = new Date().toISOString()
      const items = raw
        .map((a) => (a && typeof a === 'object' ? a as Record<string, unknown> : null))
        .filter((a): a is Record<string, unknown> => !!a)
        .map((a) => {
          const key = String(a.key ?? '')
          const def = QUALIFICATION_ITEMS.find((q) => q.key === key)
          const state = String(a.state ?? 'unanswered')
          const valid = ['yes', 'no', 'unknown', 'na', 'unanswered'].includes(state)
          return {
            key,
            label: def?.label ?? key,
            state: (valid ? state : 'unanswered') as 'unanswered' | 'yes' | 'no' | 'unknown' | 'na',
            note: a.note == null ? null : String(a.note),
            answeredAt: valid && state !== 'unanswered' ? now : null,
            answeredBy: 'ai',
          }
        })
        .filter((a) => a.key)

      if (items.length === 0 && typeof input.summary !== 'string') {
        return { error: 'Give at least one answer, or a summary.' }
      }

      // Merge onto whatever was already recorded rather than replacing it.
      const merged = [...lead.qualification.items]
      for (const item of items) {
        const at = merged.findIndex((m) => m.key === item.key)
        if (at >= 0) merged[at] = item
        else merged.push(item)
      }
      const applicable = merged.filter((m) => m.state !== 'na' && m.state !== 'unanswered')
      const score = applicable.length === 0
        ? null
        : Math.round((applicable.filter((m) => m.state === 'yes').length / applicable.length) * 100)

      try {
        const saved = await saveLeadQualification(
          lead.id,
          {
            items: merged,
            score,
            summary: typeof input.summary === 'string' ? input.summary : lead.qualification.summary,
            updatedAt: now,
          },
          { markQualified: input.markQualified === true, by: 'ai' },
        )
        auditCrmTool('qualify_lead', 'crm_lead', lead.id, { answered: items.length, score, markQualified: input.markQualified === true })
        return {
          ok: true, leadId: saved.id, status: saved.status, score,
          message: `Recorded ${items.length} answer${items.length === 1 ? '' : 's'} on ${saved.company || saved.fullName}${score !== null ? `, scoring ${score}%` : ''}${saved.status === 'qualified' ? ', and marked it qualified' : ''}.`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },

  {
    name: 'convert_lead',
    description:
      'Convert a lead into an account, a contact, and an opportunity. Links an existing account when one is named or already matched, otherwise creates one. Carries the qualification worksheet onto the opportunity. Writes on confirmation.',
    parameters: {
      leadId: { type: 'string', description: 'Lead id' },
      leadCompany: { type: 'string', description: 'Lead company name, used when leadId is omitted' },
      pipelineName: { type: 'string', description: 'Pipeline to put the opportunity in. Defaults to the pipeline matching the lead channel, else the default pipeline.' },
      opportunityName: { type: 'string', description: 'Name for the new opportunity' },
      accountId: { type: 'string', description: 'Link to this existing account instead of creating one' },
      value: { type: 'number', description: 'Opportunity value' },
      closeDate: { type: 'string', description: 'Expected close date, ISO' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const {
        convertLead, listLeads, listPipelines, listPipelineStages,
      } = await import('@/modules/crm/services/crmService')
      const { firstOpenStage } = await import('@/lib/crm/pipelineEngine')

      const leads = await listLeads()
      const id = typeof input.leadId === 'string' ? input.leadId.trim() : ''
      const company = typeof input.leadCompany === 'string' ? input.leadCompany.trim().toLowerCase() : ''
      const lead = id
        ? leads.find((l) => l.id === id)
        : leads.find((l) => (l.company ?? '').toLowerCase() === company)
      if (!lead) return { error: id ? `No lead with id ${id}.` : `No lead matches "${input.leadCompany}".` }
      if (lead.status === 'converted') return { error: `${lead.company || lead.fullName} has already been converted.` }

      const pipelines = (await listPipelines()).filter((p) => p.active)
      if (pipelines.length === 0) {
        return { error: 'There are no pipelines yet. Build one under Pipelines first: an opportunity has to live in one.' }
      }
      const wanted = typeof input.pipelineName === 'string' ? input.pipelineName.trim().toLowerCase() : ''
      const pipeline = (wanted && pipelines.find((p) => p.name.toLowerCase().includes(wanted)))
        ?? pipelines.find((p) => p.channel === lead.channel)
        ?? pipelines.find((p) => p.isDefault)
        ?? pipelines[0]
      if (!pipeline) return { error: 'Could not pick a pipeline.' }

      const stages = await listPipelineStages(pipeline.id)
      try {
        const result = await convertLead(lead.id, {
          accountId: (input.accountId as string) ?? null,
          opportunityName: typeof input.opportunityName === 'string' ? input.opportunityName : undefined,
          pipelineId: pipeline.id,
          stageId: firstOpenStage(stages)?.id ?? null,
          channel: lead.channel,
          value: typeof input.value === 'number' ? input.value : null,
          closeDate: (input.closeDate as string) ?? null,
          createdBy: 'ai',
        })
        auditCrmTool('convert_lead', 'crm_lead', lead.id, {
          accountId: result.accountId, opportunityId: result.opportunityId, pipeline: pipeline.name,
        })
        return {
          ok: true,
          leadId: lead.id,
          accountId: result.accountId,
          accountCreated: result.accountCreated,
          contactId: result.contactId,
          opportunityId: result.opportunityId,
          message: `Converted ${lead.company || lead.fullName}: ${result.accountCreated ? 'created the account' : 'linked the existing account'}, ${result.contactId ? 'created the contact, ' : ''}and opened the opportunity on ${pipeline.name}.`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },

  {
    name: 'create_opportunity',
    description:
      'Create a CRM opportunity on an account, in a named pipeline. Starts at the pipeline\'s first open stage unless a stage is named. Writes on confirmation.',
    parameters: {
      name: { type: 'string', description: 'What the pursuit is' },
      accountId: { type: 'string', description: 'Account id' },
      accountName: { type: 'string', description: 'Account name (used when accountId is omitted)' },
      pipelineName: { type: 'string', description: 'Pipeline name. Defaults to the one matching the channel, else the default pipeline.' },
      stageName: { type: 'string', description: 'Starting stage name. Defaults to the first open stage.' },
      channel: { type: 'string', description: 'gov_prime | gov_sub | commercial_oem | aftermarket | ecommerce | fms_dcs' },
      value: { type: 'number', description: 'Deal value' },
      currency: { type: 'string', description: 'Currency code (default USD)' },
      closeDate: { type: 'string', description: 'Expected close date, ISO' },
      ownerUserId: { type: 'string', description: 'User id of the owner' },
      description: { type: 'string', description: 'What the pursuit is about' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const name = typeof input.name === 'string' ? input.name.trim() : ''
      if (!name) return { error: 'An opportunity needs a name.' }
      const ctx = await resolveCrmAccount(input)
      if ('error' in ctx) return ctx

      const {
        createOpportunity, listPipelines, listPipelineStages,
      } = await import('@/modules/crm/services/crmService')
      const { firstOpenStage } = await import('@/lib/crm/pipelineEngine')

      const pipelines = (await listPipelines()).filter((p) => p.active)
      if (pipelines.length === 0) {
        return { error: 'There are no pipelines yet. Build one under Pipelines first: an opportunity has to live in one.' }
      }
      const channel = typeof input.channel === 'string' ? input.channel : null
      const wanted = typeof input.pipelineName === 'string' ? input.pipelineName.trim().toLowerCase() : ''
      const pipeline = (wanted && pipelines.find((p) => p.name.toLowerCase().includes(wanted)))
        ?? (channel ? pipelines.find((p) => p.channel === channel) : undefined)
        ?? pipelines.find((p) => p.isDefault)
        ?? pipelines[0]
      if (!pipeline) return { error: 'Could not pick a pipeline.' }

      const stages = await listPipelineStages(pipeline.id)
      const wantedStage = typeof input.stageName === 'string' ? input.stageName.trim().toLowerCase() : ''
      const stage = wantedStage
        ? stages.find((s) => s.name.toLowerCase().includes(wantedStage)) ?? firstOpenStage(stages)
        : firstOpenStage(stages)

      try {
        const opportunity = await createOpportunity({
          name,
          accountId: ctx.id,
          pipelineId: pipeline.id,
          stageId: stage?.id ?? null,
          channel,
          value: typeof input.value === 'number' ? input.value : null,
          currency: (input.currency as string) ?? 'USD',
          closeDate: (input.closeDate as string) ?? null,
          ownerUserId: (input.ownerUserId as string) ?? null,
          description: (input.description as string) ?? null,
          createdBy: 'ai',
        })
        auditCrmTool('create_opportunity', 'crm_opportunity', opportunity.id, {
          accountId: ctx.id, pipeline: pipeline.name, stage: stage?.name ?? null,
        })
        return {
          ok: true, opportunityId: opportunity.id, account: ctx.name, pipeline: pipeline.name,
          stage: opportunity.stageName,
          message: `Opened "${name}" on ${ctx.name}, in ${pipeline.name}${opportunity.stageName ? ` at ${opportunity.stageName}` : ''}.`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },

  {
    name: 'update_opportunity',
    description:
      'Change an opportunity\'s fields, or move it to another stage. A stage move is evaluated against that stage\'s entry checklist exactly as the board is: when the organization blocks on unmet required items, this refuses and names them. Writes on confirmation.',
    parameters: {
      opportunityId: { type: 'string', description: 'Opportunity id' },
      opportunityName: { type: 'string', description: 'Opportunity name, used when the id is omitted' },
      stageName: { type: 'string', description: 'Move to this stage (matched within the opportunity\'s own pipeline)' },
      checklistDone: { type: 'array', description: 'Ids or labels of entry checklist items that are satisfied, for the stage being entered' },
      gateDecision: { type: 'string', description: 'go | no_go | conditional | deferred, when entering a gate stage' },
      note: { type: 'string', description: 'Why the move, recorded on the stage history' },
      name: { type: 'string', description: 'Rename the opportunity' },
      value: { type: 'number', description: 'Deal value' },
      closeDate: { type: 'string', description: 'Expected close date, ISO' },
      ownerUserId: { type: 'string', description: 'User id of the owner' },
      captureManager: { type: 'string', description: 'Capture manager' },
      description: { type: 'string', description: 'What the pursuit is about' },
      notes: { type: 'string', description: 'Free-text notes' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const {
        listOpportunities, listPipelineStages, listSettings, updateOpportunity,
      } = await import('@/modules/crm/services/crmService')
      const { moveStageAndTriggerWorkflow } = await import('@/modules/crm/services/crmWorkflowService')
      const { evaluateEntryChecklist } = await import('@/lib/crm/pipelineEngine')
      const { resolveSetting } = await import('@/modules/crm/lib/crmSettingDefs')

      const opps = await listOpportunities()
      const id = typeof input.opportunityId === 'string' ? input.opportunityId.trim() : ''
      const wantedName = typeof input.opportunityName === 'string' ? input.opportunityName.trim().toLowerCase() : ''
      const opportunity = id
        ? opps.find((o) => o.id === id)
        : opps.find((o) => o.name.toLowerCase() === wantedName)
          ?? opps.find((o) => wantedName.length > 2 && o.name.toLowerCase().includes(wantedName))
      if (!opportunity) {
        return { error: id ? `No opportunity with id ${id}.` : `No opportunity matches "${input.opportunityName}".` }
      }

      const results: string[] = []

      // Field changes first, so a rename is not lost if the stage move refuses.
      const patch: Record<string, unknown> = {}
      if (typeof input.name === 'string' && input.name.trim()) patch.name = input.name.trim()
      if (typeof input.value === 'number') patch.value = input.value
      if (typeof input.closeDate === 'string' && input.closeDate) patch.closeDate = input.closeDate
      for (const key of ['ownerUserId', 'captureManager', 'description', 'notes']) {
        if (typeof input[key] === 'string') patch[key] = input[key]
      }
      if (Object.keys(patch).length > 0) {
        try {
          await updateOpportunity(opportunity.id, patch)
          results.push(`updated ${Object.keys(patch).join(', ')}`)
        } catch (e) {
          return { error: e instanceof Error ? e.message : String(e) }
        }
      }

      const stageName = typeof input.stageName === 'string' ? input.stageName.trim().toLowerCase() : ''
      if (!stageName) {
        if (results.length === 0) return { error: 'Name at least one field to change, or a stage to move to.' }
        auditCrmTool('update_opportunity', 'crm_opportunity', opportunity.id, patch)
        return { ok: true, opportunityId: opportunity.id, message: `${opportunity.name}: ${results.join('; ')}.` }
      }

      if (!opportunity.pipelineId) {
        return { error: `${opportunity.name} is not on a pipeline, so it has no stages to move between.` }
      }
      const stages = await listPipelineStages(opportunity.pipelineId)
      const stage = stages.find((s) => s.name.toLowerCase() === stageName)
        ?? stages.find((s) => s.name.toLowerCase().includes(stageName))
      if (!stage) {
        return { error: `No stage matching "${input.stageName}" on ${opportunity.name}'s pipeline. Stages are: ${stages.map((s) => s.name).join(', ')}.` }
      }

      // Tick only what the caller actually named, by id or by label.
      const named = Array.isArray(input.checklistDone) ? input.checklistDone.map((v) => String(v).toLowerCase()) : []
      const checklistState = stage.entryChecklist.map((item) => ({
        id: item.id,
        done: named.includes(item.id.toLowerCase()) || named.includes(item.label.toLowerCase()),
      }))

      const settings = await listSettings()
      const settingMap = new Map(settings.map((s) => [s.key, s.value]))
      const enforcement = (resolveSetting('crm.stage_checklist_enforcement', settingMap) ?? 'block') as 'block' | 'warn'
      const evaluation = evaluateEntryChecklist(stage.entryChecklist, checklistState, enforcement)
      if (!evaluation.allowed) {
        return {
          error: evaluation.message,
          unmet: evaluation.unmet.map((i) => i.label),
          hint: 'Satisfy those items and pass them in checklistDone, or change the stage checklist enforcement setting to warn.',
        }
      }

      try {
        const { move, workflow } = await moveStageAndTriggerWorkflow({
          opportunityId: opportunity.id,
          toStageId: stage.id,
          checklistState,
          checklistBypassed: evaluation.bypassed,
          gateDecision: (input.gateDecision as never) ?? null,
          note: (input.note as string) ?? null,
          actor: 'ai',
        })
        auditCrmTool('update_opportunity', 'crm_opportunity', opportunity.id, {
          toStage: stage.name, bypassed: evaluation.bypassed, ...patch,
        })
        results.push(`moved to ${stage.name}`)
        return {
          ok: true,
          opportunityId: opportunity.id,
          stage: move.opportunity.stageName,
          checklistBypassed: evaluation.bypassed,
          workflowStarted: workflow?.started ?? false,
          workflowNote: workflow?.reason ?? null,
          message: `${opportunity.name}: ${results.join('; ')}.${evaluation.bypassed ? ' Required checklist items were unmet, and the bypass is on the stage history.' : ''}${workflow?.started ? ` Started "${workflow.workflowName}".` : workflow?.reason ? ` ${workflow.reason}` : ''}`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },

  {
    name: 'add_opportunity_product',
    description:
      'Add a product line to an opportunity, priced through the CRM pricing engine with its full trace. Never invents a price: when no price list covers the material it says so rather than guessing. Writes on confirmation.',
    parameters: {
      opportunityId: { type: 'string', description: 'Opportunity id' },
      opportunityName: { type: 'string', description: 'Opportunity name, used when the id is omitted' },
      matnr: { type: 'string', description: 'Material number, padded or de-zeroed (both match)' },
      qty: { type: 'number', description: 'Quantity (default 1)' },
      notes: { type: 'string', description: 'Free-text notes on the line' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const {
        createOpportunityProduct, listAccounts, listOpportunities, listOpportunityProducts,
        listSettings, loadPricingData, syncOpportunityValueFromLines,
      } = await import('@/modules/crm/services/crmService')
      const { listSapProducts, unitStandardCost } = await import('@/modules/crm/services/sapCrmProductService')
      const {
        displayMatnr, margin, normalizeMatnr, resolvePrice, resolveUnitCost,
      } = await import('@/lib/crm/pricingEngine')
      const { resolveSetting } = await import('@/modules/crm/lib/crmSettingDefs')

      const matnrIn = typeof input.matnr === 'string' ? input.matnr.trim() : ''
      if (!matnrIn) return { error: 'Name the material to add.' }
      const qty = typeof input.qty === 'number' && input.qty > 0 ? input.qty : 1

      const opps = await listOpportunities()
      const id = typeof input.opportunityId === 'string' ? input.opportunityId.trim() : ''
      const wantedName = typeof input.opportunityName === 'string' ? input.opportunityName.trim().toLowerCase() : ''
      const opportunity = id
        ? opps.find((o) => o.id === id)
        : opps.find((o) => o.name.toLowerCase() === wantedName)
          ?? opps.find((o) => wantedName.length > 2 && o.name.toLowerCase().includes(wantedName))
      if (!opportunity) {
        return { error: id ? `No opportunity with id ${id}.` : `No opportunity matches "${input.opportunityName}".` }
      }

      let products
      try {
        products = await listSapProducts()
      } catch (e) {
        return { error: `Could not read the SAP product catalog: ${e instanceof Error ? e.message : String(e)}` }
      }
      const key = normalizeMatnr(matnrIn)
      const product = products.find((p) => normalizeMatnr(p.matnr) === key)
      if (!product) return { error: `No SAP material matches "${matnrIn}".` }

      const [{ catalog }, accounts, settings, existing] = await Promise.all([
        loadPricingData(), listAccounts(), listSettings(), listOpportunityProducts(opportunity.id),
      ])
      const account = accounts.find((a) => a.id === opportunity.accountId)
      const settingMap = new Map(settings.map((s) => [s.key, s.value]))
      const basis = (resolveSetting('crm.margin_cost_basis', settingMap) ?? 'sap_standard_cost') as never

      const cost = resolveUnitCost({
        basis,
        standardCost: product.standardCost,
        standardCostUnit: product.costUnit,
        costOverride: null,
      })
      const resolved = resolvePrice(catalog, {
        matnr: product.matnr,
        qty,
        date: new Date().toISOString(),
        accountId: opportunity.accountId,
        accountTier: account?.tier ?? null,
        channel: opportunity.channel,
        productHierarchy: product.productHierarchy,
        uom: product.salesUom || product.baseUom,
        currency: opportunity.currency,
        cost: cost.cost,
        costPriceUnit: 1,
      })
      if (resolved.status === 'error') return { error: resolved.message }

      const unitCost = unitStandardCost(product)
      const m = resolved.netUnitPrice != null && unitCost != null ? margin(resolved.netUnitPrice, unitCost) : null

      try {
        const line = await createOpportunityProduct(opportunity.id, {
          seq: existing.length,
          matnr: product.matnr,
          description: product.description || null,
          qty,
          uom: product.salesUom || product.baseUom || null,
          unitPrice: resolved.unitPrice,
          priceUnit: resolved.priceUnit,
          netUnitPrice: resolved.netUnitPrice,
          extendedPrice: resolved.extendedPrice,
          currency: resolved.currency,
          priceSource: 'engine',
          manualOverride: false,
          resolvedUnitPrice: resolved.unitPrice,
          priceListId: resolved.listId,
          priceListName: resolved.listName,
          priceTrace: resolved.trace as unknown as Record<string, unknown>[],
          pricedAt: new Date().toISOString(),
          unitCost,
          marginPct: m?.pct ?? null,
          productHierarchy: product.productHierarchy || null,
          notes: (input.notes as string) ?? null,
          createdBy: 'ai',
        })
        await syncOpportunityValueFromLines(opportunity.id)
        auditCrmTool('add_opportunity_product', 'crm_opportunity_product', line.id, {
          opportunityId: opportunity.id, matnr: product.matnr, qty, status: resolved.status,
        })
        return {
          ok: true,
          lineId: line.id,
          matnr: displayMatnr(product.matnr),
          priceStatus: resolved.status,
          unitPrice: resolved.unitPrice,
          extendedPrice: resolved.extendedPrice,
          priceList: resolved.listName,
          message: resolved.status === 'priced'
            ? `Added ${displayMatnr(product.matnr)} x ${qty} at ${resolved.currency} ${resolved.unitPrice?.toFixed(2)}${resolved.priceUnit > 1 ? ` per ${resolved.priceUnit}` : ''} from ${resolved.listName}.`
            : `Added ${displayMatnr(product.matnr)} x ${qty} with NO price: ${resolved.message}`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },

  {
    name: 'set_opportunity_scores',
    description:
      'Set PWin, PGo, and PTime on an opportunity, optionally scoring the individual PWin worksheet factors with a rationale each. Every change appends to the score history and is marked as coming from the assistant. Writes on confirmation.',
    parameters: {
      opportunityId: { type: 'string', description: 'Opportunity id' },
      opportunityName: { type: 'string', description: 'Opportunity name, used when the id is omitted' },
      pwin: { type: 'number', description: 'Probability of win, 0 to 100. Overrides the worksheet when given.' },
      pgo: { type: 'number', description: 'Probability the customer proceeds at all, 0 to 100' },
      ptime: { type: 'number', description: 'Confidence the award lands in the expected period, 0 to 100' },
      factors: {
        type: 'array',
        description: 'Worksheet factor scores as [{ key, score, rationale }]. score is 0 to 100. Factor keys come from the opportunity\'s scoring model.',
      },
      note: { type: 'string', description: 'Why the numbers moved, recorded against this history entry' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const {
        listOpportunities, listScoringModels, setOpportunityScores,
      } = await import('@/modules/crm/services/crmService')
      const { computeWeightedScore } = await import('@/lib/crm/pipelineEngine')

      const opps = await listOpportunities()
      const id = typeof input.opportunityId === 'string' ? input.opportunityId.trim() : ''
      const wantedName = typeof input.opportunityName === 'string' ? input.opportunityName.trim().toLowerCase() : ''
      const opportunity = id
        ? opps.find((o) => o.id === id)
        : opps.find((o) => o.name.toLowerCase() === wantedName)
          ?? opps.find((o) => wantedName.length > 2 && o.name.toLowerCase().includes(wantedName))
      if (!opportunity) {
        return { error: id ? `No opportunity with id ${id}.` : `No opportunity matches "${input.opportunityName}".` }
      }

      const pct = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : null)

      // Merge factor scores onto the model's factor list, or onto whatever the
      // opportunity already carries when no model is attached.
      const models = await listScoringModels()
      const model = models.find((m) => m.id === opportunity.scoring.modelId)
        ?? models.find((m) => m.active && m.channel === opportunity.channel)
        ?? models.find((m) => m.active && !m.channel)
      const base = model
        ? model.factors.map((f) => {
          const prior = opportunity.scoring.factors.find((p) => p.key === f.key)
          return { key: f.key, label: f.label, weight: f.weight, score: prior?.score ?? null, rationale: prior?.rationale ?? null }
        })
        : [...opportunity.scoring.factors]

      const given = Array.isArray(input.factors) ? input.factors : []
      let scored = 0
      for (const raw of given) {
        if (!raw || typeof raw !== 'object') continue
        const f = raw as Record<string, unknown>
        const key = String(f.key ?? '')
        const at = base.findIndex((b) => b.key === key)
        if (at < 0) continue
        const entry = base[at]
        if (!entry) continue
        base[at] = {
          ...entry,
          score: pct(f.score),
          rationale: f.rationale == null ? entry.rationale : String(f.rationale),
        }
        scored++
      }

      const computed = computeWeightedScore(base).score
      const manual = pct(input.pwin)
      const pwin = manual ?? computed

      if (pwin === null && pct(input.pgo) === null && pct(input.ptime) === null && scored === 0) {
        return { error: 'Give at least one of pwin, pgo, ptime, or a factor score.' }
      }

      try {
        const saved = await setOpportunityScores(opportunity.id, {
          pwin,
          pgo: pct(input.pgo) ?? opportunity.pgo,
          ptime: pct(input.ptime) ?? opportunity.ptime,
          scoring: {
            modelId: model?.id ?? opportunity.scoring.modelId,
            modelName: model?.name ?? opportunity.scoring.modelName,
            factors: base,
            computedPwin: computed,
            manualPwin: manual,
            updatedAt: new Date().toISOString(),
            updatedBy: 'ai',
          },
          source: 'ai',
          note: (input.note as string) ?? null,
          by: 'ai',
        })
        auditCrmTool('set_opportunity_scores', 'crm_opportunity', opportunity.id, {
          pwin: saved.pwin, pgo: saved.pgo, ptime: saved.ptime, factorsScored: scored,
        })
        return {
          ok: true,
          opportunityId: saved.id,
          pwin: saved.pwin,
          pgo: saved.pgo,
          ptime: saved.ptime,
          computedPwin: computed,
          message: `${saved.name}: PWin ${saved.pwin ?? 'unset'}%${manual !== null && computed !== null ? ` (worksheet says ${computed}%)` : ''}, PGo ${saved.pgo ?? 'unset'}%, PTime ${saved.ptime ?? 'unset'}%${scored > 0 ? `, ${scored} factor${scored === 1 ? '' : 's'} scored` : ''}.`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },

  {
    name: 'log_gate_review',
    description:
      'Record a gate review on an opportunity: the decision, its rationale, and the scores as they stand right now. A conditional go needs its conditions stated. Writes on confirmation.',
    parameters: {
      opportunityId: { type: 'string', description: 'Opportunity id' },
      opportunityName: { type: 'string', description: 'Opportunity name, used when the id is omitted' },
      gateName: { type: 'string', description: 'What gate this is, e.g. "Bid / no-bid"' },
      decision: { type: 'string', description: 'go | no_go | conditional | deferred' },
      rationale: { type: 'string', description: 'Why this call, on this evidence' },
      conditions: { type: 'string', description: 'What has to be true for a conditional go to stand' },
      reviewers: { type: 'array', description: 'Who was in the room, as [{ name, role }] or plain names' },
      nextReviewAt: { type: 'string', description: 'Date of the next review, ISO' },
      notes: { type: 'string', description: 'Free-text notes' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const {
        createGateReview, listOpportunities, listPipelineStages,
      } = await import('@/modules/crm/services/crmService')

      const opps = await listOpportunities()
      const id = typeof input.opportunityId === 'string' ? input.opportunityId.trim() : ''
      const wantedName = typeof input.opportunityName === 'string' ? input.opportunityName.trim().toLowerCase() : ''
      const opportunity = id
        ? opps.find((o) => o.id === id)
        : opps.find((o) => o.name.toLowerCase() === wantedName)
          ?? opps.find((o) => wantedName.length > 2 && o.name.toLowerCase().includes(wantedName))
      if (!opportunity) {
        return { error: id ? `No opportunity with id ${id}.` : `No opportunity matches "${input.opportunityName}".` }
      }

      const decision = String(input.decision ?? '').trim()
      if (!['go', 'no_go', 'conditional', 'deferred'].includes(decision)) {
        return { error: 'Decision has to be one of go, no_go, conditional, or deferred.' }
      }
      const rationale = typeof input.rationale === 'string' ? input.rationale.trim() : ''
      if (!rationale) return { error: 'A decision with no rationale is a decision nobody can revisit. Say why.' }
      const conditions = typeof input.conditions === 'string' ? input.conditions.trim() : ''
      if (decision === 'conditional' && !conditions) {
        return { error: 'A conditional go needs its conditions written down.' }
      }

      const pipelineStages = opportunity.pipelineId && opportunity.stageId
        ? await listPipelineStages(opportunity.pipelineId)
        : []
      const stage = pipelineStages.find((s) => s.id === opportunity.stageId)

      const rawReviewers = Array.isArray(input.reviewers) ? input.reviewers : []
      const reviewers = rawReviewers
        .map((r) => (typeof r === 'string'
          ? { name: r.trim(), role: null }
          : r && typeof r === 'object'
            ? { name: String((r as Record<string, unknown>).name ?? '').trim(), role: (r as Record<string, unknown>).role == null ? null : String((r as Record<string, unknown>).role) }
            : null))
        .filter((r): r is { name: string; role: string | null } => !!r && !!r.name)

      try {
        const review = await createGateReview(opportunity.id, {
          gateName: typeof input.gateName === 'string' && input.gateName.trim()
            ? input.gateName.trim()
            : stage?.name ? `${stage.name} gate` : 'Bid / no-bid',
          stageId: stage?.id ?? null,
          stageName: stage?.name ?? null,
          decision: decision as never,
          rationale,
          conditions: conditions || null,
          pwin: opportunity.pwin,
          pgo: opportunity.pgo,
          ptime: opportunity.ptime,
          scores: opportunity.scoring as unknown as Record<string, unknown>,
          reviewers,
          decidedBy: 'ai',
          nextReviewAt: (input.nextReviewAt as string) ?? null,
          notes: (input.notes as string) ?? null,
          createdBy: 'ai',
        })
        auditCrmTool('log_gate_review', 'crm_gate_review', review.id, {
          opportunityId: opportunity.id, decision, pwin: opportunity.pwin,
        })
        return {
          ok: true, gateReviewId: review.id, decision: review.decision, gate: review.gateName,
          message: `Recorded ${review.gateName} on ${opportunity.name} as ${decision.replace('_', ' ')}, with PWin ${opportunity.pwin ?? 'unset'}% snapshotted onto the review.`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },

  // --- P4: service desk and Customer 360 ------------------------------------
  {
    name: 'log_escalation',
    description:
      'Raise a customer escalation or log customer feedback against a CRM account. It lands on the Service and Escalations queue with a status of open, and is timed against whichever service level policy covers its type and severity. Writes on confirmation.',
    parameters: {
      accountId: { type: 'string', description: 'Target account id' },
      accountName: { type: 'string', description: 'Target account name (used when accountId is omitted)' },
      kind: { type: 'string', description: 'escalation (something has gone wrong) | feedback (something needs an answer). Default escalation.' },
      severity: { type: 'string', description: 'low | medium | high | critical. Default medium.' },
      subject: { type: 'string', description: 'One line the queue can be scanned by' },
      body: { type: 'string', description: 'What happened, in full' },
      contactId: { type: 'string', description: 'Contact who raised it, when known' },
      opportunityId: { type: 'string', description: 'Related pursuit, when it relates to one' },
      assigneeUserId: { type: 'string', description: 'Who should own working it to closure' },
      direction: { type: 'string', description: 'inbound | outbound | internal' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const ctx = await resolveCrmAccount(input)
      if ('error' in ctx) return ctx

      const subject = typeof input.subject === 'string' ? input.subject.trim() : ''
      if (!subject) return { error: 'An escalation needs a one-line summary so the queue can be scanned.' }

      const kind = input.kind === 'feedback' ? 'feedback' : 'escalation'
      const severity = ['low', 'medium', 'high', 'critical'].includes(String(input.severity))
        ? String(input.severity)
        : 'medium'

      const { createInteraction, listSlaPolicies } = await import('@/modules/crm/services/crmService')
      const { assessSla, snapshotOf } = await import('@/lib/crm/slaEngine')

      try {
        // The promise is snapshotted at creation so a later policy edit cannot
        // rewrite what was owed when the item was raised.
        const policies = await listSlaPolicies().catch(() => [])
        const assessment = assessSla(
          {
            type: kind, severity: severity as never, createdAt: new Date().toISOString(),
            status: 'open', firstResponseAt: null, resolvedAt: null,
          },
          policies,
        )

        const item = await createInteraction({
          accountId: ctx.id,
          contactId: (input.contactId as string) ?? null,
          opportunityId: (input.opportunityId as string) ?? null,
          type: kind,
          direction: (input.direction as never) ?? 'inbound',
          subject,
          body: (input.body as string) ?? null,
          status: 'open',
          severity: severity as never,
          assigneeUserId: (input.assigneeUserId as string) ?? null,
          source: 'manual',
          slaSnapshot: assessment.applicable
            ? (snapshotOf(assessment) as unknown as Record<string, unknown>)
            : {},
          createdBy: 'ai',
        })

        auditCrmTool('log_escalation', 'crm_interaction', item.id, {
          accountId: ctx.id, kind, severity, subject,
        })
        return {
          ok: true,
          interactionId: item.id,
          accountName: ctx.name,
          severity,
          slaApplied: assessment.applicable,
          responseDueAt: assessment.response?.dueAt ?? null,
          resolutionDueAt: assessment.resolution?.dueAt ?? null,
          message: assessment.applicable
            ? `Raised a ${severity} ${kind} on ${ctx.name}, timed under "${assessment.policyName ?? assessment.policyId}".`
            : `Raised a ${severity} ${kind} on ${ctx.name}. No service level policy covers it, so nothing is being timed.`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'update_escalation',
    description:
      'Move an escalation or feedback item along: change its status, assign it, or change its severity. Closing REQUIRES a resolution note saying what was done, and the tool refuses without one. Moving off open records a first response; reopening clears the resolved time so the clock runs again. Writes on confirmation.',
    parameters: {
      interactionId: { type: 'string', description: 'The item id (from the queue or log_escalation)' },
      subject: { type: 'string', description: 'Match by summary instead of id, when the id is not to hand' },
      accountName: { type: 'string', description: 'Narrow a subject match to one account' },
      status: { type: 'string', description: 'open | actioned | closed' },
      note: { type: 'string', description: 'What was done. Required to close.' },
      assigneeUserId: { type: 'string', description: 'Who owns it from now on. Pass an empty string to unassign.' },
      severity: { type: 'string', description: 'low | medium | high | critical' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const {
        listServiceItems, transitionInteraction, assignInteraction, updateInteraction,
        listSlaPolicies, listAccounts,
      } = await import('@/modules/crm/services/crmService')
      const { assessSla, snapshotOf } = await import('@/lib/crm/slaEngine')

      const items = await listServiceItems()
      if (items.length === 0) return { error: 'There is nothing in the service queue yet.' }

      const id = typeof input.interactionId === 'string' ? input.interactionId.trim() : ''
      let item = id ? items.find((i) => i.id === id) : undefined

      if (!item) {
        const wanted = typeof input.subject === 'string' ? input.subject.trim().toLowerCase() : ''
        if (!wanted) {
          return { error: 'Name the item to act on: pass interactionId, or a subject to match on.' }
        }
        let candidates = items.filter((i) => (i.subject ?? '').toLowerCase().includes(wanted))
        const accountName = typeof input.accountName === 'string' ? input.accountName.trim().toLowerCase() : ''
        if (accountName) {
          const accounts = await listAccounts()
          const ids = new Set(
            accounts.filter((a) => a.name.toLowerCase().includes(accountName)).map((a) => a.id),
          )
          candidates = candidates.filter((i) => i.accountId && ids.has(i.accountId))
        }
        if (candidates.length === 0) return { error: `No service item matches "${input.subject}".` }
        if (candidates.length > 1) {
          return {
            error: `${candidates.length} service items match "${input.subject}". Pass the interactionId of the one you mean.`,
            candidates: candidates.slice(0, 5).map((i) => ({ id: i.id, subject: i.subject, status: i.status })),
          }
        }
        item = candidates[0]!
      }

      const changes: string[] = []
      try {
        if (typeof input.severity === 'string' && input.severity.trim()) {
          const severity = input.severity.trim()
          if (!['low', 'medium', 'high', 'critical'].includes(severity)) {
            return { error: 'Severity has to be one of low, medium, high, or critical.' }
          }
          item = await updateInteraction(item.id, { severity: severity as never })
          changes.push(`severity to ${severity}`)
        }

        if (typeof input.assigneeUserId === 'string') {
          item = await assignInteraction(item.id, input.assigneeUserId.trim() || null)
          changes.push(input.assigneeUserId.trim() ? 'assigned' : 'unassigned')
        }

        if (typeof input.status === 'string' && input.status.trim()) {
          const status = input.status.trim()
          if (!['open', 'actioned', 'closed'].includes(status)) {
            return { error: 'Status has to be one of open, actioned, or closed.' }
          }
          const note = typeof input.note === 'string' ? input.note.trim() : ''
          if (status === 'closed' && !note && !item.resolutionNote) {
            return {
              error: 'Closing needs a resolution note saying what was actually done. A closed escalation with no explanation is indistinguishable from one somebody gave up on.',
            }
          }
          const policies = await listSlaPolicies().catch(() => [])
          const assessment = assessSla(
            {
              type: item.type, severity: item.severity, createdAt: item.occurredAt || item.createdAt,
              status: item.status, firstResponseAt: item.firstResponseAt, resolvedAt: item.resolvedAt,
            },
            policies,
          )
          item = await transitionInteraction(item.id, {
            status: status as never,
            note: note || null,
            actor: 'ai',
            slaSnapshot: assessment.applicable
              ? (snapshotOf(assessment) as unknown as Record<string, unknown>)
              : undefined,
          })
          changes.push(`status to ${status}`)
        }

        if (changes.length === 0) {
          return { error: 'Name at least one thing to change: status, assigneeUserId, or severity.' }
        }

        auditCrmTool('update_escalation', 'crm_interaction', item.id, { changes })
        return {
          ok: true,
          interactionId: item.id,
          status: item.status,
          severity: item.severity,
          assigneeUserId: item.assigneeUserId,
          resolvedAt: item.resolvedAt,
          message: `Updated "${item.subject ?? item.id}": ${changes.join(', ')}.`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'get_customer_360_summary',
    description:
      'Read-only. How is one customer doing: open contracts and their value, open orders, deliveries due in the next 30 days and how many are late, billed year to date, on-time delivery percentage, open escalations, and the account health score with the drivers behind it. ERP figures come live from S/4HANA and need the account to carry an SAP customer number; the CRM figures work either way.',
    parameters: {
      accountId: { type: 'string', description: 'Target account id' },
      accountName: { type: 'string', description: 'Target account name (used when accountId is omitted)' },
    },
    requiresConfirmation: false,
    handler: async (input) => {
      const ctx = await resolveCrmAccount(input)
      if ('error' in ctx) return ctx

      const {
        getAccount, listInteractions, listSlaPolicies, listSettings, loadAccountSignals,
        emptyAccountSignals,
      } = await import('@/modules/crm/services/crmService')
      const {
        listCustomerContracts, listCustomerOrders, listCustomerDeliveries,
        listCustomerBilling, contractRollup, orderRollup, billedYearToDate,
        billingRollup, summarizeUpcoming, onTimeDeliveryRate, describeSap360Error,
      } = await import('@/modules/crm/services/sapCrm360Service')
      const { buildHealthInput } = await import('@/modules/crm/lib/healthSignals')
      const { resolveHealthWeights, resolve360Window } = await import('@/modules/crm/lib/serviceSettings')
      const { scoreAccountHealth, summarizeHealth } = await import('@/lib/crm/accountHealth')

      try {
        const account = await getAccount(ctx.id)
        if (!account) return { error: `No CRM account with id ${ctx.id}.` }

        const settingsMap = new Map<string, unknown>()
        for (const s of await listSettings().catch(() => [])) settingsMap.set(s.key, s.value)
        const top = resolve360Window(settingsMap)

        const [policies, signalMap, interactions] = await Promise.all([
          listSlaPolicies().catch(() => []),
          loadAccountSignals().catch(() => null),
          listInteractions({ accountId: account.id }).catch(() => []),
        ])
        const signals = signalMap?.get(account.id) ?? emptyAccountSignals(account.id)

        const kunnr = account.sapKunnr ?? ''
        let erp: Record<string, unknown> = {
          available: false,
          reason: 'This account is not linked to an SAP customer, so there is no ERP history to read.',
        }
        let deliveriesOnTime: number | undefined
        let deliveriesTotal: number | undefined
        let billedTrailing12: number | null = null

        if (kunnr) {
          // Each read is settled independently: one failing entity must not
          // blank the whole answer, exactly as the 360 tab behaves.
          const [contracts, orders, upcoming, shipped, billing] = await Promise.allSettled([
            listCustomerContracts(kunnr, { top }),
            listCustomerOrders(kunnr, { top }),
            listCustomerDeliveries(kunnr, 'UPCOMING', { top }),
            listCustomerDeliveries(kunnr, 'SHIPPED', { top }),
            listCustomerBilling(kunnr, { top }),
          ])

          const rate = shipped.status === 'fulfilled' ? onTimeDeliveryRate(shipped.value) : null
          if (rate && rate.measured > 0) {
            deliveriesOnTime = rate.onTime
            deliveriesTotal = rate.measured
          }
          if (billing.status === 'fulfilled') {
            billedTrailing12 = billingRollup(billing.value, { sinceDays: 365 }).value
          }

          const due = upcoming.status === 'fulfilled'
            ? summarizeUpcoming(upcoming.value, { horizonDays: 30 })
            : null

          erp = {
            available: true,
            sapCustomer: kunnr,
            rowWindow: top,
            openContracts: contracts.status === 'fulfilled'
              ? contractRollup(contracts.value)
              : { error: describeSap360Error(contracts.reason) },
            openOrders: orders.status === 'fulfilled'
              ? orderRollup(orders.value)
              : { error: describeSap360Error(orders.reason) },
            deliveriesDueIn30Days: due?.dueSoon ?? null,
            nextDeliveryDate: due?.nextDate ?? null,
            lateDeliveries: due?.late ?? null,
            billedYearToDate: billing.status === 'fulfilled'
              ? billedYearToDate(billing.value)
              : { error: describeSap360Error(billing.reason) },
            billedTrailing12Months: billedTrailing12,
            onTimeDeliveryPct: rate?.pct === null || rate === null ? null : Math.round(rate.pct),
            onTimeMeasuredOver: rate?.measured ?? 0,
          }
        }

        const health = scoreAccountHealth(
          buildHealthInput(signals, policies, { deliveriesOnTime, deliveriesTotal, billedTrailing12 }),
          { weights: resolveHealthWeights(settingsMap) },
        )

        const openService = interactions.filter(
          (i) => (i.type === 'escalation' || i.type === 'feedback') && i.status !== 'closed',
        )

        auditCrmTool('get_customer_360_summary', 'crm_account', account.id, { name: account.name })

        return {
          ok: true,
          accountId: account.id,
          accountName: account.name,
          erp,
          crm: {
            openEscalations: openService.length,
            openEscalationsBySeverity: openService.reduce<Record<string, number>>((acc, i) => {
              const key = i.severity ?? 'unrated'
              acc[key] = (acc[key] ?? 0) + 1
              return acc
            }, {}),
            lastInteractionAt: signals.lastInteractionAt,
            openOpportunities: signals.openOpportunityCount,
            openPipelineValue: signals.openPipelineValue,
          },
          health: {
            score: health.score,
            band: health.band,
            coverage: Math.round(health.coverage * 100),
            drivers: health.drivers.map((d) => ({
              label: d.label, score: d.score, contribution: d.contribution, why: d.detail,
            })),
            notCounted: health.missing.map((m) => ({ label: m.label, reason: m.reason })),
          },
          message: summarizeHealth(health),
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },

  // --- P5: CPQ. Quotes, guardrails, and the SAP sales order -----------------
  // Every write tool is confirmation gated, as the whole block is. The one READ
  // tool (get_quote_summary) is not, matching the existing registry precedent
  // that only writes are gated (the PPM, agile, and Customer 360 read tools all
  // do this).
  {
    name: 'create_quote_from_opportunity',
    description:
      'Create a CRM quote from an existing opportunity, carrying its priced product lines across with their pricing traces intact. The quote links back to the pursuit, and the SAP order created from it later writes its number onto both. Writes on confirmation.',
    parameters: {
      opportunityId: { type: 'string', description: 'The opportunity id' },
      opportunityName: { type: 'string', description: 'Match by pursuit name instead, when the id is not to hand' },
      name: { type: 'string', description: 'What the quote is for. Defaults to the pursuit name.' },
      validUntil: { type: 'string', description: 'Expiry date, YYYY-MM-DD. Defaults to the organization setting.' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const {
        createQuoteFromOpportunity, listOpportunities, listSettings,
      } = await import('@/modules/crm/services/crmService')
      const { resolveSetting } = await import('@/modules/crm/lib/crmSettingDefs')
      const { defaultValidUntil } = await import('@/lib/crm/quoteMath')

      let opportunityId = typeof input.opportunityId === 'string' ? input.opportunityId.trim() : ''
      if (!opportunityId) {
        const wanted = typeof input.opportunityName === 'string' ? input.opportunityName.trim().toLowerCase() : ''
        if (!wanted) return { error: 'Name the pursuit to quote (opportunityName), or pass its opportunityId.' }
        const matches = (await listOpportunities()).filter((o) => o.name.toLowerCase().includes(wanted))
        if (matches.length === 0) return { error: `No opportunity matches "${input.opportunityName}".` }
        if (matches.length > 1) {
          return {
            error: `${matches.length} opportunities match "${input.opportunityName}". Pass the opportunityId of the one you mean.`,
            candidates: matches.slice(0, 5).map((o) => ({ id: o.id, name: o.name, stage: o.stageName })),
          }
        }
        opportunityId = matches[0]!.id
      }

      try {
        const settings = await listSettings().catch(() => [])
        const map = new Map(settings.map((s) => [s.key, s.value]))
        const days = Number(resolveSetting('crm.quote_validity_days', map) ?? '30')

        const quote = await createQuoteFromOpportunity(opportunityId, {
          name: (input.name as string) ?? undefined,
          validUntil: (input.validUntil as string) ?? defaultValidUntil(Number.isFinite(days) ? days : 30),
          createdBy: 'ai',
        })

        auditCrmTool('create_quote_from_opportunity', 'crm_quote', quote.id, {
          opportunityId, quoteNo: quote.quoteNo, lineCount: quote.lineCount,
        })
        return {
          ok: true,
          quoteId: quote.id,
          quoteNo: quote.quoteNo,
          lineCount: quote.lineCount,
          subtotal: quote.subtotal,
          currency: quote.currency,
          requiresApproval: quote.requiresApproval,
          message: `Created ${quote.quoteNo} with ${quote.lineCount} ${quote.lineCount === 1 ? 'line' : 'lines'} totalling ${quote.currency} ${(quote.subtotal ?? 0).toFixed(2)}.`
            + (quote.requiresApproval ? ' It breaches a pricing guardrail, so it will need approval before it can be sent.' : ''),
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'add_quote_line',
    description:
      'Add a priced line to a DRAFT quote. The price is resolved by the CRM pricing engine (never invented) and the line is checked against the organization\'s pricing guardrails, whose verdict is returned. Writes on confirmation.',
    parameters: {
      quoteId: { type: 'string', description: 'The quote id' },
      quoteNo: { type: 'string', description: 'Match by quote number (Q-000007) instead of the id' },
      matnr: { type: 'string', description: 'SAP material number, padded or plain' },
      qty: { type: 'number', description: 'Quantity. Must be positive.' },
      notes: { type: 'string', description: 'Line note, printed on the document' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const {
        createQuoteLine, getQuote, listAccounts, listPriceRules, listQuoteLines,
        listQuotes, listSettings, loadPricingData, recalcQuote, toGuardrailRules,
      } = await import('@/modules/crm/services/crmService')
      const { resolveSetting } = await import('@/modules/crm/lib/crmSettingDefs')
      const { resolvePrice, resolveUnitCost, margin } = await import('@/lib/crm/pricingEngine')
      const { evaluateLine } = await import('@/lib/crm/guardrails')
      const { listSapProducts, unitStandardCost } = await import('@/modules/crm/services/sapCrmProductService')

      const matnr = typeof input.matnr === 'string' ? input.matnr.trim() : ''
      const qty = Number(input.qty)
      if (!matnr) return { error: 'Name the material to add (matnr).' }
      if (!Number.isFinite(qty) || qty <= 0) return { error: 'Quantity has to be a positive number.' }

      const id = typeof input.quoteId === 'string' ? input.quoteId.trim() : ''
      let quote = id ? await getQuote(id) : null
      if (!quote) {
        const wanted = typeof input.quoteNo === 'string' ? input.quoteNo.trim().toUpperCase() : ''
        if (!wanted) return { error: 'Name the quote to add to (quoteId or quoteNo).' }
        const candidates = (await listQuotes()).filter((q) => q.quoteNo.toUpperCase() === wanted)
        if (candidates.length === 0) return { error: `No quote numbered ${wanted}.` }
        // Newest version of that number: the older ones are superseded history.
        quote = candidates.sort((a, b) => b.version - a.version)[0]!
      }
      if (quote.status !== 'draft') {
        return { error: `${quote.quoteNo} is ${quote.status}, so its lines are frozen. Revise it to change the prices.` }
      }

      try {
        const [{ catalog }, rules, accounts, products, lines, settings] = await Promise.all([
          loadPricingData(), listPriceRules(), listAccounts(), listSapProducts().catch(() => []),
          listQuoteLines(quote.id), listSettings().catch(() => []),
        ])
        const account = accounts.find((a) => a.id === quote.accountId)
        const map = new Map(settings.map((s) => [s.key, s.value]))
        const basis = (resolveSetting('crm.margin_cost_basis', map) ?? 'sap_standard_cost') as never
        const product = products.find((p) => p.matnr === matnr)
          ?? products.find((p) => p.matnr.replace(/^0+/, '') === matnr.replace(/^0+/, ''))

        const cost = product
          ? resolveUnitCost({
            basis,
            standardCost: product.standardCost,
            standardCostUnit: product.costUnit,
            costOverride: null,
          })
          : null

        const resolved = resolvePrice(catalog, {
          matnr: product?.matnr ?? matnr,
          qty,
          date: new Date().toISOString(),
          accountId: quote.accountId,
          accountTier: account?.tier ?? null,
          channel: quote.channel,
          productHierarchy: product?.productHierarchy ?? null,
          uom: product?.salesUom ?? product?.baseUom ?? null,
          currency: quote.currency,
          cost: cost?.cost ?? null,
          costPriceUnit: 1,
        })
        if (resolved.status !== 'priced') {
          return { error: `The pricing engine returned no price for ${matnr}: ${resolved.message}` }
        }

        const unitCost = product ? unitStandardCost(product) : null
        const m = unitCost != null ? margin(resolved.netUnitPrice, unitCost) : null
        const verdict = evaluateLine(
          {
            matnr: product?.matnr ?? matnr,
            productHierarchy: product?.productHierarchy ?? null,
            qty,
            netUnitPrice: resolved.netUnitPrice,
            currency: resolved.currency,
            unitCost,
            resolvedNetUnitPrice: resolved.netUnitPrice,
          },
          toGuardrailRules(rules),
          { accountTier: account?.tier ?? null, channel: quote.channel, date: new Date().toISOString() },
        )

        const line = await createQuoteLine(quote.id, {
          lineNo: lines.length + 1,
          matnr: product?.matnr ?? matnr,
          description: product?.description ?? null,
          qty,
          uom: product?.salesUom ?? product?.baseUom ?? resolved.uom,
          unitPrice: resolved.unitPrice,
          priceUnit: resolved.priceUnit,
          netUnitPrice: resolved.netUnitPrice,
          extendedPrice: resolved.extendedPrice,
          currency: resolved.currency,
          priceTrace: resolved.trace as unknown as Record<string, unknown>[],
          priceListId: resolved.listId,
          priceListName: resolved.listName,
          priceSource: 'engine',
          resolvedUnitPrice: resolved.unitPrice,
          unitCost,
          marginPct: m?.pct ?? null,
          marginAmount: m === null ? null : Math.round(m.amount * qty * 100) / 100,
          guardrailFlags: verdict.violations as unknown as Record<string, unknown>[],
          requiresApproval: verdict.requiresApproval,
          productHierarchy: product?.productHierarchy ?? null,
          notes: (input.notes as string) ?? null,
          createdBy: 'ai',
        })
        const recalc = await recalcQuote(quote.id)

        auditCrmTool('add_quote_line', 'crm_quote_line', line.id, {
          quoteId: quote.id, matnr: line.matnr, qty, netUnitPrice: line.netUnitPrice,
        })
        return {
          ok: true,
          quoteId: quote.id,
          quoteNo: quote.quoteNo,
          lineId: line.id,
          unitPrice: line.netUnitPrice,
          priceList: resolved.listName,
          extendedPrice: line.extendedPrice,
          marginPct: line.marginPct,
          quoteSubtotal: recalc?.totals.subtotal ?? null,
          guardrails: {
            violations: verdict.violations.map((v) => ({ rule: v.ruleName, severity: v.severity, message: v.message })),
            notMeasured: verdict.skipped.map((s) => s.reason),
            requiresApproval: verdict.requiresApproval,
            blocked: verdict.blocked,
          },
          message: `Added ${line.qty} x ${line.matnr} at ${resolved.currency} ${line.netUnitPrice?.toFixed(2)} per unit from "${resolved.listName}".`
            + (verdict.blocked ? ' It breaches a rule that cannot be approved away, so the quote cannot be submitted until the price changes.'
              : verdict.requiresApproval ? ' It breaches a pricing guardrail, so the quote will need approval.'
                : ''),
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'submit_quote',
    description:
      'Submit a draft quote. This FREEZES every price and its trace onto the document (a sent quote never moves), refuses an empty quote, an unpriced line, a mixed-currency cart, or a hard-blocked guardrail breach, and routes the quote for approval when a rule demands one. Writes on confirmation.',
    parameters: {
      quoteId: { type: 'string', description: 'The quote id' },
      quoteNo: { type: 'string', description: 'Match by quote number (Q-000007) instead of the id' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const {
        attachQuoteApprovalRun, getQuote, listAccounts, listQuotes, listSettings, transitionQuote,
      } = await import('@/modules/crm/services/crmService')
      const { resolveOptionalText } = await import('@/modules/crm/lib/crmSettingDefs')
      const { startQuoteApprovalWorkflow } = await import('@/modules/crm/services/crmWorkflowService')

      const id = typeof input.quoteId === 'string' ? input.quoteId.trim() : ''
      let quote = id ? await getQuote(id) : null
      if (!quote) {
        const wanted = typeof input.quoteNo === 'string' ? input.quoteNo.trim().toUpperCase() : ''
        if (!wanted) return { error: 'Name the quote to submit (quoteId or quoteNo).' }
        const candidates = (await listQuotes()).filter((q) => q.quoteNo.toUpperCase() === wanted)
        if (candidates.length === 0) return { error: `No quote numbered ${wanted}.` }
        quote = candidates.sort((a, b) => b.version - a.version)[0]!
      }

      try {
        const result = await transitionQuote(quote.id, { to: 'submitted', actor: 'ai' })

        let approval: { started: boolean; reason: string | null; workflowName: string | null } | null = null
        if (result.awaitingApproval) {
          const settings = await listSettings().catch(() => [])
          const map = new Map(settings.map((s) => [s.key, s.value]))
          const accounts = await listAccounts().catch(() => [])
          const outcome = await startQuoteApprovalWorkflow({
            quote: result.quote,
            trigger: resolveOptionalText('crm.quote_approval_workflow', map),
            accountName: accounts.find((a) => a.id === result.quote.accountId)?.name ?? null,
            violations: (result.guardrails?.violations ?? []).map((v) => ({
              message: v.message, severity: v.severity, kind: v.kind,
            })),
            actor: 'ai',
          })
          if (outcome.started && outcome.runId) await attachQuoteApprovalRun(result.quote.id, outcome.runId)
          approval = { started: outcome.started, reason: outcome.reason, workflowName: outcome.workflowName }
        }

        auditCrmTool('submit_quote', 'crm_quote', result.quote.id, {
          quoteNo: result.quote.quoteNo, status: result.quote.status,
        })
        return {
          ok: true,
          quoteId: result.quote.id,
          quoteNo: result.quote.quoteNo,
          status: result.quote.status,
          snapshotAt: result.quote.snapshotAt,
          subtotal: result.quote.subtotal,
          currency: result.quote.currency,
          awaitingApproval: result.awaitingApproval,
          approval,
          message: result.message
            + (approval && !approval.started && approval.reason ? ` ${approval.reason}` : ''),
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'push_quote_to_sap',
    description:
      'Create the SAP sales order for an ACCEPTED quote, carrying the CRM prices as explicit conditions, and verify every line SAP returns against the quote to the cent. A quote that already carries an order number is refused rather than pushed twice. Writes on confirmation.',
    parameters: {
      quoteId: { type: 'string', description: 'The quote id' },
      quoteNo: { type: 'string', description: 'Match by quote number (Q-000007) instead of the id' },
      poNumber: { type: 'string', description: 'Customer purchase order number. Defaults to the quote number.' },
      testRun: { type: 'boolean', description: 'True runs the whole create in SAP and writes nothing' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const {
        getQuote, listAccounts, listQuoteLines, listQuotes, listSettings, recordQuoteSapPush,
      } = await import('@/modules/crm/services/crmService')
      const { resolveSetting } = await import('@/modules/crm/lib/crmSettingDefs')
      const {
        buildSalesOrderPayload, dominantContract, verifyPushAgainstSnapshot, describeVerification,
      } = await import('@/lib/crm/quoteMath')
      const { createSapSalesOrder, describeSoResult } = await import('@/modules/crm/services/sapSalesOrderService')

      const id = typeof input.quoteId === 'string' ? input.quoteId.trim() : ''
      let quote = id ? await getQuote(id) : null
      if (!quote) {
        const wanted = typeof input.quoteNo === 'string' ? input.quoteNo.trim().toUpperCase() : ''
        if (!wanted) return { error: 'Name the quote to push (quoteId or quoteNo).' }
        const candidates = (await listQuotes()).filter((q) => q.quoteNo.toUpperCase() === wanted)
        if (candidates.length === 0) return { error: `No quote numbered ${wanted}.` }
        quote = candidates.sort((a, b) => b.version - a.version)[0]!
      }

      if (quote.sapOrderVbeln) {
        return {
          error: `${quote.quoteNo} already carries SAP sales order ${quote.sapOrderVbeln}. Revise the quote if a different order is needed; pushing again would create a duplicate.`,
        }
      }
      if (quote.status !== 'accepted') {
        return { error: `${quote.quoteNo} is ${quote.status}. An order is created from an accepted quote.` }
      }

      try {
        const settings = await listSettings().catch(() => [])
        const map = new Map(settings.map((s) => [s.key, s.value]))
        if (resolveSetting('crm.so_push', map) !== true) {
          return { error: 'This organization has SAP order creation from quotes switched off in CRM Settings.' }
        }

        const [lines, accounts] = await Promise.all([listQuoteLines(quote.id), listAccounts()])
        const account = accounts.find((a) => a.id === quote.accountId)
        const soldTo = (account?.sapKunnr ?? '').trim()
        if (!soldTo) {
          return { error: `${account?.name ?? 'That account'} has no SAP customer number, so no order can be created for it.` }
        }

        const contract = dominantContract(lines)
        const payload = buildSalesOrderPayload({
          lines,
          soldTo,
          poNumber: (input.poNumber as string) || `${quote.quoteNo}-V${quote.version}`,
          currency: quote.currency,
          contractVbeln: contract.vbeln,
          salesOrg: String(resolveSetting('crm.sales_org', map) ?? '1710'),
          distChannel: String(resolveSetting('crm.distribution_channel', map) ?? '10'),
          plant: String(resolveSetting('crm.sales_org', map) ?? '1710'),
          testRun: input.testRun === true,
        })

        const result = await createSapSalesOrder(payload)
        const verification = verifyPushAgainstSnapshot(lines, result.lines)
        await recordQuoteSapPush(quote.id, {
          vbeln: result.status === 'OK' ? result.vbeln : null,
          result: result as unknown as Record<string, unknown>,
          contractVbeln: contract.vbeln,
        })

        auditCrmTool('push_quote_to_sap', 'crm_quote', quote.id, {
          quoteNo: quote.quoteNo, vbeln: result.vbeln, status: result.status,
        })
        return {
          ok: result.status === 'OK' || result.status === 'TESTRUN_OK',
          quoteId: quote.id,
          quoteNo: quote.quoteNo,
          vbeln: result.vbeln,
          status: result.status,
          netValue: result.netValue,
          currency: result.currency,
          contractReferenced: contract.vbeln,
          contractContested: contract.contested,
          verification: {
            ok: verification.ok,
            mismatchCount: verification.mismatchCount,
            expectedTotal: verification.expectedTotal,
            actualTotal: verification.actualTotal,
          },
          message: `${describeSoResult(result)} ${describeVerification(verification)}`
            + (contract.contested
              ? ` The lines drew on ${contract.contracts.length} contracts and SAP takes one per order, so the order references ${contract.vbeln}.`
              : ''),
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'get_quote_summary',
    description:
      'Read one quote: its status, totals, margin, lines, pricing guardrail verdict, and the SAP order created from it. Read only, so it runs without confirmation.',
    parameters: {
      quoteId: { type: 'string', description: 'The quote id' },
      quoteNo: { type: 'string', description: 'Match by quote number (Q-000007) instead of the id' },
      accountName: { type: 'string', description: 'Summarise the latest quote for this account instead' },
    },
    requiresConfirmation: false,
    handler: async (input) => {
      const {
        getQuote, listAccounts, listQuoteLines, listQuotes,
      } = await import('@/modules/crm/services/crmService')
      const { describeQuoteGuardrails } = await import('@/lib/crm/guardrails')
      const { quoteTotals } = await import('@/lib/crm/quoteMath')

      try {
        const id = typeof input.quoteId === 'string' ? input.quoteId.trim() : ''
        let quote = id ? await getQuote(id) : null

        if (!quote) {
          const quotes = await listQuotes()
          if (quotes.length === 0) return { error: 'There are no quotes yet.' }
          const wantedNo = typeof input.quoteNo === 'string' ? input.quoteNo.trim().toUpperCase() : ''
          if (wantedNo) {
            const candidates = quotes.filter((q) => q.quoteNo.toUpperCase() === wantedNo)
            if (candidates.length === 0) return { error: `No quote numbered ${wantedNo}.` }
            quote = candidates.sort((a, b) => b.version - a.version)[0]!
          } else {
            const wantedAccount = typeof input.accountName === 'string' ? input.accountName.trim().toLowerCase() : ''
            if (!wantedAccount) return { error: 'Name the quote (quoteId or quoteNo), or an accountName.' }
            const accounts = await listAccounts()
            const ids = new Set(accounts.filter((a) => a.name.toLowerCase().includes(wantedAccount)).map((a) => a.id))
            const forAccount = quotes.filter((q) => q.accountId && ids.has(q.accountId))
            if (forAccount.length === 0) return { error: `No quotes for an account matching "${input.accountName}".` }
            quote = forAccount[0]!
          }
        }

        const [lines, accounts] = await Promise.all([listQuoteLines(quote.id), listAccounts()])
        const totals = quoteTotals(lines)
        const summary = quote.guardrailSummary as {
          violations?: { ruleName?: string; severity?: string; message?: string }[]
          requiresApproval?: boolean
          blocked?: boolean
          worstSeverity?: 'warn' | 'approve' | 'block' | null
          linesFlagged?: number
        }
        // The verdict as it was STORED, re-expressed through the same sentence
        // builder the UI uses, so the panel and the banner word it identically.
        // The per-line detail is rebuilt from the flags frozen onto the lines
        // rather than re-evaluated, which is what keeps a submitted quote saying
        // what was true when it was submitted.
        const storedVerdict: Parameters<typeof describeQuoteGuardrails>[0] = {
          lines: lines.map((l) => ({
            lineId: l.id,
            result: {
              violations: l.guardrailFlags as never,
              skipped: [],
              requiresApproval: l.requiresApproval,
              blocked: false,
              worstSeverity: null,
            },
          })),
          violations: (summary.violations ?? []) as never,
          skipped: [],
          requiresApproval: summary.requiresApproval ?? quote.requiresApproval,
          blocked: summary.blocked ?? false,
          worstSeverity: summary.worstSeverity ?? null,
          linesFlagged: summary.linesFlagged ?? lines.filter((l) => l.guardrailFlags.length > 0).length,
        }

        return {
          ok: true,
          quoteId: quote.id,
          quoteNo: quote.quoteNo,
          version: quote.version,
          status: quote.status,
          approvalState: quote.approvalState,
          accountName: accounts.find((a) => a.id === quote.accountId)?.name ?? null,
          currency: quote.currency,
          subtotal: quote.subtotal ?? totals.subtotal,
          marginPct: quote.marginPct ?? totals.marginPct,
          validUntil: quote.validUntil,
          snapshotAt: quote.snapshotAt,
          sapOrderVbeln: quote.sapOrderVbeln,
          lines: lines.map((l) => ({
            lineNo: l.lineNo,
            matnr: l.matnr,
            description: l.description,
            qty: l.qty,
            uom: l.uom,
            unitPrice: l.netUnitPrice,
            extendedPrice: l.extendedPrice,
            marginPct: l.marginPct,
            priceList: l.priceListName,
            overridden: l.manualOverride,
            flags: l.guardrailFlags.length,
          })),
          guardrails: {
            requiresApproval: summary.requiresApproval ?? quote.requiresApproval,
            blocked: summary.blocked ?? false,
            linesFlagged: summary.linesFlagged ?? 0,
            violations: (summary.violations ?? []).map((v) => ({
              rule: v.ruleName, severity: v.severity, message: v.message,
            })),
          },
          message: `${quote.quoteNo} v${quote.version} is ${quote.status}, ${quote.currency} ${(quote.subtotal ?? totals.subtotal).toFixed(2)} across ${lines.length} ${lines.length === 1 ? 'line' : 'lines'}`
            + (quote.marginPct != null ? ` at ${quote.marginPct.toFixed(1)}% margin` : ' with no cost to measure margin against')
            + '. '
            + describeQuoteGuardrails(storedVerdict),
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'get_portal_activity',
    description:
      'What one customer has been doing in the self-service portal: the cart they have open right now, orders they placed through it, anything waiting on a confirmation, and the SAP order numbers. Read only, so it runs without confirmation.',
    parameters: {
      accountId: { type: 'string', description: 'The CRM account id' },
      accountName: { type: 'string', description: 'Match the account by name instead of the id' },
    },
    requiresConfirmation: false,
    handler: async (input) => {
      const { listPortalQuotes, listPortalAccess } = await import('@/modules/crm/services/crmService')
      try {
        const account = await resolveCrmAccount(input)
        if ('error' in account) return account

        const [quotes, access] = await Promise.all([
          listPortalQuotes(account.id),
          listPortalAccess(account.id),
        ])

        // An open DRAFT is a cart, not an order. Reporting the two together is
        // how "portal orders this month" ends up counting things nobody bought.
        const carts = quotes.filter((q) => q.status === 'draft')
        const awaiting = quotes.filter((q) => q.status === 'submitted' || q.status === 'approved' || q.status === 'sent')
        const ordered = quotes.filter((q) => !!q.sapOrderVbeln)
        const value = ordered.reduce((sum, q) => sum + (q.subtotal ?? 0), 0)
        const currency = quotes[0]?.currency ?? 'USD'

        return {
          ok: true,
          accountId: account.id,
          accountName: account.name,
          portalContactsEnabled: access.filter((a) => a.enabled).length,
          openCart: carts[0]
            ? {
              quoteNo: carts[0].quoteNo,
              lineCount: carts[0].lineCount,
              value: carts[0].subtotal ?? 0,
              currency: carts[0].currency,
              startedAt: carts[0].createdAt,
              lastTouchedAt: carts[0].updatedAt,
            }
            : null,
          awaitingConfirmation: awaiting.map((q) => ({
            quoteNo: q.quoteNo,
            status: q.status,
            approvalState: q.approvalState,
            value: q.subtotal ?? 0,
            requiresApproval: q.requiresApproval,
          })),
          ordered: ordered.map((q) => ({
            quoteNo: q.quoteNo,
            sapOrderVbeln: q.sapOrderVbeln,
            value: q.subtotal ?? 0,
            placedAt: q.sapPushedAt,
            contractVbeln: q.sapContractVbeln,
          })),
          message: quotes.length === 0
            ? `${account.name} has not used the customer portal yet.`
            : `${account.name}: ${ordered.length} ${ordered.length === 1 ? 'order' : 'orders'} placed through the portal`
              + (ordered.length > 0 ? ` totalling ${currency} ${value.toFixed(2)}` : '')
              + (carts[0]
                ? `, and a cart open with ${carts[0].lineCount} ${carts[0].lineCount === 1 ? 'item' : 'items'} last touched ${new Date(carts[0].updatedAt).toLocaleDateString()}`
                : ', and no cart open right now')
              + (awaiting.length > 0 ? `. ${awaiting.length} waiting on a confirmation.` : '.'),
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },

  // -------------------------------------------------------------------------
  // P7 - the AI-deep layer
  // -------------------------------------------------------------------------
  {
    name: 'suggest_cross_sell',
    description:
      'What else this customer could buy, ranked. Reads their SD contract lines to work out what '
      + 'they already buy, compares that against the org catalog, and ranks the gaps. Every suggestion '
      + 'carries the reasons that produced its score and whether the customer may already order it '
      + 'today (an open contract or the portal-flagged catalog). READ ONLY: it proposes nothing and '
      + 'writes nothing. Use create_opportunity to act on one.',
    parameters: {
      accountId: { type: 'string', description: 'Target account id' },
      accountName: { type: 'string', description: 'Target account name (used when accountId is omitted)' },
      limit: { type: 'number', description: 'How many suggestions to return (default 8)' },
    },
    requiresConfirmation: false,
    handler: async (input) => {
      const resolved = await resolveCrmAccount(input)
      if ('error' in resolved) return resolved
      try {
        const { buildAccountStrategyData } = await import('@/modules/crm/services/strategyDataService')
        const data = await buildAccountStrategyData(resolved.id)
        const limit = typeof input.limit === 'number' && input.limit > 0 ? Math.floor(input.limit) : 8

        const suggestions = data.suggestions.slice(0, limit).map((s) => ({
          matnr: s.matnr,
          material: s.key,
          description: s.description,
          group: s.group.label,
          groupedBy: s.group.source,
          score: s.score,
          reasons: s.reasons,
          entitled: s.entitled,
        }))

        return {
          ok: true,
          accountId: resolved.id,
          accountName: resolved.name,
          installBase: {
            materials: data.installBase.materials.length,
            groups: data.installBase.groups.length,
            totalContractValue: data.installBase.totalValue,
            currency: data.installBase.currency,
            runRatePerYear: data.installBase.runRatePerYear,
            runRateReason: data.installBase.runRateReason,
          },
          whitespace: data.whitespace.slice(0, limit).map((w) => ({
            group: w.label, groupedBy: w.source, catalogMaterials: w.catalogCount, rationale: w.rationale,
          })),
          suggestions,
          notes: data.notes,
          message: data.installBase.materials.length === 0
            ? `${resolved.name} has no contract lines, so there is no install base to compare against. `
              + 'Contract items are the only material-level source: the ERP read exposes order headers with no items.'
            : `${resolved.name} buys ${data.installBase.materials.length} materials across `
              + `${data.installBase.groups.length} groups. ${suggestions.length} cross-sell `
              + `${suggestions.length === 1 ? 'candidate' : 'candidates'} ranked`
              + (suggestions.filter((s) => s.entitled).length > 0
                ? `, ${suggestions.filter((s) => s.entitled).length} of which they can already order today.`
                : '.'),
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'research_account',
    description:
      'Research an account and save a timestamped brief on its strategy. Runs LIVE WEB SEARCH when '
      + 'this deployment supports it, and every claim carries a citation. When it does not, it writes '
      + 'an INTERNAL-ONLY brief from the install base and catalog which states on its face that it '
      + 'contains no external research. It never invents news either way. Writes on confirmation, '
      + 'because the brief is stored on the account.',
    parameters: {
      accountId: { type: 'string', description: 'Target account id' },
      accountName: { type: 'string', description: 'Target account name (used when accountId is omitted)' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const resolved = await resolveCrmAccount(input)
      if ('error' in resolved) return resolved
      try {
        const { runAccountResearch } = await import('@/modules/crm/services/strategyDataService')
        const result = await runAccountResearch(resolved.id, 'ai')
        auditCrmTool('research_account', 'crm_account', resolved.id, {
          live: result.live, citations: result.brief.citations.length,
        })
        return {
          ok: true,
          accountId: resolved.id,
          accountName: resolved.name,
          live: result.live,
          headline: result.brief.headline,
          sections: result.brief.sections.map((s) => ({ heading: s.heading, body: s.body })),
          citations: result.brief.citations,
          message: result.live
            ? `Researched ${resolved.name} with live web search and saved the brief with `
              + `${result.brief.citations.length} ${result.brief.citations.length === 1 ? 'citation' : 'citations'}.`
            : `Saved an internal-only brief for ${resolved.name}. ${result.reason ?? ''}`.trim(),
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'add_competitor_to_opportunity',
    description:
      'Put a competitor on a pursuit. Matches the org competitor register by name and CREATES the '
      + 'register entry when there is no match, so the same competitor named on three deals ends up as '
      + 'one register row with three links rather than three rows. Role primary means the one to beat. '
      + 'Writes on confirmation.',
    parameters: {
      opportunityId: { type: 'string', description: 'Target opportunity id' },
      opportunityName: { type: 'string', description: 'Target opportunity name (used when the id is omitted)' },
      competitorName: { type: 'string', description: 'The competitor as a person would name them' },
      role: { type: 'string', description: 'primary | secondary (default secondary)' },
      stance: { type: 'string', description: 'incumbent | likely_bidder | confirmed_bidder | ruled_out' },
      threat: { type: 'number', description: '1 (minor) to 5 (severe). Omit when nobody has assessed it.' },
      notes: { type: 'string', description: 'What is known about them on this deal' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const name = typeof input.competitorName === 'string' ? input.competitorName.trim() : ''
      if (!name) return { error: 'Name the competitor to add.' }
      try {
        const crm = await import('@/modules/crm/services/crmService')
        const opportunities = await crm.listOpportunities()
        if (opportunities.length === 0) {
          return { error: 'There are no opportunities yet.' }
        }

        const id = typeof input.opportunityId === 'string' ? input.opportunityId.trim() : ''
        let opportunity = id ? opportunities.find((o) => o.id === id) : undefined
        if (!opportunity) {
          const oppName = typeof input.opportunityName === 'string' ? input.opportunityName.trim() : ''
          if (!oppName) return { error: 'Name the pursuit to act on (opportunityName), or pass its opportunityId.' }
          const { findDuplicates } = await import('@/lib/crm/dedupe')
          const matches = findDuplicates(
            { name: oppName },
            opportunities.map((o) => ({ id: o.id, name: o.name })),
            0.5,
          )
          if (matches.length === 0) return { error: `No pursuit matches "${oppName}".` }
          opportunity = opportunities.find((o) => o.id === matches[0]!.candidate.id)
        }
        if (!opportunity) return { error: 'Could not resolve the pursuit.' }

        const register = await crm.listCompetitors()
        const existing = register.find((c) => c.name.trim().toLowerCase() === name.toLowerCase())
        const competitor = existing ?? await crm.createCompetitor({
          name,
          kind: input.stance === 'incumbent' ? 'incumbent' : 'challenger',
          createdBy: 'ai',
        })

        const threat = typeof input.threat === 'number'
          ? Math.max(1, Math.min(5, Math.round(input.threat)))
          : null

        const link = await crm.linkOpportunityCompetitor({
          opportunityId: opportunity.id,
          competitorId: competitor.id,
          role: input.role === 'primary' ? 'primary' : 'secondary',
          stance: (input.stance as never) ?? 'likely_bidder',
          threat,
          notes: (input.notes as string) ?? null,
          createdBy: 'ai',
        })

        auditCrmTool('add_competitor_to_opportunity', 'crm_opportunity', opportunity.id, {
          competitorId: competitor.id, competitorName: competitor.name, role: link.role, created: !existing,
        })

        return {
          ok: true,
          opportunityId: opportunity.id,
          opportunityName: opportunity.name,
          competitorId: competitor.id,
          competitorName: competitor.name,
          registerEntryCreated: !existing,
          role: link.role,
          message: `${competitor.name} is now on ${opportunity.name} as the ${link.role} competitor`
            + (existing ? '.' : ', and was added to the competitor register.'),
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'log_email',
    description:
      'Record an email or Teams message against an account, so it appears on the comms list and the '
      + 'account timeline. Takes the subject and a SHORT excerpt, never a whole message body: bodies '
      + 'stay in the mail system. Pass externalRef (the provider message id) and re-logging the same '
      + 'message updates it instead of duplicating it. Writes on confirmation.',
    parameters: {
      accountId: { type: 'string', description: 'Target account id' },
      accountName: { type: 'string', description: 'Target account name (used when accountId is omitted)' },
      contactId: { type: 'string', description: 'The contact on the message, when known' },
      opportunityId: { type: 'string', description: 'The pursuit it relates to, when known' },
      kind: { type: 'string', description: 'email | teams (default email)' },
      subject: { type: 'string', description: 'Subject or first line' },
      snippet: { type: 'string', description: 'A short excerpt. Not the whole body.' },
      direction: { type: 'string', description: 'inbound | outbound | internal' },
      occurredAt: { type: 'string', description: 'ISO timestamp of when it was sent' },
      fromAddress: { type: 'string', description: 'Sender address' },
      externalRef: { type: 'string', description: 'The provider message id, which makes a re-log idempotent' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const resolved = await resolveCrmAccount(input)
      if ('error' in resolved) return resolved
      const subject = typeof input.subject === 'string' ? input.subject.trim() : ''
      const snippet = typeof input.snippet === 'string' ? input.snippet.trim() : ''
      if (!subject && !snippet) {
        return { error: 'A logged message needs at least a subject or an excerpt.' }
      }
      try {
        const crm = await import('@/modules/crm/services/crmService')
        const settings = await crm.listSettings()
        const map = new Map(settings.map((s) => [s.key, s.value]))
        const { resolveSetting } = await import('@/modules/crm/lib/crmSettingDefs')
        const writeInteraction = resolveSetting('crm.comms_log_interaction', map) !== false

        const kind = input.kind === 'teams' ? 'teams' : 'email'
        const direction = ['inbound', 'outbound', 'internal'].includes(String(input.direction))
          ? (input.direction as 'inbound' | 'outbound' | 'internal')
          : null

        const { commLink, interaction } = await crm.logCommMessage({
          kind,
          accountId: resolved.id,
          contactId: (input.contactId as string) ?? null,
          opportunityId: (input.opportunityId as string) ?? null,
          subject: subject || null,
          // Capped here as well as in the UI: a tool called from the panel must
          // obey the same "no bodies stored" rule the dialog does.
          snippet: snippet ? snippet.slice(0, 1000) : null,
          direction,
          occurredAt: (input.occurredAt as string) ?? new Date().toISOString(),
          fromAddress: (input.fromAddress as string) ?? null,
          externalRef: (input.externalRef as string) ?? null,
          matchMethod: 'ai',
          createdBy: 'ai',
          writeInteraction,
        })

        auditCrmTool('log_email', 'crm_comm_link', commLink.id, {
          accountId: resolved.id, kind, interactionId: interaction?.id ?? null,
        })

        return {
          ok: true,
          commLinkId: commLink.id,
          accountId: resolved.id,
          accountName: resolved.name,
          interactionId: interaction?.id ?? null,
          message: `Logged ${kind === 'teams' ? 'a Teams message' : 'an email'} on ${resolved.name}`
            + (interaction ? ', and it is on the account timeline.' : '. It is on the comms list only, because logging an interaction alongside each message is switched off.'),
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  // -------------------------------------------------------------------------
  // P8 - strategy, OKRs, quotas, forecasting
  // -------------------------------------------------------------------------
  {
    name: 'get_forecast_summary',
    description:
      'The sales forecast for a period: committed, best case, pipeline, the weighted total, what has '
      + 'closed, and the roll-up by owner and by channel. Computed by the same forecast engine the '
      + 'Forecast page runs, so this answer and that page cannot differ. READ ONLY: it writes nothing. '
      + 'An open pursuit with no close date is NOT forecast into a period and is reported separately, '
      + 'and currencies are never converted.',
    parameters: {
      period: {
        type: 'string',
        description: 'Period key, for example 2026-Q3 or 2026-08. Defaults to the current period.',
      },
      dimension: {
        type: 'string',
        description: 'Which roll-up to return: owner or channel. Defaults to owner.',
      },
    },
    requiresConfirmation: false,
    handler: async (input) => {
      try {
        const { loadPerformanceBundle, resolvePeriod } = await import('@/modules/crm/services/performanceDataService')
        const { buildForecast } = await import('@/lib/crm/forecastEngine')
        const bundle = await loadPerformanceBundle()
        const period = resolvePeriod(
          typeof input.period === 'string' ? input.period : null,
          bundle.periodKind,
          bundle.now,
        )
        const forecast = buildForecast({
          opportunities: bundle.metrics.opportunities,
          stages: bundle.stages,
          period,
          basis: bundle.weightingBasis,
          ownerLabels: bundle.ownerLabels,
          channelLabels: bundle.channelLabels,
          currency: bundle.currency,
        })
        const dimension = input.dimension === 'channel' ? 'channel' : 'owner'
        const groups = (dimension === 'channel' ? forecast.byChannel : forecast.byOwner).map((g) => ({
          key: g.key, label: g.label, deals: g.count, value: g.value, weighted: g.weighted,
          commit: g.byCategory.commit.value,
          bestCase: g.byCategory.best_case.value,
          pipeline: g.byCategory.pipeline.value,
        }))
        return {
          ok: true,
          period: period.key,
          periodLabel: period.label,
          currency: forecast.currency,
          weightedOn: forecast.basis,
          totals: forecast.totals,
          dimension,
          groups,
          notForecast: forecast.undated,
          message: forecast.rows.length === 0
            ? `Nothing closes in ${period.label}.`
              + (forecast.undated > 0
                ? ` ${forecast.undated} open pursuit${forecast.undated === 1 ? '' : 's'} carry no close date, so they cannot be forecast into any period.`
                : '')
            : `${period.label}: ${forecast.totals.commitValue} committed, `
              + `${forecast.totals.openWeighted} weighted across ${forecast.rows.length} pursuits, `
              + `${forecast.totals.closedWonValue} already won.`
              + (forecast.undated > 0
                ? ` ${forecast.undated} open pursuit${forecast.undated === 1 ? '' : 's'} carry no close date and are not counted.`
                : ''),
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'create_objective',
    description:
      'Add an objective to a sales strategy. Resolves the strategy by id or by name and REFUSES '
      + 'ambiguously rather than guessing which plan you meant. An objective always belongs to a '
      + 'strategy, because a strategy is what gives it a period, and without a period "did we do it" '
      + 'has no answer.',
    parameters: {
      strategyId: { type: 'string', description: 'Sales strategy id' },
      strategyName: { type: 'string', description: 'Sales strategy name (used when strategyId is omitted)' },
      name: { type: 'string', description: 'The objective' },
      description: { type: 'string', description: 'Why it matters' },
      ownerUserId: { type: 'string', description: 'Who answers for it (roster id)' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const name = typeof input.name === 'string' ? input.name.trim() : ''
      if (!name) return { error: 'An objective needs a name.' }
      try {
        const svc = await import('@/modules/crm/services/crmService')
        const strategies = await svc.listSalesStrategies()
        if (strategies.length === 0) {
          return { error: 'There are no sales strategies yet. Create one on the Sales Strategy page first.' }
        }
        let target = typeof input.strategyId === 'string'
          ? strategies.find((s) => s.id === input.strategyId)
          : undefined
        if (!target && typeof input.strategyName === 'string' && input.strategyName.trim()) {
          const needle = input.strategyName.trim().toLowerCase()
          const matches = strategies.filter((s) => s.name.toLowerCase().includes(needle))
          if (matches.length > 1) {
            return {
              error: `More than one strategy matches "${input.strategyName}".`,
              candidates: matches.map((s) => ({ id: s.id, name: s.name, status: s.status })),
            }
          }
          target = matches[0]
        }
        if (!target) {
          const active = strategies.filter((s) => s.status === 'active')
          if (active.length === 1) target = active[0]
        }
        if (!target) {
          return {
            error: 'Which strategy should this objective go under?',
            candidates: strategies.map((s) => ({ id: s.id, name: s.name, status: s.status })),
          }
        }
        const objective = await svc.createObjective({
          strategyId: target.id,
          name,
          description: typeof input.description === 'string' ? input.description : null,
          ownerUserId: typeof input.ownerUserId === 'string' ? input.ownerUserId : null,
          createdBy: 'ai',
        })
        auditCrmTool('create_objective', 'crm_objective', objective.id, {
          strategyId: target.id, name,
        })
        return {
          ok: true,
          objectiveId: objective.id,
          strategyId: target.id,
          strategyName: target.name,
          message: `Added "${name}" to ${target.name}. It has no key results yet, so nothing measures it.`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'update_key_result',
    description:
      'Change a key result: its target, its called status, its due date, or its hand-kept current '
      + 'value. REFUSES to set a current value on a metric-linked key result, because that value is '
      + 'computed live and a typed number sitting beside a computed one is two answers to the same '
      + 'question. Resolves the key result by id or by name, and refuses ambiguously.',
    parameters: {
      keyResultId: { type: 'string', description: 'Key result id' },
      keyResultName: { type: 'string', description: 'Key result name (used when the id is omitted)' },
      currentValue: { type: 'number', description: 'The current value, on a hand-kept key result only' },
      targetValue: { type: 'number', description: 'A new target' },
      status: { type: 'string', description: 'not_started | on_track | at_risk | off_track | done' },
      dueDate: { type: 'string', description: 'ISO date' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      try {
        const svc = await import('@/modules/crm/services/crmService')
        const all = await svc.listKeyResults()
        if (all.length === 0) return { error: 'There are no key results yet.' }

        let target = typeof input.keyResultId === 'string'
          ? all.find((k) => k.id === input.keyResultId)
          : undefined
        if (!target && typeof input.keyResultName === 'string' && input.keyResultName.trim()) {
          const needle = input.keyResultName.trim().toLowerCase()
          const matches = all.filter((k) => k.name.toLowerCase().includes(needle))
          if (matches.length > 1) {
            return {
              error: `More than one key result matches "${input.keyResultName}".`,
              candidates: matches.map((k) => ({ id: k.id, name: k.name })),
            }
          }
          target = matches[0]
        }
        if (!target) {
          return {
            error: 'Which key result?',
            candidates: all.slice(0, 20).map((k) => ({ id: k.id, name: k.name })),
          }
        }

        const notes: string[] = []
        if (typeof input.currentValue === 'number') {
          if (target.metricKey) {
            return {
              error: `"${target.name}" is linked to the ${target.metricKey} metric, so its current value is `
                + 'computed rather than typed. Unlink the metric on the key result form if you want to keep it by hand.',
            }
          }
          await svc.setKeyResultCurrent(target.id, input.currentValue)
          notes.push(`current set to ${input.currentValue}`)
        }

        const patch: Record<string, unknown> = {}
        if (typeof input.targetValue === 'number') { patch.targetValue = input.targetValue; notes.push(`target set to ${input.targetValue}`) }
        if (typeof input.status === 'string') { patch.status = input.status; notes.push(`called ${input.status}`) }
        if (typeof input.dueDate === 'string') { patch.dueDate = input.dueDate; notes.push(`due ${input.dueDate}`) }
        if (Object.keys(patch).length > 0) await svc.updateKeyResult(target.id, patch)

        if (notes.length === 0) return { error: 'Nothing to change. Say what should be different.' }

        auditCrmTool('update_key_result', 'crm_key_result', target.id, { changes: notes })
        return {
          ok: true,
          keyResultId: target.id,
          keyResultName: target.name,
          message: `Updated "${target.name}": ${notes.join(', ')}.`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'set_quota',
    description:
      'Set one person\'s quota for one period. Upserts on the person and period pair, so setting it '
      + 'twice corrects the number rather than creating a second quota. Passing a quota of null CLEARS '
      + 'it, which reads as "no quota set" on the scorecard rather than as a miss; a real zero is a '
      + 'different statement and is stored as one.',
    parameters: {
      userId: { type: 'string', description: 'Roster id of the person' },
      userName: { type: 'string', description: 'Their name (used when userId is omitted)' },
      period: { type: 'string', description: 'Period key, for example 2026-Q3. Defaults to the current period.' },
      quotaValue: { type: 'number', description: 'The quota. Omit to clear it.' },
      dealCountTarget: { type: 'number', description: 'Optional deal count target' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      try {
        const svc = await import('@/modules/crm/services/crmService')
        const { loadPerformanceBundle, resolvePeriod } = await import('@/modules/crm/services/performanceDataService')
        const bundle = await loadPerformanceBundle()

        let userId = typeof input.userId === 'string' ? input.userId.trim() : ''
        if (!userId && typeof input.userName === 'string' && input.userName.trim()) {
          const needle = input.userName.trim().toLowerCase()
          const matches = bundle.users.filter((u) => u.name.toLowerCase().includes(needle))
          if (matches.length > 1) {
            return {
              error: `More than one person matches "${input.userName}".`,
              candidates: matches.map((u) => ({ id: u.id, name: u.name })),
            }
          }
          userId = matches[0]?.id ?? ''
        }
        if (!userId) {
          return {
            error: 'Which person?',
            candidates: bundle.users.slice(0, 25).map((u) => ({ id: u.id, name: u.name })),
          }
        }

        const period = resolvePeriod(
          typeof input.period === 'string' ? input.period : null,
          bundle.periodKind,
          bundle.now,
        )
        const quota = await svc.setQuota({
          userId,
          period: period.key,
          currency: bundle.currency,
          quotaValue: typeof input.quotaValue === 'number' ? input.quotaValue : null,
          dealCountTarget: typeof input.dealCountTarget === 'number' ? input.dealCountTarget : null,
          updatedBy: 'ai',
        })
        auditCrmTool('set_quota', 'crm_quota', quota.id, {
          userId, period: period.key, quotaValue: quota.quotaValue,
        })
        const name = bundle.ownerLabels[userId] ?? userId
        return {
          ok: true,
          quotaId: quota.id,
          userId,
          period: period.key,
          quotaValue: quota.quotaValue,
          message: quota.quotaValue === null
            ? `Cleared ${name}'s quota for ${period.label}. Their attainment now reads "no quota set" rather than zero.`
            : `${name} carries ${quota.quotaValue} ${quota.currency} for ${period.label}.`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
]

/**
 * Resolve the tool set the AI panel should advertise. Agile tools are included
 * only when the agile.enabled capability is on (resolve the flag once at the
 * panel/provider init and pass it here). CRM tools are included whenever the
 * CRM module is built into this deployment and the signed-in user is entitled
 * to it (moduleEntitlements), since the module gate already answers "did this
 * customer buy the CRM".
 */
export function getPpmTools(opts?: { agileEnabled?: boolean }): AiTool[] {
  const tools = [...ppmTools]
  if (opts?.agileEnabled) tools.push(...agileAiTools)
  if (isModuleVisible('crm')) tools.push(...crmAiTools)
  if (isModuleVisible('it-ticketing')) tools.push(...itTicketingAiTools)
  return tools
}
